(ns ductile.ui.workflow
  (:require ["luxon" :as luxon :refer [DateTime]]
            #_[dataspex.core :as dataspex]
            [applied-science.js-interop :as j]
            [cljs.reader]
            [clojure.pprint :as pprint]
            [clojure.set :as set]
            [clojure.string :as str]
            [clojure.walk :as walk]
            [ductile.base64u :as base64]
            [ductile.clerk.viewer :as dcv]
            [ductile.dependent-selects :as dependent-selects]
            [ductile.time.format :as time-format]
            [ductile.ui.code :as code]
            [ductile.ui.components :as ui]
            [ductile.ui.components.combobox :as combobox]
            [ductile.ui.components.table :as table]
            [ductile.ui.damages :as damages]
            [ductile.ui.deframe :as deframe]
            [ductile.ui.dependent-selects :as ui.dependent-selects]
            [ductile.ui.fahrzeug :as fahrzeug :refer [->fahrzeug-id expected-error-view]]
            [ductile.ui.fields :as fields]
            [ductile.ui.http :as http]
            [ductile.ui.icons :as icons]
            [ductile.ui.locale :refer [tr trn] :as locale]
            [ductile.ui.mapbox :as mapbox]
            [ductile.ui.mobile :as mobile]
            [ductile.ui.router :as router]
            [ductile.ui.routes :as routes]
            [ductile.ui.scanner :as scanner]
            [ductile.ui.scene :as scene]
            [ductile.ui.scope]
            [ductile.ui.transport :as transport]
            [ductile.ui.util :as util]
            [ductile.ui.vehicle-call-off :as vehicle-call-off]
            [ductile.ui.view-stack :as view-stack]
            [ductile.util :refer [when-ok ok->]]
            [ductile.util.magic-wand :refer [magic-wand-defaults]]
            [ductile.workflow.equipment-utils :as equipment-utils]
            [ductile.workflow.parking-data :as parking-data]
            [ductile.workflow.parking-utils :as parking-utils]
            [ductile.workflow.simulator :as-alias simulator]
            [goog.string :as gstring]
            [kitchen-async.promise :as p]
            [malli.core :as m]
            [nextjournal.clerk.render :as render]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clerk.render.localstorage :as localstorage]
            [nextjournal.clerk.viewer :as v]
            [nextjournal.log :as log]
            [reagent.core :as r])
  (:require-macros [ductile.ui.macros :refer [defc]]
                   [ductile.ui.scene.macros :refer [defscene]]
                   [ductile.ui.scope.macros :refer [when-current-user-scope]]))

(declare !recorded-attrs)

(defn auxiliary-fields
  "Returns the topmost auxiliary fields from the stack, or nil if empty."
  []
  (let [top (peek @(deframe/!route-get ::view-stack/view-stack))]
    (when (= :fields (:view/type top))
      (:fields top))))

(defn enter-auxiliary! [focus-return-attr fields]
  (view-stack/push-view! {:view/type :fields
                          :focus-return-attr focus-return-attr
                          :scroll-y js/window.scrollY
                          :fields fields}))

(defn exit-auxiliary! []
  (view-stack/pop-view!))

(defn auxiliary-focus-effect
  "Hook to focus first focusable element when auxiliary view mounts"
  [auxiliary-fields]
  (hooks/use-effect
   (fn []
     (when auxiliary-fields
       (js/requestAnimationFrame
        (fn []
          (when-let [el (js/document.querySelector ".auxiliary-view [tabindex='0'], .auxiliary-view input, .auxiliary-view textarea")]
            (.focus el)))))
     js/undefined)
   [auxiliary-fields]))

(defn recorded-attr-path [{:as field :ui/keys [attr recorded-attrs-path]}]
  (conj (or recorded-attrs-path
            [::recorded-attrs])
        attr))

(defn magic-wand-fill-field! [{:as field :ui/keys [attr magic-wand-value]}]
  (let [path (recorded-attr-path field)
        current-value @(deframe/!route-get-in path)
        magic-value (magic-wand-defaults attr magic-wand-value)]
    (when (and (nil? current-value) (some? magic-value))
      (when-some [v (if (fn? magic-value) (magic-value field) magic-value)]
        (deframe/route-assoc-in! path v)))))

(defn use-magic-wand [f]
  (hooks/use-effect
   (fn []
     (when (or (not ui/is-mobile?) (util/has-required-scope? :scope/developer))
       (let [[field-name fill!] (cond
                                  (fn? f) [:f f]
                                  (and (map? f) (:ui/attr f)) [(:ui/attr f) #(magic-wand-fill-field! f)])

             on-enter (fn [^js e]
                        (when (and (or (.-metaKey e) (.-ctrlKey e))
                                   (= "e" (.-key e)))
                          (.stopPropagation e)
                          (fill!)))]
         (when fill!
           (.addEventListener js/window "keydown" on-enter)
           #(.removeEventListener js/window "keydown" on-enter))))
     [f])))

(def simulate-scan-hint
  [:div.text-xs.text-slate-600 (tr "%1 to simulate a scan" scanner/simulate-scan-binding)])

(defn camera-button []
  [:div.mr-2 {:class "w-[75px] "}
   [mobile/button-full {:on-click (fn [_] (deframe/activate-scanner!))
                        :data-testid "scanner-camera-button"}
    [ui/icon :camera]]])

(defn fahrzeug-route [{:vehicle/keys [vin] :as fahrzeug}]
  (router/path (:workflow/route-path fahrzeug) fahrzeug))


(defn process-scan
  "Processes the scan entity removing attributes we don't care
  about (classification happens on the backend) and adding the
  `:compound/name`."
  [scan]
  (-> scan
      (dissoc :scan/classified :scan/attr :db/id)
      (assoc :compound/name (util/current-compound-name))
      ;; duplicating this for now because we want to update that after-hours
      (assoc :scan (:scan/result scan))))

(comment
  (process-scan scan))

(defn fahrzeug-scan-navigate [{:as scan :scan/keys [result classified]}]
  (when (and result (not (:error classified)))
    (.then (http/$lambda 'ductile.workflow/build-fahrzeug+scan (process-scan scan))
           (fn [{:as result :keys [vehicle/vin]}]
             (if (:error result)
               (reset! scanner/!scan-confirmation (assoc (into {} scan) :scan/classified result))
               (router/navigate-to (router/path '[compound-ops :compound-name workflow :fahrzeug-id]
                                                {:compound-name (util/current-compound-name) :fahrzeug-id vin})))
             (deframe/reset-current-scan!)))))

(defn developer-submit-on-enter [submit-fn]
  (r/with-let [on-enter (fn [^js e]
                          (when (= "Enter" (.-key e))
                            (.stopPropagation e)
                            (submit-fn e)))
               _ (.addEventListener js/window "keydown" on-enter)]
    [:<>]
    (finally
      (.removeEventListener js/window "keydown" on-enter))))

(defn footer [{:as options :keys [label hide-camera? component]}]
  [(or component mobile/footer)
   (when (and (:on-click options) (or (not ui/is-mobile?) (util/has-required-scope? :scope/developer)))
     ^{:key (:on-click options)} [developer-submit-on-enter (:on-click options)])
   [:div.flex.w-full
    (when-not hide-camera?
      [camera-button])
    [mobile/button-full
     (dissoc options :hide-camera? :label)
     (or label (tr "Confirm"))]]])

(defn toolbar [opts]
  [footer (assoc opts :component mobile/toolbar)])

(defn wheel-set? [workflow-map]
  (= :accessory.kind/wheel-set (:accessory/kind workflow-map)))

(def !fahrzeug
  "Aka the `workflow-map`: a map which the workflow acts on.

  Used to always be a fahrzeug, but now it can be any compatible map, like a wheel-set"
  (r/track (fn []
             (when-let [fetch-result @(deframe/!route-get-in [:fetch :result])]
               (when (contains? fetch-result :workflow/services)
                 fetch-result)))))

(comment @!fahrzeug
         (-> @!fahrzeug :workflow/services first :service/services first))

(def !parking-layout
  (r/track (fn []
             (when-let [fahrzeug (or @!fahrzeug
                                     @(deframe/!route-get ::fahrzeug-to-park))]
               (get parking-data/zone->dimensions (:compound/name fahrzeug))))))

(comment
  (get parking-data/zone->dimensions (:compound/name @!fahrzeug))
  @!parking-layout)

(defn parking?
  "True iff this service is a re-parking service. Obsolete once we
  unify service codes across italy & germany."
  [service]
  (contains? #{"Parken" "Posizionamento" "Ingresso FPR"} (:service/code service)))

(defn parking-state-path [& ks]
  (into [:current-route/transient ::selected-service ::parking] ks))

(def !suggested-spot
  (deframe/!get-in (parking-state-path :suggested-spot)))

(defn !rows->spots->n-occupied [zone]
  (deframe/!get-in (parking-state-path :rows->spots->n-occupied zone :result)))

(defn fetch-rows->spots->n-occupied! [zone]
  ;; TODO: combine this with `!rows->spots->n-occupied` into a ratom that does the fetching (only once).
  (when (and (not (str/blank? zone))
             (not @(deframe/!get-in (parking-state-path :rows->spots->n-occupied zone))))
    (deframe/call-lambda
     {:result-path (parking-state-path :rows->spots->n-occupied zone)}
     'ductile.workflow.parking/rows->spots->n-occupied
     {:compound-name (:compound/name @!fahrzeug)
      :zone zone})))

(defn !parking-row-options [zone]
  (r/track (fn [] (sort (keys @(!rows->spots->n-occupied zone))))))

(defn free-places [place->n-occupied]
  (when (map? place->n-occupied)
    (sort (keep (fn [[place n]] (when (zero? n) place)) place->n-occupied))))

(defn free-rows [spot-occupancy]
  (sort (keep (fn [[row place->n-occupied]]
                (when (or
                       (and (number? place->n-occupied) (zero? place->n-occupied)) ;;for :assignment-type :row
                       (and (map? place->n-occupied) (not-empty (free-places place->n-occupied))))
                  row))
              spot-occupancy)))

(defn !parking-place-options [zone row]
  (r/track (fn [] (let [place->n-occupied (get @(!rows->spots->n-occupied zone) row)]
                    (when (map? place->n-occupied)
                      (sort (keys place->n-occupied)))))))

(defn parking-model [compound]
  (get {"Lonato" :parking.assignment/box
        "Civitavecchia" :parking.assignment/box
        "Ravenna" :parking.assignment/free-spot}
       compound
       :parking.assignment/predefined-spot))

(comment
  (parking-model "Ravenna")
  (parking-model "Lonato")
  (parking-model "Krefeld"))

(def !selected-parking-spot
  (r/track (fn []
             (let [{:as selected-spot :parking-spot/keys [zone row]} @(deframe/!get-in (parking-state-path :selected-parking-spot :result))]
               (merge (when zone
                        (let [current-row (or row (first (free-rows @(!rows->spots->n-occupied zone))))
                              place-options @(!parking-place-options zone current-row)]
                          (cond-> {:parking-spot/row current-row}
                            (and (seq? place-options) (= :parking.assignment/predefined-spot (parking-model (:compound/name @!fahrzeug))))
                            (assoc :parking-spot/place (first (free-places (get @(!rows->spots->n-occupied zone) current-row)))))))
                      selected-spot)))))

(comment @!selected-parking-spot)

(defn select-parking-zone! [zone]
  (fetch-rows->spots->n-occupied! zone)
  (deframe/assoc-in! (parking-state-path :selected-parking-spot :result) {:parking-spot/zone zone}))

(defn select-parking-row! [row]
  (deframe/assoc-in! (parking-state-path :selected-parking-spot :result) {:parking-spot/zone @(deframe/!get-in (parking-state-path :selected-parking-spot :result :parking-spot/zone))
                                                                          :parking-spot/row row}))

(defn select-parking-place! [place]
  (deframe/assoc-in! (parking-state-path :selected-parking-spot  :result :parking-spot/place) place))

(declare !selected-service)

(defn ->parking-keys [m]
  (select-keys m [:parking-spot/zone :parking-spot/row :parking-spot/place]))

(defn parking-assignment
  "Computes the parking assignment type for the given compound (and optional spot).

   TODO: currently happening in the frontend but would be nice to move
  this to the backend. This is currently hard because of circular deps
  from parking <> engine."
  [fahrzeug]
  (case (get-in @!parking-layout [(:parking-spot/zone fahrzeug) :assignment-type])
    :row :parking.assignment/line
    :zone :parking.assignment/zone
    nil (parking-model (:compound/name fahrzeug))))

(comment
  (parking-assignment {:compound/name "Ravenna"})
  (parking-assignment {:compound/name "Lonato"})
  (parking-assignment {:compound/name "Lonato" :parking-spot/zone "GZ"})
  (parking-assignment {:compound/name "Krefeld"}))

(defn fetch-suggested-parking-spot! [{:keys [service]}]
  ;; TODO: suggested parking spot should be computed in the backend and be on the service
  (when (= :parking.assignment/predefined-spot
           (parking-model (:compound/name @!fahrzeug)))
    (deframe/call-lambda
     {:result-path (parking-state-path :selected-parking-spot)}
     'ductile.workflow.parking/handle-available-parking-spot
     (cond-> (select-keys @!fahrzeug [:vehicle/vin :compound/name])
       service (assoc :service/code (:service/code service))))))

(defn maybe-set-selected-parking-spot! [service]
  (let [suggested-spot (->parking-keys (:service/default-attrs service))]
    (cond (seq suggested-spot)
          (deframe/assoc-in!
           (parking-state-path :selected-parking-spot)
           {:result suggested-spot})

          (parking? service)
          (do (deframe/assoc-in!
               (parking-state-path :selected-parking-spot)
               {:result (dissoc (->parking-keys @!fahrzeug) :parking-spot/place)})
              (fetch-rows->spots->n-occupied! (:parking-spot/zone @!fahrzeug))))))

(defn select-service! [service]
  (deframe/route-assoc! ::selected-service (assoc service :num-completed (count (:fahrzeug/services @!fahrzeug))))
  (maybe-set-selected-parking-spot! service))

(defn deselect-service! []
  (deframe/route-dissoc! ::selected-service))

(defn find-service [fahrzeug service-code]
  ;; TODO: simplify once all services are nested
  (first (filter #(= service-code (:service/code %))
                 (tree-seq (some-fn sequential? map?) #(if (map? %) (% :service/services) %) (:workflow/services fahrzeug)))))

(defn select-ship-service! [workflow-fahrzeug]
  (select-service! (find-service workflow-fahrzeug "Caricamento Nave")))

(defmethod deframe/route-on-fetch ::select-ship-loading-service! [_ctx _match]
  {:on-result select-ship-service!})

(defmethod deframe/route-on-fetch ::import-accessories [_ctx _match]
  {:build-lambda-params (fn [_match]
                          {:wizard/name "import-accessories"})})

#_(find-service @!fahrzeug "Bereitstellung Selbstabholer")

(comment @!fahrzeug
         (find-service @!fahrzeug "Ingresso FPR"))

(defn completed? [{:event/keys [kind]}]
  (contains? #{:service.event/completed :service.event/canceled} kind))

(defn completed-since-selected?
  [fahrzeug {:keys [service/code num-completed]}]
  (some? (first (filter #(= code (:service/code %))
                        (drop num-completed (:fahrzeug/services @!fahrzeug))))))

(declare name->view-fn)

(defn resolve-ui-views
  "Resolves ui-view symbols to fns."
  [fahrzeug]
  (walk/postwalk-replace name->view-fn fahrzeug))

#_(resolve-ui-views @(deframe/!route-get-in [:fetch :result]))

(def !selected-service
  "The currently selected service. We currently lack a clear notion of
  when we want to navigate back to the workflow (after completing a
  service). This is due to the different underlying models where not
  every service stores its completion in datomic.

  We should be able to simplify this once we move to a unified model."
  (r/track (fn []
             (when-let [{:as selected-service :keys [service/code num-completed]}
                        (merge @(deframe/!route-get ::selected-service)
                               (when-let [service (first (filter :service/active? (:workflow/services @!fahrzeug)))]
                                 (when (:service/take-first-child-service-as-active? service)
                                   (assoc (first (filter :service/active? (:service/services service)))
                                          :num-completed (count (:fahrzeug/services @!fahrzeug))))))]
               ;; FIXME: find-service only looks at :service/code
               ;; this can pick the wrong service if there are multiple instances with the same service/code
               ;; but differing in other attributes (e.g. :service/template-quantity)
               (when-let [active-service (and @!fahrzeug (resolve-ui-views (find-service @!fahrzeug code)))]
                 (when (and (:service/active? active-service)
                            (not (completed-since-selected? @!fahrzeug selected-service)))
                   active-service))))))

(comment @!selected-service)

(defn workflow-menu-dropdown []
  (r/with-let [open? (r/atom false)
               workflow-menu (r/atom nil)
               dev-link (fn [title shortcut on-click]
                          (when (or (not ui/is-mobile?)
                                    (util/has-required-scope? :scope/developer))
                            [:a.text-gray-700.flex.gap-2.justify-between.pl-4.pr-6.py-2.text-sm.hover:bg-gray-100
                             {:on-click (fn [e]
                                          (.preventDefault e)
                                          (.stopPropagation e)
                                          (on-click))
                              :href "#"} (tr title) (when shortcut [:span.text-slate-500.tracking-wider.uppercase shortcut])]))]
    [:<>
     [scanner/simulate-scan-modal]
     [:div.flex.items-center.cursor-pointer.text-gray-600.flex-shrink-0
      {:on-click #(swap! open? not)
       :ref #(when % (reset! workflow-menu %))}
      [:div.w-8.flex.items-center.justify-center.flex-shrink-0
       {:class "h-[42px]"}
       [ui/icon {:size 24} :more-horizontal]]]
     (when @open?
       [:div.absolute.top-0.z-20.rounded-md.shadow-lg.bg-white.ring-1.ring-black.ring-opacity-5.focus:outline-none
        {:style {:right 5 :top 43}
         :ref (fn [el]
                (let [handler #(when-not (.contains @workflow-menu (.-target %)) (reset! open? false))]
                  (if el
                    (.addEventListener js/document "click" handler)
                    (.removeEventListener js/document "click" handler))))}
        [:div.py-1
         [:a.text-gray-700.block.pl-4.pr-6.py-2.text-sm.hover:bg-gray-100
          {:on-click #(do
                        (.preventDefault %)
                        (.reload js/window.location))
           :href "#"} (tr "Reload")]
         (let [vin @(deframe/!route-get-in [:fetch :result :vehicle/vin])
               ductile-id @(deframe/!route-get-in [:fetch :result :ductile/id])
               url (str/join "/" ["/compound" @deframe/!current-compound "vehicle" ductile-id])]
           (when (and vin ductile-id)
             [dev-link (tr "Desktop view") nil #(.focus (js/window.open url "_blank"))]))
         [dev-link (tr "Simulate scan") "⌘S" #(deframe/route-assoc! ::scanner/simulate-modal-open? true)]
         [dev-link (tr "Fill with magic wand") "⌘E" #(let [key-event (js/KeyboardEvent. "keydown" #js {:key "e" :code "KeyE" :keyCode 69 :which 69 :metaKey true})] (js/window.dispatchEvent key-event))]]])]))


(defn entry-route [{:keys [mode] transport-name :transport/name :as params}]
  (router/path (if transport-name
                 ['compound-ops :compound-name 'workflow 'entry (symbol mode) transport-name]
                 ['compound-ops :compound-name 'workflow 'entry (symbol mode)])
               {:compound-name (:compound/name params)}))

(defn scanner-view []
  (when @deframe/!scan-active?
    [:div.fixed.left-0.top-0.w-screen.h-screen {:style {:z-index 999}}
     [scanner/scanner-view {:width js/innerWidth
                            :height js/innerHeight
                            :scanning-mode (scanner/scan-mode :vin)
                            :scan/context :workflow
                            :on-vin-detected (fn [_state body]
                                               (let [{:keys [vin scan]} body]
                                                 (deframe/scan! {:scan/attr :vehicle/vin
                                                                 :scan/id (:ductile/id scan)
                                                                 :scan/result vin
                                                                 :scan/ocr-details body
                                                                 :scan/classified (scanner/classify-scan vin)
                                                                 :scan/source :camera})
                                                 (deframe/close-scanner!)))
                            :on-ocr-error (fn [_state body]
                                            (deframe/scan! {:scan/attr :vehicle/vin
                                                            :scan/ocr-details body
                                                            :scan/result ""
                                                            :scan/source :camera})
                                            (deframe/close-scanner!))}]
     [:div.fixed.right-2.top-2.text-white.z-10
      {:on-click (fn [_] (deframe/close-scanner!))}
      [ui/icon {:size 30} :x]]]))

(defn floating-scan-button []
  [:div.fixed.left-2.bottom-2.rounded-full.text-white.flex.items-center.justify-center.border-4.border-white
   {:class (str mobile/bg-primary mobile/bg-primary-active mobile/bg-primary-hover "z-[100] w-[52px] h-[52px]")
    :style {:box-shadow "0 0 3px rgba(0,0,0,.3), 0 4px 7px rgba(0,0,0,.2)"}
    :on-click (fn [_] (deframe/activate-scanner!))}
   [ui/icon {:size 20} :camera]])

(def with-hardware-scanner (partial ui/with-hardware-scanner scanner-view))

(defn compound-ops [{:compound/keys [entry-modes workflows service-lists legacy-arrival-scan bulk-entry? parking-mode?]} _match]
  (let [compound-name (get-in @deframe/!current-route [:path-params :compound-name])]
    (with-hardware-scanner
      {:view `compound-ops
       :on-scan fahrzeug-scan-navigate}
      [mobile/scroll-content
       [mobile/navbar {:title (tr "Start")
                       :back [mobile/back-link {:route (router/path '[])}]
                       :right-item [workflow-menu-dropdown]}]
       [:div.flex.items-center.justify-center.mt-8.-mb-4
        [:img {:src "/images/logo-green.png" :style {:height 50}}]]
       [floating-scan-button]
       (when-current-user-scope
        :scope/compound-entry
        [mobile/table {:inset? true
                       :items (if legacy-arrival-scan
                                (cond-> []
                                  (contains? entry-modes :mode/truck)
                                  (conj [mobile/list-item-disclose {:title (tr "Truck entry")
                                                                    :icon [mobile/framed-icon "bg-orange-600" :truck]
                                                                    :route (entry-route {:compound/name compound-name
                                                                                         :mode :truck})}])
                                  (contains? entry-modes :mode/train)
                                  (conj [mobile/list-item-disclose {:title (tr "Train entry")
                                                                    :icon [mobile/framed-icon "bg-green-600" :train]
                                                                    :route (entry-route {:compound/name compound-name :mode :train})}])

                                  (contains? entry-modes :mode/own-wheels)
                                  (conj [mobile/list-item-disclose {:title (tr "Own wheels entry")
                                                                    :icon [mobile/framed-icon "bg-yellow-600" :car]
                                                                    :route (entry-route {:compound/name compound-name :mode :own-wheels})}])

                                  (contains? entry-modes :mode/ship)
                                  (conj [mobile/list-item-disclose {:title (tr "Ship entry")
                                                                    :icon [mobile/framed-icon "bg-blue-600" :ship]
                                                                    :route (entry-route {:compound/name compound-name :mode :ship})}]))
                                (cond-> [[mobile/list-item-disclose {:title (tr "Entry")
                                                                     :icon [mobile/framed-icon "bg-green-600" :truck]
                                                                     :route (router/path
                                                                             '[compound-ops :compound-name arrival-scan]
                                                                             (:path-params @deframe/!current-route))}]]
                                  (and (not legacy-arrival-scan)
                                       bulk-entry?
                                       (not-empty (set/intersection entry-modes #{:mode/ship :mode/train})))
                                  (conj [mobile/list-item-disclose {:title (tr "Bulk Entry")
                                                                    :icon [mobile/framed-icon "bg-purple-600" :train]
                                                                    :route (router/path '[compound-ops :compound-name bulk-arrival-scan]
                                                                                        {:compound-name compound-name})}])))}])

       ;; Re-enable for Ravenna when there's the need
       #_[mobile/list-item-disclose {:title (tr "Ship exit")
                                     :icon [mobile/framed-icon "bg-blue-600" :anchor]
                                     :route (router/path '[compound-ops :compound-name workflow exit ship] {:compound-name compound-name})}]

       (when parking-mode?
         [mobile/table {:inset? true
                        :items [[mobile/list-item-disclose {:title (tr "Parking mode")
                                                            :icon mobile/icon-parking
                                                            :route (router/path '[compound-ops :compound-name workflow mode parking] {:compound-name compound-name})}]]}])
       (when-current-user-scope
        :scope/vehicle-exit-verification
        (when (not (#{"Ravenna" "Lonato" "Civitavecchia"} compound-name))
          [mobile/table {:inset? true
                         :items [[mobile/list-item-disclose {:title (tr "Exit verification")
                                                             :icon [mobile/framed-icon "bg-blue-600" :scan]
                                                             :route (router/path '[compound-ops :compound-name workflow exit verification]
                                                                                 {:compound-name compound-name})}]]}]))

       (when-current-user-scope
        :scope/workflow
        (when workflows
          [:<> (for [{:as workflow :workflow/keys [title scope]} workflows]
                 (when-current-user-scope
                  scope
                  [mobile/table {:inset? true
                                 :items [[mobile/list-item-disclose {:title (tr title)
                                                                     :icon [mobile/framed-icon "bg-blue-600" (or (:ui/icon workflow) :scan)]
                                                                     :route (router/path '[compound-ops :compound/name flow :workflow/type]
                                                                                         (merge workflow {:compound/name compound-name}))}]]}]))]))

       (when (= compound-name "Eitting")
         [mobile/table {:inset? true
                        :items [[mobile/list-item-disclose {:title (tr "Free storage spots")
                                                            :icon [mobile/framed-icon "bg-blue-600" :parking]
                                                            :route (router/path '[compound-ops :compound/name storage-rows-with-free-spots]
                                                                                {:compound/name compound-name})}]]}])

       (when-current-user-scope
        #{:scope/service-list}
        (when (not-empty service-lists)
          [mobile/table {:title (tr "Service lists")
                         :inset? true
                         :items (mapv (fn [item]
                                        ^{:key (:id item)}
                                        [mobile/list-item-disclose
                                         (-> item
                                             (select-keys [:title :icon])
                                             (assoc :route
                                                    (router/path '[compound-ops :compound-name service-lists :service-list-type]
                                                                 {:service-list-type (name (:id item))
                                                                  :compound-name compound-name}))
                                             (update :icon (fn [icon] [mobile/framed-icon "bg-blue-600" icon])))])
                                      service-lists)}]))

       (when-let [extra-items (not-empty (remove nil?
                                                 [(let [required-scopes (:scope (routes/template->data '[compound-ops :compound-name ars-id-coupling]))]
                                                    (when-current-user-scope
                                                     required-scopes [mobile/list-item-disclose {:title (tr "ARS-ID Coupling")
                                                                                                 :icon [mobile/framed-icon "bg-blue-600" :scan]
                                                                                                 :route (router/path '[compound-ops :compound-name ars-id-coupling]
                                                                                                                     {:compound-name compound-name})}]))
                                                  (when (#{"Eitting" "Krefeld"} compound-name)
                                                    (when-current-user-scope
                                                     #{:scope/developer}
                                                     [mobile/list-item-disclose {:title (tr "Workflow Simulator")
                                                                                 :icon [mobile/framed-icon "bg-blue-600" :car]
                                                                                 :route (router/path '[compound-ops :compound-name workflow-simulator]
                                                                                                     {:compound-name compound-name})}]))
                                                  (when (#{"Eitting" "Krefeld"} compound-name)
                                                    (when-current-user-scope
                                                     #{:scope/developer}
                                                     [mobile/list-item-disclose {:title (tr "ELOG Scanner")
                                                                                 :icon [mobile/framed-icon "bg-blue-600" :scan]
                                                                                 :route (router/path '[compound-ops :compound-name elog-scanner]
                                                                                                     {:compound-name compound-name})}]))]))]
         [mobile/table {:inset? true
                        :items extra-items}])])))

(defn compound-ops-backlink [fahrzeug]
  [mobile/back-link {:route (router/path '[compound-ops :compound/name] fahrzeug)}])

(defn context-aware-compound-ops-backlink
  "use previous-url for backlink, when set, otherwise go back to compound-ops"
  [fahrzeug]
  [mobile/back-link {:route (or @(deframe/!route-get :previous-url)
                                (router/path '[compound-ops :compound/name] fahrzeug))}])

(defn manual-announcement-view [fahrzeug {:keys [service]}]
  (r/with-let [!contract (r/atom nil)]
    (let [vin (:vehicle/vin fahrzeug)]
      [mobile/scroll-content
       [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]

       [mobile/navbar {:title (tr "Select Contract")
                       :right-item [workflow-menu-dropdown]
                       :back [compound-ops-backlink fahrzeug]}]
       [:div
        [mobile/table {:title (tr "Customer contract") ;; "Auftragskunde"
                       :inset? true
                       :items (mapv (fn [w]
                                      [mobile/list-item-radio {:label w
                                                               :input {:checked (= @!contract w)
                                                                       :on-change #(reset! !contract w)}}])
                                    (:contracts/available service))}]
        [toolbar {:disabled (not @!contract)
                  :loading @deframe/!loading?
                  :on-click (fn [_evt] (deframe/call-lambda 'ductile.workflow/perform-service
                                                            {:service/code (:service/code service)
                                                             :ductile/id (:ductile/id fahrzeug)
                                                             :service/attrs {:fahrzeug/contract [:contract/name @!contract]}}))}]]])))

(defn simulator [{:as data
                  ::simulator/keys [available-scenarios
                                    announced-vins]
                  :keys [speculative-transaction-opts]} _match]
  (r/with-let [!state (r/atom {::simulator/scenario nil ::simulator/phase :entry})]
    [mobile/scroll-content
     [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
     [mobile/navbar {:title (tr "Workflow Simulator")
                     :right-item [workflow-menu-dropdown]
                     :back [compound-ops-backlink data]}]
     [:div
      (for [[contract-name vins] announced-vins]
        [mobile/table {:title (or contract-name (tr "Unknown"))
                       :inset? true
                       :items (mapv (fn [vin]
                                      [mobile/list-item-radio {:label vin
                                                               :input {:checked (= (:vehicle/vin @!state) vin)
                                                                       :on-change #(swap! !state (fn [state]
                                                                                                   (cond-> (assoc state :vehicle/vin vin)
                                                                                                     contract-name (assoc ::simulator/scenario contract-name))))
                                                                       :data-testid vin}}])
                                    vins)}])
      [mobile/table {:title (tr "Workflow Scenario")
                     :inset? true
                     :items (mapv (fn [w]
                                    [mobile/list-item-radio {:label w
                                                             :input {:checked (= (::simulator/scenario @!state) w)
                                                                     :on-change #(swap! !state assoc ::simulator/scenario w)
                                                                     :data-testid w}}])
                                  available-scenarios)}]
      [mobile/table {:title (tr "Phase")
                     :inset? true
                     :items (mapv (fn [w]
                                    [mobile/list-item-radio {:label w
                                                             :input {:checked (= (::simulator/phase @!state) w)
                                                                     :on-change #(swap! !state assoc ::simulator/phase w)
                                                                     :data-testid w}}])
                                  [:entry :compound])}]
      [toolbar {:disabled (not (or (:vehicle/vin @!state) (::simulator/scenario @!state)))
                :on-click #(-> (if (http/speculative-transactions?)
                                 (js/Promise.resolve {})
                                 (http/$enable-speculative-transactions! {:ductile.datomic.speculative-transactions/scope :session}))
                               (.then (fn [result]
                                        (if (:error result)
                                          (js/console.warn result)
                                          (router/navigate-to
                                           (if (:vehicle/vin @!state)
                                             (router/path
                                              '[compound-ops :compound/name workflow :vehicle/vin]
                                              {:compound/name (:compound/name data)
                                               :vehicle/vin (:vehicle/vin @!state)})
                                             (router/path
                                              '[compound-ops :compound/name arrival-scan]
                                              {:compound/name (:compound/name data)})))))))}]]]))

(defn entry-mode-label [{:keys [entry/mode]}]
  (case mode
    :mode/truck
    (tr "Truck entry")
    :mode/train
    (tr "Train entry")
    :mode/ship
    (tr "Ship entry")
    :mode/own-wheels
    (tr "Own wheels entry")
    (tr "Unknown entry mode")))

(defn coupling-ars-id-view [fahrzeug {:keys [service]}]
  (let [{:keys [error scanned-ars-id]} @(deframe/!route-get ::state)]
    (with-hardware-scanner {:view `coupling-ars-id-view
                            :on-scan (fn [{:as scan :scan/keys [result classified]}]
                                       (when scan
                                         (when-let [ars-id (:ars/id classified)]
                                           (deframe/route-assoc-in! [::state :scanned-ars-id] ars-id))
                                         (deframe/reset-current-scan!)))}
      [:<>
       [mobile/navbar {:title (entry-mode-label fahrzeug)
                       :subtitle (tr "Entry")
                       :right-item [workflow-menu-dropdown]
                       :back [compound-ops-backlink fahrzeug]}]
       [ui/error-view @(deframe/!route-get-in [:fetch :error])]
       [:div
        [mobile/inline-prompt :scan [:div (tr "Please scan the ARS-ID with the Hardware Scanner")
                                     (when-not ui/is-mobile? simulate-scan-hint)]]

        [mobile/table {:inset? true
                       :items [[mobile/list-item {:icon mobile/icon-task-complete
                                                  :title [:span {:class "text-[1rem]"}
                                                          (tr "VIN detected")]
                                                  :description [:div (:vehicle/vin fahrzeug)]}]]}]

        [mobile/table {:inset? true
                       :items [(if scanned-ars-id
                                 [mobile/list-item {:icon mobile/icon-task-complete
                                                    :title [:span {:class "text-[1rem]"}
                                                            (tr "ARS-ID detected")]
                                                    :description [:div scanned-ars-id]}]
                                 [mobile/list-item {:icon mobile/icon-scan
                                                    :title [:span {:class "text-[1rem]"
                                                                   :data-testid "ars-id"}
                                                            (tr "Please scan the ARS-ID")]}])]}]
        [mobile/toolbar
         [camera-button]
         [mobile/button-full {:disabled (not scanned-ars-id)
                              :loading @deframe/!loading?
                              #_#_:loading @(deframe/!get ::deframe/current-workflow-fahrzeug)
                              :on-click (fn [_] (deframe/call-lambda
                                                 'ductile.workflow/perform-service
                                                 {:service/code (:service/code service)
                                                  :ductile/id (:ductile/id fahrzeug)
                                                  :service/attrs (-> fahrzeug
                                                                     (select-keys [:entry/mode])
                                                                     (assoc :ars/id scanned-ars-id))}))}
          (tr "Confirm")]]]])))

(comment
  ;; verifying that `when-ok` macro works as expected with `p/->`
  (p/-> (p/timeout 1000 {:error 1})
        (when-ok (prn :NOT-OK))) ;; is not printed

  (p/-> (p/timeout 2000 {})
        (when-ok (prn :OK)))) ;; is printed

(def translate-color-names
  ;; TODO let's use `tr` for this eventually
  {"gray" "grau" "brown" "braun" "orange" "orange" "green" "grün" "blue" "blau" "red" "rot"})

(defn entry-inspection-view [fahrzeug {:keys [service on-result in-modal?]}]
  (r/with-let [!local-state (r/atom {:day? true :in-order? true :engine-type (:vehicle/engine-type fahrzeug)})
               engine-type-radio (fn [engine-type label]
                                   [mobile/list-item-radio {:label label
                                                            :input {:checked (= engine-type (:engine-type @!local-state))
                                                                    :on-change #(swap! !local-state assoc :engine-type engine-type)}}])]
    (let [{:keys [in-order? day? ice-snow? engine-type new?]} @!local-state]
      [:div
       [mobile/scroll-content
        [mobile/navbar (merge {:title "Eingangskontrolle"
                               :subtitle "LKW Eingang"}
                              (when-not in-modal?
                                {:back [compound-ops-backlink fahrzeug]}))]
        [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
        [mobile/info-bar fahrzeug]
        [:div
         [mobile/table {:title "Fahrzeugtyp"
                        :items [[engine-type-radio :engine.type/internal-combustion "Verbrennungsmotor"]
                                [engine-type-radio :engine.type/hybrid "Hybrid"]
                                [engine-type-radio :engine.type/electric "Elektrisch"]]}]
         [mobile/table {:title "Übernahme bei..."
                        :items
                        [[mobile/list-item-radio {:label "Tag" :input {:checked day?
                                                                       :on-change #(swap! !local-state assoc :day? true)}}]
                         [mobile/list-item-radio {:label "Nacht" :input {:checked (false? day?)
                                                                         :on-change #(swap! !local-state assoc :day? false)}}]]}]
         [mobile/table {:title "Übernahme bedingungen"
                        :items [[mobile/list-item-checkbox {:label "Eis & Schnee"
                                                            :input {:checked ice-snow?
                                                                    :on-change #(swap! !local-state update :ice-snow? not)}}]]}]

         [mobile/table {:title "Kontrolle"
                        :items [[mobile/list-item-radio {:label "In Ordnung"
                                                         :input {:checked in-order?
                                                                 :on-change #(swap! !local-state assoc :in-order? true)}}]
                                [mobile/list-item-radio {:label "Nicht in Ordnung"
                                                         :input {:checked (false? in-order?)
                                                                 :on-change #(swap! !local-state assoc :in-order? false)}}]]}]
         [toolbar {:disabled (nil? engine-type)
                   :loading @deframe/!loading?
                   :on-click #(deframe/call-lambda
                               (if on-result {:on-result on-result} {})
                               'ductile.workflow/perform-service
                               {:service/code (:service/code service)
                                :ductile/id (:ductile/id fahrzeug)
                                :service/attrs {:vehicle/engine-type engine-type
                                                :acceptance.conditions/day-or-night (if day? :day :night)
                                                :acceptance.conditions/ice-&-snow? (boolean ice-snow?)
                                                :entry.inspection/in-ordnung? in-order?}})}]]]])))

(defn service-view-backlink [fahrzeug]
  (cond
    (and (:ductile/id fahrzeug) (not (:entry/datetime fahrzeug)))
    [mobile/back-link {:route (router/path '[compound-ops :compound/name] fahrzeug)
                       :confirm (tr "Entry is not complete. Really go back to main menu?")}]

    (and @!selected-service (:ductile/id fahrzeug))
    [mobile/back-link {:route (fahrzeug-route fahrzeug)
                       :on-click #(deselect-service!)}]

    :else [compound-ops-backlink fahrzeug]))


(defn record-high-voltage-battery-charge-status-view
  [{:vehicle/keys [vin] :as fahrzeug} {:keys [service on-result in-modal?] :as args}]
  (let [requested? ((some-fn :requested/charge-percentage :requested/charge-kwh) service)]
    (r/with-let [!battery-charge (r/atom nil)
                 available-units (or (:service/available-units service)
                                     #{:unit/kwh :unit/percentage})
                 !unit (r/atom nil)]
      [:div
       [(if in-modal? :div.rounded-b-lg.border.border-t-0.border-gray-300 mobile/scroll-content)
        [mobile/navbar (merge {:title (if requested?
                                        (tr "Charge High-Voltage battery")
                                        (tr "Record High-Voltage battery charge status"))}
                              (when-not in-modal?
                                {:back [service-view-backlink fahrzeug args]}))]
        [mobile/info-bar fahrzeug]
        [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
        [mobile/inline-prompt :car (if requested?
                                     [:div
                                      [:div (tr "Please charge the High-Voltage battery with the following amount and confirm:")]
                                      [:div.text-xl.font-bold.mt-4 (ui/template-quantity service)]]
                                     (tr "Enter the current charge of the High-Voltage battery"))]
        [mobile/table {:title (tr "Current charge") ;; "Aktueller Ladezustand"
                       :inset? true
                       :items [[mobile/list-item-input {:input {:placeholder (tr "Charge")
                                                                :disabled (completed? service)
                                                                :value @!battery-charge
                                                                :data-testid "hv-battery-charge"
                                                                :on-input #(reset! !battery-charge (.. % -target -value))}}]
                               (when (available-units :unit/percentage)
                                 [mobile/list-item-radio {:label "%"
                                                          :input {:checked (= :unit/percentage @!unit)
                                                                  :disabled (completed? service)
                                                                  :data-testid "hv-percentage"
                                                                  :on-change #(reset! !unit :unit/percentage)}}])
                               (when (available-units :unit/kwh)
                                 [mobile/list-item-radio {:label "kWh"
                                                          :input {:checked (= :unit/kwh @!unit)
                                                                  :disabled (completed? service)
                                                                  :data-testid "hv-kwh"
                                                                  :on-change #(reset! !unit :unit/kwh)}}])]}]
        [toolbar {:disabled (or (nil? @!battery-charge)
                                (nil? @!unit)
                                (completed? service))
                  :loading @deframe/!loading?
                  :on-click (fn [_] (deframe/call-lambda
                                     (if on-result {:on-result on-result} {})
                                     'ductile.workflow/perform-service
                                     {:ductile/id (:ductile/id fahrzeug)
                                      :service/code (:service/code service)
                                      :service/result {:vehicle.status/hv-battery-charge (parse-double @!battery-charge)
                                                       :service/unit @!unit}}))}]]])))

(defn record-battery-charge-status-lonato-view [fahrzeug {:keys [service] :as args}]
  (r/with-let [battery-charge (r/atom (:vehicle.status/battery-charge service))
               !battery-status (r/atom nil)]
    [:div
     [mobile/scroll-content
      [mobile/navbar {:title (tr "Record 12V battery status")
                      :back [service-view-backlink fahrzeug args]}]
      [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
      [mobile/info-bar fahrzeug]
      [mobile/inline-prompt :car (tr "Enter the current charge of the 12V Battery")]
      [mobile/table {:title (tr "Current charge")
                     :inset? true
                     :items [[mobile/list-item-input {:input {:placeholder (tr "Charge")
                                                              :disabled (completed? service)
                                                              :value @battery-charge
                                                              :on-input #(reset! battery-charge (.. % -target -value))
                                                              :data-testid "charge"}}]
                             [mobile/list-item-radio {:label (tr "Battery Ok")
                                                      :input {:checked (= :battery/ok @!battery-status)
                                                              :data-testid "battery-ok"
                                                              :disabled (completed? service)
                                                              :on-change #(reset! !battery-status :battery/ok)}}]
                             [mobile/list-item-radio {:label (tr "Battery needs charging")
                                                      :input {:checked (= :battery/charge-needed @!battery-status)
                                                              :data-testid "battery-needs-charging"
                                                              :disabled (completed? service)
                                                              :on-change #(reset! !battery-status :battery/charge-needed)}}]
                             [mobile/list-item-radio {:label (tr "Battery needs to be replaced")
                                                      :input {:checked (= :battery/replacement-needed @!battery-status)
                                                              :data-testid "battery-needs-replacement"
                                                              :disabled (completed? service)
                                                              :on-change #(reset! !battery-status :battery/replacement-needed)}}]]}]
      [toolbar {:disabled (or (nil? @battery-charge)
                              (completed? service))
                :loading @deframe/!loading?
                :on-click (fn [_] (deframe/call-lambda
                                   'ductile.workflow/perform-service
                                   {:ductile/id (:ductile/id fahrzeug)
                                    :service/code (:service/code service)
                                    :service/result {:vehicle.status/battery-status @!battery-status}
                                    :service/attrs {:vehicle.status/battery-charge (int @battery-charge)
                                                    :service/unit :unit/volt}}))}]]]))

(defn record-battery-charge-status-view [fahrzeug {:keys [service] :as args}]
  (r/with-let [!battery-status (r/atom nil)]
    [:div
     [mobile/scroll-content
      [mobile/navbar {:title (tr "Record 12V battery status")
                      :back [service-view-backlink fahrzeug args]}]
      [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
      [mobile/info-bar fahrzeug]
      [mobile/inline-prompt :car (tr "Enter the current status of the 12V Battery")]
      [mobile/table {:title (tr "Current 12V battery status")
                     :inset? true
                     :items [[mobile/list-item-radio {:label (tr "Battery Ok")
                                                      :input {:checked (= :battery/ok @!battery-status)
                                                              :data-testid "battery-ok"
                                                              :disabled (completed? service)
                                                              :on-change #(reset! !battery-status :battery/ok)}}]
                             [mobile/list-item-radio {:label (tr "Battery needs charging")
                                                      :input {:checked (= :battery/charge-needed @!battery-status)
                                                              :data-testid "battery-needs-charging"
                                                              :disabled (completed? service)
                                                              :on-change #(reset! !battery-status :battery/charge-needed)}}]]}]
      [toolbar {:disabled (or (nil? @!battery-status)
                              (completed? service))
                :loading @deframe/!loading?
                :on-click (fn [_] (deframe/call-lambda
                                   'ductile.workflow/perform-service
                                   {:ductile/id (:ductile/id fahrzeug)
                                    :service/code (:service/code service)
                                    :service/result {:vehicle.status/battery-status @!battery-status}}))}]]]))

(defn confirm-tire-adjustment-view [fahrzeug {:keys [service] :as args}]
  [:div
   [mobile/scroll-content
    [mobile/navbar {:title (tr "Adjust tire pressure")
                    :back [service-view-backlink fahrzeug args]}]
    [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
    [mobile/info-bar fahrzeug]
    [mobile/inline-prompt :car [:div
                                [:div (tr "Please adjust the tire pressure until the following pressure and confirm:")]
                                [:div.text-xl.font-bold.mt-4 (ui/template-quantity service)]]]
    [toolbar {:disabled (completed? service)
              :loading @deframe/!loading?
              :on-click (fn [] (deframe/call-lambda
                                'ductile.workflow/perform-service
                                {:ductile/id (:ductile/id fahrzeug)
                                 :service/code (:service/code service)
                                 :service/result {:vehicle.status/tire-pressure (:requested/pressure-bar service)
                                                  :service/unit :unit/bar}}))}]]])


(defn confirm-battery-charging-view [fahrzeug {:keys [service] :as args}]
  [:div
   [mobile/scroll-content
    [mobile/navbar {:title (tr "Charge 12V battery")
                    :back [service-view-backlink fahrzeug args]}]
    [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
    [mobile/info-bar fahrzeug]
    [mobile/inline-prompt :car (tr "Confirm 12V Battery was charged")]

    [toolbar {:disabled (completed? service)
              :loading @deframe/!loading?
              :on-click (fn [] (deframe/call-lambda
                                'ductile.workflow/perform-service
                                {:ductile/id (:ductile/id fahrzeug)
                                 :service/code (:service/code service)
                                 :service/result {:vehicle.status/battery-status :battery/ok}}))}]]])

(defn record-tire-pressure-view [fahrzeug {:keys [service] :as args}]
  (r/with-let [tire-pressure (r/atom (:vehicle.status/tire-pressure service))]

    [:div
     [mobile/scroll-content
      [mobile/navbar {:title (tr "Record tire pressure")
                      :back [service-view-backlink fahrzeug args]}]
      [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
      [mobile/info-bar fahrzeug]
      [mobile/inline-prompt :car  (tr "Enter the lowest pressure of the 4 tires")]
      [mobile/table {:title (tr "Current pressure")
                     :inset? true
                     :items [[mobile/list-item-input {:input {:placeholder "bar"
                                                              :data-testid "tire-pressure"
                                                              :disabled (completed? service)
                                                              :type "number"
                                                              :input-mode "decimal"
                                                              :step "0.1"
                                                              :value @tire-pressure
                                                              :max 100
                                                              :min 0
                                                              :on-input #(reset! tire-pressure (.. % -target -value))}}]]}]
      [toolbar {:disabled (or (nil? @tire-pressure)
                              (completed? service))
                :loading @deframe/!loading?
                :on-click (fn [_] (deframe/call-lambda
                                   'ductile.workflow/perform-service
                                   {:ductile/id (:ductile/id fahrzeug)
                                    :service/code (:service/code service)
                                    :service/result {:vehicle.status/tire-pressure (* 1.0 (double @tire-pressure))
                                                     :service/unit :unit/bar}}))}]]]))

(defn italian-fueling-view [fahrzeug {:keys [service] :as args}]
  (r/with-let [ref-name :fueling
               !state (r/atom {:fuel/quantity (str (or (:service/quantity service)
                                                       (:requested/fuel-liters service)))
                               ;:fueling/start-datetime (js/Date.)
                               ;:fueling/end-datetime (js/Date.)
                               :fuel/type (get service :fuel/type :fuel.type/petrol)})]

    (let [{:fuel/keys [quantity type]} @!state]
      [:div
       [mobile/scroll-content
        [mobile/navbar {:title (str "Rifornimento " (ui/template-quantity service))
                        :subtitle (tr "Services")
                        :back [service-view-backlink fahrzeug args]
                        :right-item [workflow-menu-dropdown]}]
        [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
        [mobile/info-bar fahrzeug]
        [mobile/table {:title "Di quanti litri è stato il rifornimento?"
                       :inset? true
                       :items [[mobile/list-item-input {:input {:value (str quantity)
                                                                :disabled (completed? service)
                                                                :type "number"
                                                                :input-mode "numeric"
                                                                :on-input #(swap! !state assoc :fuel/quantity (.. % -target -value))}}]
                               [mobile/list-item-radio {:label "Benzina"
                                                        :input {:checked (= :fuel.type/petrol type)
                                                                :disabled (completed? service)
                                                                :on-change #(swap! !state assoc :fuel/type :fuel.type/petrol)}}]
                               [mobile/list-item-radio {:label "Diesel"
                                                        :input {:checked (= :fuel.type/diesel type)
                                                                :disabled (completed? service)
                                                                :on-change #(swap! !state assoc :fuel/type :fuel.type/diesel)}}]]}]

        [toolbar {:disabled (completed? service)
                  :on-click (fn [_] (deframe/call-lambda
                                     'ductile.workflow/perform-service
                                     {:ductile/id (:ductile/id fahrzeug)
                                      :service/code (:service/code service)
                                      :service/attrs {:fuel/type type
                                                      :service/unit :unit/liter
                                                      :service/quantity (int quantity)}}))}]]])))

(defn fueling-view [fahrzeug {:keys [service] :as args}]
  (r/with-let [ref-name :fueling
               !state (r/atom {:fuel/quantity (str (or (:service/quantity service)
                                                       (:requested/fuel-liters service)))
                                        ;:fueling/start-datetime (js/Date.)
                                        ;:fueling/end-datetime (js/Date.)
                               :fuel/type (get service :fuel/type :fuel.type/petrol)})]

    (with-hardware-scanner
      {:view `fueling-view
       :on-scan (fn [scan-data]
                  (let [{:as _scan :keys [scan/result scan/type]} scan-data
                        {:fueling/keys [start-datetime end-datetime]} @!state]
                    (when (= type "qrcode")
                      (let [fueling-qr? (= (str/lower-case result)
                                           (str/lower-case "FZ-TANKEN"))]
                        (cond
                          (and fueling-qr? (not start-datetime))
                          (swap! !state assoc :fueling/start-datetime (js/Date.))

                          (and fueling-qr? start-datetime (not end-datetime))
                          (swap! !state assoc :fueling/end-datetime (js/Date.))))

                      (deframe/reset-current-scan!))))}
      (let [{:fueling/keys [start-datetime end-datetime]
             :fuel/keys [quantity type]} @!state]
        [:div
         [mobile/scroll-content
          [mobile/navbar {:title (str "Tanken" (ui/template-quantity service))
                          :subtitle (tr "Services")
                          :back [service-view-backlink fahrzeug args]
                          :right-item [workflow-menu-dropdown]}]
          [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
          [mobile/info-bar fahrzeug]
          [mobile/table {:inset? true
                         :items [(if start-datetime
                                   [mobile/list-item {:icon mobile/icon-task-complete
                                                      :title [:span {:class "text-[1rem]"}
                                                              "Tanksäule gescannt"]}]
                                   [mobile/list-item {:icon mobile/icon-scan
                                                      :title [:span {:class "text-[1rem]"}
                                                              "Fahren Sie zur Tankstelle und scannen Sie den QR-Code an der Tanksäule, um den Tankprozess zu starten"]}])]}]

          (when start-datetime
            [mobile/table {:title "Wie viele Liter wurde getankt?"
                           :inset? true
                           :items [[mobile/list-item-input {:input {:value (str quantity)
                                                                    :disabled (completed? service)
                                                                    :type "number"
                                                                    :input-mode "numeric"
                                                                    :on-input #(swap! !state assoc :fuel/quantity (.. % -target -value))}}]
                                   [mobile/list-item-radio {:label "Benzin"
                                                            :input {:checked (= :fuel.type/petrol type)
                                                                    :disabled (completed? service)
                                                                    :on-change #(swap! !state assoc :fuel/type :fuel.type/petrol)}}]
                                   [mobile/list-item-radio {:label "Diesel"
                                                            :input {:checked (= :fuel.type/diesel type)
                                                                    :disabled (completed? service)
                                                                    :on-change #(swap! !state assoc :fuel/type :fuel.type/diesel)}}]
                                   #_[mobile/list-item-radio {:label "LPG"
                                                              :input {:checked (= :fuel.type/lpg @!fuel-type)
                                                                      :on-change #(reset! !fuel-type :fuel.type/lpg)}}]]}])

          (when start-datetime
            [mobile/table {:inset? true
                           :items [(if (or (completed? service)
                                           end-datetime)
                                     [mobile/list-item {:icon mobile/icon-task-complete
                                                        :title [:span {:class "text-[1rem]"}
                                                                "Tanken abgeschlossen"]}]
                                     [mobile/list-item {:icon mobile/icon-scan
                                                        :title [:span {:class "text-[1rem]"}
                                                                "Scannen Sie den QR-Code an der Tanksäule, um den Tankprozess abzuschließen"]
                                                        :description "Verwenden Sie zum Scannen den Hardware-Scanner."}])]}])

          [toolbar {:disabled (or (completed? service)
                                  (not end-datetime))
                    :on-click (fn [_] (deframe/call-lambda
                                       'ductile.workflow/perform-service
                                       {:ductile/id (:ductile/id fahrzeug)
                                        :service/code (:service/code service)
                                        :service/attrs {:fuel/type type
                                                        :service/unit :unit/liter
                                                        :service/quantity (int quantity)
                                                        :service/start-datetime start-datetime
                                                        :service/end-datetime end-datetime}}))}]]]))))


(defn record-special-equipment-view [fahrzeug {:keys [service in-modal? on-result] :as args}]
  (r/with-let [!special-equipment (r/atom (cond
                                            (not-empty (:vehicle/equipment fahrzeug))
                                            (set/intersection (set (:service/equipment-list service))
                                                              (set (:vehicle/equipment fahrzeug)))

                                            (= :mode/ship (:entry/mode fahrzeug))
                                            (:service/equipment-list service)

                                            :else #{}))]
    [:div
     [mobile/scroll-content
      [mobile/navbar (merge {:title (:service/code service)}
                            (when-not in-modal?
                              {:back [service-view-backlink fahrzeug args]}))]
      [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
      [mobile/info-bar fahrzeug]
      [mobile/table {:items (mapv (fn [label]
                                    [mobile/list-item-checkbox {:label label
                                                                :input {:checked (contains? @!special-equipment label)
                                                                        :on-change #(swap! !special-equipment (fn [s] (if (contains? s label)
                                                                                                                        (disj s label)
                                                                                                                        (conj s label))))}}])
                                  (:service/equipment-list service))}]
      [toolbar {:loading @deframe/!loading?
                :on-click (fn [_] (deframe/call-lambda
                                   (if on-result {:on-result on-result} {})
                                   'ductile.workflow/perform-service
                                   {:ductile/id (:ductile/id fahrzeug)
                                    :service/code (:service/code service)
                                    :service/attrs {:vehicle/equipment @!special-equipment}}))}]]]))

(def ^:private storage-care-colors ["gray" "brown" "orange" "green" "blue" "red"])

(defn ^js color+today->production-datetime
  "Look back up to 12 weeks to find the most recent week with the matching color.
   Need more than 6 weeks because 52 weeks don't divide evenly by 6 colors,
   so at year boundary week 52 is green (index 3), week 1 is gray (index 0), skipping blue/red

   Example: today is 2026-01-14 (ISO week 3, color orange)
   Looking back for blue (index 4):
   weeks 2      1     52    51     50     49
   color brown  gray  green orange brown  gray

   but blue last occurred in week 47 (2025-11-17), 8 weeks ago"
  ([color] (color+today->production-datetime color (.now DateTime)))
  ([color ^js today]
   (let [^js matching-date
         (->> (range 1 13)
              (keep (fn [weeks-ago]
                      (let [past-date (.minus today (clj->js {"weeks" weeks-ago}))
                            week-number (.-weekNumber past-date)
                            color-idx (mod (dec week-number) 6)]
                        (when (= color (nth storage-care-colors color-idx))
                          past-date))))
              first)]
     (some-> matching-date (.toUTC) (.startOf "day")))))

(comment
  ;; old (buggy) implementation for comparison
  (defn color+today->production-datetime-OLD [color ^js today]
    (let [ll storage-care-colors
          current-week-number (.-weekNumber today)
          last-week-n (->> (into {} (map-indexed (fn [idx c] [(inc idx) c]) (take 52 (cycle ll))))
                           (filter (fn [[k v]]
                                     (and
                                      (> k (- current-week-number 7))
                                      (< k current-week-number)
                                      (= v color))))
                           first)]
      (when last-week-n
        (-> today
            (.minus (clj->js {"weeks" (- current-week-number (first last-week-n))}))
            (.toUTC)
            (.startOf "day")))))

  ;; manual regression test, see old fail in jan/feb
  (doseq [day (range 1 3000)]
    (let [today (.plus (.now DateTime) (clj->js {"days" day}))
          old-results (map #(some-> (color+today->production-datetime-OLD % today) .toISODate) storage-care-colors)
          new-results (map #(some-> (color+today->production-datetime % today) .toISODate) storage-care-colors)]
      (when (not= old-results new-results)
        (prn (.toISODate today)
             "OLD" old-results
             "NEW" new-results)))))

(defn storage-care-start-date-view [value path {:as field :ui/keys [attr title subtitle disabled?]}]
  (let [iso-value (and value (.toISODate (.fromJSDate DateTime value)))]
    (into [:<>
           [mobile/list-item-input {:input {:disabled disabled?
                                            :value iso-value
                                            :on-change #(deframe/route-assoc-in! path (js/Date. (.. % -target -value)))
                                            :class "tabular-nums"
                                            :type "date"}}]]
          (mapv (fn [[display-color color-name]]
                  (let [iso-date (.toISODate (color+today->production-datetime color-name))]
                    [mobile/list-item-radio {:label [:div.flex.items-center.text-xs
                                                     [mobile/icon-color display-color]
                                                     [:span.m-2.text-sm (translate-color-names color-name)]
                                                     [:span.ml-4 (.toLocaleString (color+today->production-datetime color-name)
                                                                                  (clj->js {:month "numeric"
                                                                                            :day "numeric"
                                                                                            :year "numeric"}))]]
                                             :input {:value color-name
                                                     :data-testid (str :fahrzeug/storage-care-start-datetime "@" color-name)
                                                     :disabled disabled?
                                                     :checked (= iso-value iso-date)
                                                     :on-change #(deframe/route-assoc-in! path (js/Date. iso-date))}}]))
                {"#0A9900" "green"
                 "#20FFFF" "blue"
                 "#9B3B08" "brown"
                 "#FF9933" "orange"
                 "#FE0500" "red"
                 "#A6A6A6" "gray"}))))

(defn record-storage-care-start-date-view [fahrzeug {:keys [service] :as args}]
  (r/with-let [!selected-date (r/atom (.toISODate (.fromJSDate DateTime (:fahrzeug/storage-care-start-datetime fahrzeug))))]

    (let [color-component (fn [[display-color color-name]]
                            (let [date (.toISODate (color+today->production-datetime color-name))]
                              [mobile/list-item-radio {:label [:div.flex.items-center.text-xs
                                                               [mobile/icon-color display-color]
                                                               [:span.m-2.text-sm (translate-color-names color-name)]
                                                               [:span.ml-4 (.toLocaleString (color+today->production-datetime color-name)
                                                                                            (clj->js {:month "numeric"
                                                                                                      :day "numeric"
                                                                                                      :year "numeric"}))]]
                                                       :input {:value color-name
                                                               :data-testid (str :fahrzeug/storage-care-start-datetime
                                                                                 "@"
                                                                                 color-name)
                                                               :disabled (completed? service)
                                                               :checked (= @!selected-date date)
                                                               :on-change #(reset! !selected-date date)}}]))]

      [:div
       [mobile/scroll-content
        [mobile/navbar {:title "Startdatum Lagerpflege ermitteln"
                        :subtitle (tr "Services")
                        :back [service-view-backlink fahrzeug args]}]
        [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
        [mobile/info-bar fahrzeug]
        [mobile/table {:title "Produktionsdatum"
                       :subtitle "Datum eingeben oder Stickerfarbe auswählen"
                       :items (into [[mobile/list-item-input {:input {:disabled (completed? service)
                                                                      :value @!selected-date
                                                                      :on-change #(reset! !selected-date (.. % -target -value))
                                                                      :type "date"}}]]
                                    (mapv color-component {"#0A9900" "green"
                                                           "#20FFFF" "blue"
                                                           "#9B3B08" "brown"
                                                           "#FF9933" "orange"
                                                           "#FE0500" "red"
                                                           "#A6A6A6" "gray"}))}]
        [toolbar {:disabled (or (str/blank? @!selected-date)
                                (completed? service))
                  :loading @deframe/!loading?
                  :on-click (fn [_] (deframe/call-lambda 'ductile.workflow/perform-service
                                                         {:ductile/id (:ductile/id fahrzeug)
                                                          :service/code (:service/code service)
                                                          :service/attrs {:fahrzeug/storage-care-start-datetime (js/Date. @!selected-date)}}))}]]])))

(defn mounting-license-plate-view [fahrzeug {:keys [service] :as args}]
  [:div
   (log/info :ui service)
   [mobile/scroll-content
    [mobile/navbar {:title (:service/code service)
                    :subtitle (tr "Services")
                    :right-item [workflow-menu-dropdown]
                    :back [service-view-backlink fahrzeug args]}]
    [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
    [mobile/info-bar fahrzeug]
    (if-let [license-plate (:vehicle/license-plate fahrzeug)]
      [:div
       [mobile/inline-prompt :car (tr "Please mount the license plate on the vehicle")]
       [mobile/table {:items [[mobile/list-item {:title license-plate}]]}]
       [toolbar {:disabled (or (nil? license-plate)
                               (completed? service))
                 :loading @deframe/!loading?
                 :on-click (fn [_] (deframe/call-lambda
                                    'ductile.workflow/perform-service
                                    {:ductile/id (:ductile/id fahrzeug)
                                     :service/code (:service/code service)
                                     :service/attrs {:vehicle/license-plate license-plate}}))}]]
      [mobile/inline-prompt :alert-circle [:span.text-red-700 (tr "No info on which license plate to mount on this car!")]])]])


(defn pdi-carpets-view [fahrzeug {:keys [service] :as args}]
  [:div
   [mobile/scroll-content
    [mobile/navbar {:title (:service/code service)
                    :subtitle (tr "Services")
                    :right-item [workflow-menu-dropdown]
                    :back [service-view-backlink fahrzeug args]}]
    [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
    [mobile/info-bar fahrzeug]
    (if-let [carpets (:service/carpets service)]
      (with-hardware-scanner
        {:view `pdi-carpets-view
         :classify-scan (fn [results] {:scan/carpets results})
         :on-scan (fn [scan-data]
                    (when-let [scanned-code (get-in scan-data [:scan/result])]
                      (deframe/route-assoc-in! [::state :scanned-carpet-codes scanned-code] (contains? (into #{} (map :code) carpets) scanned-code))
                      (deframe/reset-current-scan!)))}
        (let [scanned-codes @(deframe/!route-get-in [::state :scanned-carpet-codes])
              all-carpets-ok? (every? (fn [{:as carpet :keys [code type]}] (get scanned-codes code)) carpets)]
          [:<>
           [mobile/inline-prompt :scan [:div
                                        (tr "Put these carpets in the car")
                                        [:div.text-xs (tr "Scan code on carpet to verify")]
                                        (when-not ui/is-mobile? [:div.mt-1 simulate-scan-hint])]]
           (into [:<>]
                 (map-indexed
                  (fn [i {:as carpet :keys [code type]}]
                    (let [carpet-ok? (get scanned-codes code)]
                      [mobile/table {:inset? true
                                     :items [[mobile/list-item {:icon (if scanned-codes
                                                                        (if carpet-ok? mobile/icon-task-complete mobile/icon-task-fail)
                                                                        mobile/icon-scan)
                                                                :title [:span {:data-testid (str "carpet-code-" i)}
                                                                        (str code (when-not (str/blank? type) (str " (" type ")")))]
                                                                :description
                                                                [:span {:data-testid (str "carpet-code-status-" i)
                                                                        :class (when scanned-codes
                                                                                 (if carpet-ok? "text-green-600" "text-red-600"))}
                                                                 (if scanned-codes
                                                                   (if carpet-ok? (tr "Carpet OK") (tr "Carpet code doesn't match"))
                                                                   (tr "Scan code on carpet to verify"))]}]]}]))
                  carpets))

           [toolbar {:label [:span.flex.items-center.justify-center.text-white
                             [ui/icon {:size 20} :check-square]
                             [:span.ml-2 (tr "Complete")]]
                     :disabled (or (completed? service)
                                   (not all-carpets-ok?))
                     :loading @deframe/!loading?
                     :on-click (fn [_] (deframe/call-lambda
                                        'ductile.workflow/perform-service
                                        {:ductile/id (:ductile/id fahrzeug)
                                         :service/result {:pdi/carpets carpets}
                                         :service/code (:service/code service)
                                         :service/attrs {:vehicle/equipment (set (map (fn [c] (str/join " " (remove str/blank? ["Tappetino" (:type c) (:code c)]))) carpets))}}))}]]))
      [mobile/inline-prompt :alert-circle [:span.text-red-700 (tr "No carpet data found for this car!")]])]])


(defn train-loading-view [fahrzeug {:keys [service] :as args}]
  [:div
   [mobile/scroll-content
    [mobile/navbar {:title (:service/code service)
                    :subtitle (tr "Services")
                    :right-item [workflow-menu-dropdown]
                    :back [service-view-backlink fahrzeug args]}]
    [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
    [mobile/info-bar fahrzeug]
    (with-hardware-scanner
      {:view `train-loading-view
       :on-scan (fn [scan-data]
                  (when-let [scanned-wagon (get-in scan-data [:scan/result])]
                    (deframe/route-assoc-in! [::state :scanned-wagon] scanned-wagon)
                    (deframe/reset-current-scan!)))}
      (let [scanned-wagon @(deframe/!route-get-in [::state :scanned-wagon])]
        [:<>
         [mobile/inline-prompt :scan [:div
                                      (tr "Scan the wagon to confirm train loading")
                                      (when-not ui/is-mobile? [:div.mt-1 simulate-scan-hint])]]
         [mobile/table {:inset? true
                        :items [[mobile/list-item {:icon (if scanned-wagon
                                                           mobile/icon-task-complete
                                                           mobile/icon-scan)
                                                   :title [:span {:data-testid "wagon"} scanned-wagon]}]]}]


         [toolbar {:label [:span.flex.items-center.justify-center.text-white
                           [ui/icon {:size 20} :check-square]
                           [:span.ml-2 (tr "Complete")]]
                   :disabled (or (completed? service)
                                 (not scanned-wagon))
                   :loading @deframe/!loading?
                   :on-click (fn [_] (deframe/call-lambda
                                      'ductile.workflow/perform-service
                                      {:ductile/id (:ductile/id fahrzeug)
                                       ;; TODO match again known lists of wagon / containers
                                       :service/result {:transport/container scanned-wagon}
                                       :service/code (:service/code service)}))}]]))]])


(defn default-view [fahrzeug {:keys [service] :as args}]
  [:div
   [mobile/scroll-content
    [mobile/navbar {:title (:service/code service)
                    :subtitle (tr "Services")
                    :back [service-view-backlink fahrzeug args]}]
    [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
    [mobile/info-bar fahrzeug]
    [mobile/inline-prompt :info (tr "Click on 'Complete' to mark the service as completed.")]

    [toolbar {:label [:span.flex.items-center.justify-center.text-white
                      [ui/icon {:size 20} :check-square]
                      [:span.ml-2 (tr "Complete")]]
              :disabled (completed? service)
              :loading @deframe/!loading?
              :on-click (fn [_] (deframe/call-lambda 'ductile.workflow/perform-service {:ductile/id (:ductile/id fahrzeug)
                                                                                        :service/code (:service/code service)}))}]]])

(defn complete-label [text]
  [:span.text-gray-400 {:label "service-completed"} text])

(defn fahrzeug-not-found-view [{:keys [vin compound-name ars/id]}]
  [:div
   [mobile/scroll-content
    [mobile/navbar {:title (tr "Vehicle Scan")}]
    [mobile/inline-prompt :alert-circle (tr "No vehicle found")]
    [mobile/inline-prompt
     (str/join " / "
               (filter identity [(when compound-name compound-name)
                                 (when id (str "ARS-ID: " id))
                                 (when vin  (str "vin: " vin))]))]
    [mobile/toolbar
     [mobile/button-full {:on-click #(router/navigate-to (router/path '[compound-ops :compound-name] {:compound-name (util/current-compound-name)}))}
      (tr "Back to start")]]]])

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn record-transport-info-view [fahrzeug {:as opts :keys [service]}]
  (r/with-let [!info (r/atom @(deframe/!get :entry/transport-info))]
    [:div
     [mobile/scroll-content
      [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
      [mobile/navbar {:title (tr "Transport info")
                      :back [compound-ops-backlink fahrzeug]}]
      [mobile/table {:title (tr "Transport name")
                     :items [[mobile/list-item-input {:input {:on-change #(swap! !info assoc :transport/name (j/get-in % [:target :value]))
                                                              :label "transport-name"
                                                              :data-testid "transport-name-input"
                                                              :value (:transport/name @!info)}}]]}]
      [toolbar {:loading @deframe/!loading?
                :on-click (fn [_] (deframe/call-lambda {:on-result (fn [result]
                                                                     (deframe/assoc! :entry/transport-info (assoc @!info :db/id :entry/transport-info)))}
                                                       'ductile.workflow/perform-service {:service/code (:service/code service)
                                                                                          :ductile/id (:ductile/id fahrzeug)
                                                                                          :service/attrs {:transport/name (:transport/name @!info)}}))}]]]))

(defn record-vehicle-production-datetime-view [fahrzeug {:as opts :keys [service]}]
  (r/with-let [!selected-date (r/atom (.toISODate (.fromJSDate DateTime (:fahrzeug/storage-care-start-datetime fahrzeug))))]

    (let [color-component (fn [[display-color color-name]]
                            (let [date (.toISODate (color+today->production-datetime color-name))]
                              [mobile/list-item-radio {:label [:div.flex.items-center.text-xs
                                                               [mobile/icon-color display-color]
                                                               [:span.m-2.text-sm (translate-color-names color-name)]
                                                               [:span.ml-4 (.toLocaleString (color+today->production-datetime color-name)
                                                                                            (clj->js {:month "numeric"
                                                                                                      :day "numeric"
                                                                                                      :year "numeric"}))]]
                                                       :input {:value color-name
                                                               :checked (= @!selected-date date)
                                                               :on-change #(reset! !selected-date date)}}]))]

      [:div
       [mobile/scroll-content
        [mobile/navbar {:title "Startdatum Lagerpflege ermitteln"
                        :subtitle (tr "Services")
                        :back [service-view-backlink fahrzeug opts]}]
        [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
        [mobile/info-bar fahrzeug]
        [mobile/table {:title "Produktionsdatum"
                       :subtitle "Datum eingeben oder Stickerfarbe auswählen"
                       :items (into [[mobile/list-item-input {:input {:value @!selected-date
                                                                      :on-change #(reset! !selected-date (.. % -target -value))
                                                                      :type "date"}}]]
                                    (mapv color-component
                                          {"#0A9900" "green"
                                           "#20FFFF" "blue"
                                           "#9B3B08" "brown"
                                           "#FF9933" "orange"
                                           "#FE0500" "red"
                                           "#A6A6A6" "gray"}))}]
        [toolbar {:disabled (str/blank? @!selected-date)
                  :on-click (fn [_] (deframe/call-lambda 'ductile.workflow/perform-service {:service/code (:service/code service)
                                                                                            :ductile/id (:ductile/id fahrzeug)
                                                                                            :service/attrs {:fahrzeug/storage-care-start-datetime (js/Date. @!selected-date)}}))}]]])))

(defn parking-select [{:keys [label on-change options value] :or {on-change #()}}]
  [:div.rounded-lg.border-2.border-slate-400.relative
   {:class "pb-[15px] px-[20px] bg-white hover:bg-slate-100 cursor-pointer shadow-inner min-w-[100px]"}
   [:div.flex.items-center.justify-center {:label (str "parking-select-" label "-value") :class "h-[40px]"} value]
   (into
    [:select.opacity-0.absolute.left-0.top-0.w-full.bottom-0.cursor-pointer
     {:value value
      :data-testid (str "select-" (str/lower-case label))
      :label (str "parking-select-" label)
      :on-change #(on-change (.. % -target -value))}]
    (map (fn [option] [:option {:value option} option]))
    options)
   [:div.absolute.bg-slate-300.bottom-0.left-0.right-0.leading-none.uppercase.border-t.border-slate-400.rounded-b-md.flex.w-full.justify-center.items-center
    {:class "text-[9px] h-[15px]"}
    label]])

(defn parking-input [{:as opts :keys [label on-change]}]
  [:div.rounded-lg.border-2.border-slate-400.relative
   {:class "pb-[15px] bg-white hover:bg-slate-100 cursor-pointer shadow-inner"}
   [:input.flex.items-center.appearance-none.bg-transparent.text-center
    (merge {:label "parking-input-value"
            :data-testid (str "select-" (str/lower-case label))
            :style {:border-radius 0 :border 0 :outline 0}
            :class "px-[20px] h-[40px] w-[80px]"
            :on-input #(on-change (.. % -target -value))
            :type "text"}
           (dissoc opts :label :on-change))]
   [:div.absolute.bg-slate-300.bottom-0.left-0.right-0.leading-none.uppercase.border-t.border-slate-400.rounded-b-md.flex.w-full.justify-center.items-center
    {:class "text-[9px] h-[15px]"}
    label]])

(defn vehicle-details-table [{:vehicle/keys [length-mm width-mm height-mm vin]}]
  [:div.flex.items-center.bg-slate-50
   [:div.pr-4.w-full.relative.flex.flex-col.justify-center
    {:class (str "min-h-[30px] " mobile/hairline-b)}
    [:div.flex.gap-4.items-center.justify-center
     {:class "text-[17px]"}
     [:div.text-xs vin]
     (when length-mm
       [:div.text-xs
        [:span.font-bold (tr "L") ": "] length-mm "mm"])
     (when width-mm
       [:div.text-xs
        [:span.font-bold (tr "W") ": "] width-mm "mm"])
     (when height-mm
       [:div.text-xs
        [:span.font-bold (tr "H") ": "] height-mm "mm"])]]])

(defn confirm-parking-spot! [{:as fahrzeug :keys [on-result]}]
  (deframe/call-lambda
   (if on-result {:on-result on-result} {})
   'ductile.workflow/perform-service
   (merge (select-keys fahrzeug [:compound/name :vehicle/vin])
          {:service/attrs {:fahrzeug/parking-spot @!selected-parking-spot}}
          (when-let [selected-service (or @!selected-service
                                          (::service fahrzeug))]
            (select-keys selected-service [:service/code])))))

(defn parking-mode-confirm-spot! [service-id {:as fahrzeug :keys [on-result]}]
  ;; TODO: unify with `confirm-parking-spot` above
  (-> (http/$lambda 'ductile.workflow/perform-service
                    (merge (select-keys fahrzeug [:compound/name :vehicle/vin])
                           {:service/attrs {:fahrzeug/parking-spot @!selected-parking-spot}
                            :service/template {:service/id service-id}}))
      (.then (fn [fahrzeug-or-error]
               (if (:error fahrzeug-or-error)
                 (deframe/route-assoc! ::notice {:message (:error-message fahrzeug-or-error)})
                 (do
                   (deframe/assoc-in! (parking-state-path :selected-parking-spot :result) (-> (->parking-keys fahrzeug-or-error)
                                                                                              (update :parking-spot/place inc)))
                   (deframe/route-assoc! ::notice {:message (str (:vehicle/vin fahrzeug-or-error) " " (tr "Parked at") " " (:parking-spot fahrzeug-or-error) ".")
                                                   :type :success})
                   (deframe/route-dissoc! :fetch)
                   (when on-result
                     (on-result fahrzeug-or-error))))))))

(defn parking-spot-selectors [fahrzeug {:as spot :parking-spot/keys [zone row place]}]
  (let [assignment (parking-assignment (merge fahrzeug spot))]
    [:div.flex.items-center.gap-2.font-bold.justify-center.text-lg
     [:div.text-sm
      [parking-select {:label (tr "Zone")
                       :options (->> @!parking-layout
                                     (remove (comp :disallow-manual-parking? val))
                                     keys
                                     sort
                                     (into [""]))
                       :on-change select-parking-zone!
                       :value (str zone)}]]
     (when-not (contains? #{:parking.assignment/zone} assignment)
       (let [opts {:value (str row)
                   :on-change #(select-parking-row! (parse-long %))
                   :label (if (= assignment :parking.assignment/line)
                            (tr "Line")
                            (tr "Row"))}]
         (if (= :parking.assignment/free-spot assignment)
           [parking-input opts]
           [parking-select (assoc opts :options (into [""] @(!parking-row-options zone)))])))
     (when-not (contains? #{:parking.assignment/line
                            :parking.assignment/zone}
                          assignment)
       (let [opts {:label (if (= assignment :parking.assignment/box)
                            (tr "Box")
                            (tr "Place"))
                   :on-change #(select-parking-place! (parse-long %))
                   :options @(!parking-place-options zone row)
                   :value (str place)}]
         (if (contains? #{:parking.assignment/free-spot :parking.assignment/box} assignment)
           [parking-input opts]
           [parking-select (assoc opts :options (into [""] @(!parking-place-options zone row)))])))]))

(defn spot-invalid? [fahrzeug {:as spot :parking-spot/keys [zone row place]}]
  (let [assignment (parking-assignment (merge fahrzeug spot))]
    (or @(deframe/!get-in (parking-state-path :rows->spots->n-occupied zone :loading))
        @(deframe/!get-in (parking-state-path :selected-parking-spot :loading))
        (not @(deframe/!get-in (parking-state-path :selected-parking-spot :result)))
        (case assignment
          :parking.assignment/zone (not zone)
          :parking.assignment/line (not row)
          (not (and zone row place)))
        (= (->parking-keys fahrzeug)
           @(deframe/!get-in (parking-state-path :selected-parking-spot :result))))))

(defn parking-spot-select [fahrzeug {:as opts :keys [service on-result]}]
  [:div.my-3
   (let [{:as spot :parking-spot/keys [zone row place]} @!selected-parking-spot
         assignment (parking-assignment (merge fahrzeug spot))]
     (when (and zone
                (not= :parking.assignment/free-spot assignment)
                (not @(!rows->spots->n-occupied zone)))
       (fetch-rows->spots->n-occupied! zone))
     [:div
      [:div.font-bold.text-sm.py-4.text-center.px-4 (tr "Select parking spot")]
      [parking-spot-selectors fahrzeug spot]
      (when (= :parking.assignment/predefined-spot assignment)
        [:div.flex.items-center.justify-center.mt-3.gap-2
         [:button.bg-white.text-indigo-700.font-bold.text-xs.px-3.py-1.border-2.border-indigo-700.rounded-lg.shadow.disabled:opacity-50
          {:type :button
           :on-click #(fetch-suggested-parking-spot! opts)}
          (tr "New free spot")]])
      [toolbar {:disabled (spot-invalid? fahrzeug spot)
                :loading @deframe/!loading?
                :on-click #((:on-complete opts confirm-parking-spot!) (assoc fahrzeug
                                                                             ::service service
                                                                             :on-result on-result))}]])])

(defn parking-view [fahrzeug {:as args :keys [service on-result in-modal?]}]
  (r/with-let [_ (when-not @(deframe/!get-in (parking-state-path :selected-parking-spot))
                   (fetch-suggested-parking-spot! service)
                   (maybe-set-selected-parking-spot! service))]
    [mobile/scroll-content
     [mobile/navbar (merge {:title (:service/code service)
                            :subtitle (tr "Services")
                            :right-item [workflow-menu-dropdown]}
                           (when-not in-modal?
                             {:back [service-view-backlink fahrzeug args]}))]
     [mobile/info-bar fahrzeug]
     (when-let [fetch-error @(deframe/!route-get-in [:fetch :error])]
       [ui/expected-error-view fetch-error])
     (when-let [parking-error @(deframe/!get-in (parking-state-path :selected-parking-spot :error))]
       [ui/expected-error-view parking-error])
     [mobile/inline-prompt :car [:div.flex.flex-col
                                 [:span (tr "Bring the car to the parking spot")]]]
     [parking-spot-select fahrzeug args]]))

(defn unannounced-fahrzeug-parking-view [fahrzeug opts]
  [parking-view (assoc fahrzeug :parking-spot/zone "GZ") opts])

(defn entry-truck-additional-info-view [fahrzeug {:as opts :keys [service]}]
  (r/with-let [saved-truck-info (localstorage/get-item "workflow/entry-details")
               !form-data (r/atom (if (some? saved-truck-info)
                                    (merge {:ars-truck? false
                                            :entry/mode :mode/truck
                                            :vehicle/new? true
                                            :use-saved-data? true}
                                           saved-truck-info)
                                    {:ars-truck? false
                                     :entry/mode :mode/truck
                                     :vehicle/new? true
                                     :use-saved-data? false}))
               !details-expanded? (r/atom (not (:vehicle/new? @!form-data)))]
    (if @!selected-parking-spot
      (fetch-rows->spots->n-occupied! (:parking-spot/zone @!selected-parking-spot))
      (maybe-set-selected-parking-spot! service))
    (let [{:keys [use-saved-data? entry/mode vehicle/new?]} @!form-data]
      [mobile/scroll-content
       [mobile/navbar {:title "Eingang"
                       :back [compound-ops-backlink fahrzeug]}]
       [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
       (when saved-truck-info
         [mobile/table
          {:inset? true
           :items [[mobile/list-item-toggle {:label [:div [:div "Letzte Eingaben verwenden"]
                                                     [:span.text-sm.opacity-50
                                                      [:span (if (= :mode/own-wheels (get saved-truck-info :entry/mode))
                                                               (str (tr "Own wheels") " / "
                                                                    (if (:vehicle/new? saved-truck-info) (tr "New") (tr "Used")))

                                                               (->> (if (:ars-truck? saved-truck-info)
                                                                      [(:transport/number saved-truck-info)]
                                                                      [(:transport/license-plate saved-truck-info)
                                                                       (:transport/name saved-truck-info)])
                                                                    (remove str/blank?)
                                                                    (str/join " / ")))]]]
                                             :input {:checked use-saved-data?
                                                     :on-change #(do (swap! !form-data (fn [{:keys [use-saved-data?] :as m}]
                                                                                         (if use-saved-data?
                                                                                           {:use-saved-data? false}
                                                                                           (assoc saved-truck-info :use-saved-data? true))))
                                                                     (when use-saved-data?
                                                                       (reset! !details-expanded? (not (:vehicle/new? saved-truck-info)))))}}]]}])
       [mobile/table {:inset? true
                      :title "Anlieferungsart"
                      :items [[mobile/list-item-radio
                               {:label (tr "Truck")
                                :input {:name "entry/mode"
                                        :checked (= :mode/truck mode)
                                        :data-testid "truck"
                                        :on-change #(swap! !form-data (fn [{:as form-data current-mode :entry/mode}]
                                                                        (merge form-data (when (.. % -target -checked)
                                                                                           {:entry/mode :mode/truck}))))}}]
                              [mobile/list-item-radio
                               {:label (tr "Own wheels")
                                :input {:name "entry/mode"
                                        :checked (= :mode/own-wheels mode)
                                        :data-testid "own-wheels"
                                        :on-change #(swap! !form-data (fn [{:as form-data current-mode :entry/mode}]
                                                                        (merge form-data (when (.. % -target -checked)
                                                                                           {:entry/mode :mode/own-wheels :vehicle/new? false}))))}}]]}]
       (when (= :mode/truck mode)
         [mobile/table {:inset? true
                        :title "LKW Angaben"
                        :items (into [[mobile/list-item-toggle {:label "ARS Altmann LKW?"
                                                                :input {:checked (boolean (:ars-truck? @!form-data))
                                                                        :on-change (fn [_] (if (:ars-truck? @!form-data)
                                                                                             (swap! !form-data
                                                                                                    #(-> %
                                                                                                         (assoc :ars-truck? false)
                                                                                                         (dissoc :transport/number)))
                                                                                             (swap! !form-data
                                                                                                    #(-> %
                                                                                                         (assoc :ars-truck? true)
                                                                                                         (dissoc :transport/license-plate :transport/name)))))}}]]
                                     (if (:ars-truck? @!form-data)
                                       [[mobile/list-item-input {:label (tr "Truck number")
                                                                 :input {:placeholder "z.B. ES834"
                                                                         :value (or (:transport/number @!form-data) "")
                                                                         :on-input #(swap! !form-data assoc :transport/number (j/get-in % [:target :value]))}}]]
                                       [[mobile/list-item-input {:label (tr "License plate")
                                                                 :input {:placeholder "z.B. M-UC4242"
                                                                         :data-testid "license-plate"
                                                                         :value (or (:transport/license-plate @!form-data) "")
                                                                         :on-input #(swap! !form-data assoc :transport/license-plate (j/get-in % [:target :value]))}}]
                                        [mobile/list-item-input {:label (tr "Spediteur")
                                                                 :input {:placeholder "z.B. Max Mustermann"
                                                                         :data-testid "transport-name"
                                                                         :value (or (:transport/name @!form-data) "")
                                                                         :on-input #(swap! !form-data assoc :transport/name (j/get-in % [:target :value]))}}]]))}])
       ;; TODO PLM: when set, use `(select-keys saved-truck-info [:parking-spot/zone :parking-spot/row])`
       [mobile/table (merge {:inset? true
                             :title (tr "Parking spot")
                             :items [[mobile/list-item
                                      {:title [parking-spot-selectors fahrzeug @!selected-parking-spot]}]]})]
       (when (not= false (:service/record-old-or-new? service))
         [:div {:class (when (= :mode/truck mode) "-mt-4")}
          [mobile/table (merge {:inset? true
                                :items [[mobile/list-item-radio
                                         {:label (tr "New vehicle")
                                          :input {:checked new?
                                                  :data-testid "new-or-used-new"
                                                  :on-change #(do (swap! !form-data update :vehicle/new? (constantly true))
                                                                  (reset! !details-expanded? false))}}]
                                        [mobile/list-item-radio
                                         {:label (tr "Used vehicle")
                                          :input {:checked (not new?)
                                                  :data-testid "new-or-used-used"
                                                  :on-change #(do (swap! !form-data update :vehicle/new? (constantly false))
                                                                  (reset! !details-expanded? true))}}]]}
                               (when-not (= :mode/truck mode)
                                 {:title "Fahrzeugdetails"}))]])
       [:div {:class "-mt-4"}
        [mobile/table {:inset? true
                       :items (cond-> [[mobile/list-item {:inset? true
                                                          :on-click #(swap! !details-expanded? not)
                                                          :class "cursor-pointer"
                                                          :title (str (tr "Mileage") " & " (tr "License plate"))
                                                          :right [ui/icon {:size 24
                                                                           :class (str "transition "
                                                                                       (if @!details-expanded?
                                                                                         "rotate-90"
                                                                                         "rotate-0"))} :chevron-right]}]]
                                @!details-expanded? (concat [[mobile/list-item-input {:inset? true
                                                                                      :label (tr "Mileage")
                                                                                      :input {:placeholder (tr "Mileage")
                                                                                              :data-testid "mileage"
                                                                                              :type "number"
                                                                                              :input-mode "numeric"
                                                                                              :value (or (:vehicle/mileage @!form-data) "")
                                                                                              :min 0
                                                                                              :on-input (fn [e] (swap! !form-data assoc :vehicle/mileage (int (j/get-in e [:target :value]))))}}]
                                                             [mobile/list-item-input {:label (tr "License plate")
                                                                                      :input {:on-change #(swap! !form-data assoc :vehicle/license-plate (j/get-in % [:target :value]))
                                                                                              :data-testid "vehicle-license-plate"
                                                                                              :placeholder (tr "License plate")
                                                                                              :value (or (:vehicle/license-plate @!form-data) "")}}]]))}]]
       [:div
        [toolbar {:disabled (let [{:keys [ars-truck? transport/number]} @!form-data]
                              (or (spot-invalid? fahrzeug @!selected-parking-spot)
                                  (and (= (:entry/mode @!form-data) :mode/truck)
                                       ars-truck?
                                       (str/blank? number))))
                  :loading @deframe/!loading?
                  :on-click #(if (= :mode/truck (:entry/mode @!form-data))
                               (do (localstorage/set-item! "workflow/entry-details"
                                                           (merge (dissoc @!selected-parking-spot :parking-spot/place)
                                                                  (select-keys @!form-data
                                                                               [:ars-truck?
                                                                                :entry/mode
                                                                                :transport/number
                                                                                :transport/license-plate
                                                                                :transport/name])))
                                   (deframe/call-lambda
                                    'ductile.workflow/perform-service
                                    {:ductile/id (:ductile/id fahrzeug)
                                     :service/code (:service/code service)
                                     :service/attrs (-> (select-keys @!form-data
                                                                     [:ars-truck?
                                                                      :entry/mode
                                                                      :vehicle/new?
                                                                      :vehicle/mileage
                                                                      :vehicle/license-plate
                                                                      :transport/number
                                                                      :transport/license-plate
                                                                      :transport/name])
                                                        (assoc :fahrzeug/parking-spot @!selected-parking-spot))}))
                               ;; Own-wheels
                               (do (localstorage/set-item! "workflow/entry-details" (select-keys @!form-data [:entry/mode]))
                                   (deframe/call-lambda
                                    'ductile.workflow/perform-service
                                    {:ductile/id (:ductile/id fahrzeug)
                                     :service/code (:service/code service)
                                     :service/attrs (-> @!form-data
                                                        (select-keys [:vehicle/new?
                                                                      :vehicle/mileage
                                                                      :vehicle/license-plate
                                                                      :entry/mode])
                                                        util/dissoc-blank-vals
                                                        (assoc :fahrzeug/parking-spot @!selected-parking-spot))})))}]]])))


(defn photo-mode [{:keys [entity notice !state]}]
  (let [cancel-photo-capture #(swap! !state assoc :taking-photo? false)]
    [:div.fixed.left-0.top-0.w-screen.h-screen {:style {:z-index 999}}
     [scanner/photo-view
      {:width js/innerWidth
       :height js/innerHeight
       :entity entity
       :on-success (fn [_state body]
                     ;; add the image path
                     (swap! !state
                            update :photos
                            (fnil #(conj % (select-keys body [:ductile/id :cas/multihash :url]))
                                  []))
                     (cancel-photo-capture))
       :on-error (fn [_state error]
                   (cancel-photo-capture)
                   (reset! notice error))}]
     [:div.fixed.right-2.top-2.text-white.z-10.cursor-pointer
      {:on-click cancel-photo-capture}
      [ui/icon {:size 30} :x]]]))

(defn damage-photos [{:keys [entity notice !state]}]
  (let [{:keys [photos service]} @!state]
    [:<>
     [:div.mx-4.mr-0.pr-4.py-3.border-b
      [mobile/add-image-button {:on-click #(swap! !state assoc :taking-photo? true)
                                :label (tr "Add photo")}]]
     (when (seq photos)
       [:div.px-4.py-3.border-b
        (into [:div.flex.flex-wrap.gap-3]
              (map (fn [{:ductile/keys [id] :keys [url] :cas/keys [multihash]}]
                     [mobile/image-tile
                      {:url url
                       :remove-fn #(p/let [result (http/$lambda
                                                   'ductile.damages/handle-remove-photo
                                                   {:entity-id (:ductile/id entity)
                                                    :cas-id id})]
                                     (if (:error result)
                                       (reset! notice result)
                                       (swap! !state
                                              update :photos
                                              (partial remove (fn [cas] (= multihash (:cas/multihash cas)))))))}]))
              photos)])]))


;; TODO use it also for Ravenna when we have damages in a stable way
(defn record-damages-with-photos-view [fahrzeug {:keys [service] :as args}]
  (r/with-let [!notice (r/atom nil)
               !state (r/atom {:taking-photo? false})]
    (let [{:keys [damaged? taking-photo?] :as state} @!state]
      (if taking-photo?
        [photo-mode {:entity fahrzeug
                     :notice !notice
                     :!state !state}]
        [:div
         [mobile/scroll-content
          [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
          [mobile/navbar {:title (tr "Damage check")
                          :back [service-view-backlink fahrzeug args]}]
          [mobile/table {:title (tr "Is the vehicle damaged?")
                         :items (mapv (fn [{:keys [label value]}]
                                        [mobile/list-item-radio {:label label
                                                                 :input {:checked (= damaged? value)
                                                                         :on-change #(swap! !state assoc :damaged? value)}}])
                                      [{:label (tr "Yes") :value true}
                                       {:label (tr "No") :value false}])}]
          (when damaged?
            [mobile/table {:title (tr "Damage photos")
                           :items [[damage-photos {:entity fahrzeug
                                                   :notice !notice
                                                   :!state !state}]]}])
          [toolbar {:disabled (nil? damaged?)
                    :loading @deframe/!loading?
                    :hide-camera? true
                    :on-click (fn [] (deframe/call-lambda
                                      'ductile.workflow/perform-service
                                      {:ductile/id (:ductile/id fahrzeug)
                                       :service/code (:service/code service)
                                       :service/result {:damaged? damaged?}
                                       :service/attrs (cond-> {:ductile/comments (map (fn [p] [:ductile/id (:ductile/id p)])
                                                                                      (:photos state))}
                                                        damaged?
                                                        (assoc :fahrzeug/damages "performed-service"))}))}]]]))))

(defn record-damages-view [fahrzeug {:keys [service] :as args}]
  (r/with-let [!state (r/atom {:taking-photo? false
                               :service service})
               !notice (r/atom nil)]
    (let [{:keys [damaged? taking-photo? service]} @!state]
      (if taking-photo?
        [photo-mode {:entity-id (:ductile/id service)
                     :notice !notice
                     :!state !state}]
        [:div
         [mobile/scroll-content
          [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
          [mobile/navbar {:title (tr "Damage check")
                          :back [mobile/back-link {:route (router/path '[compound-ops :compound-name] {:compound-name (:compound/name fahrzeug)})}]}]
          [mobile/table {:title (tr "Is the vehicle damaged or are there missing items?")
                         :items (mapv (fn [{:keys [accessory-view label value]}]
                                        [mobile/list-item-radio {:label label
                                                                 :input {:checked (= damaged? value)
                                                                         :on-change #(swap! !state assoc :damaged? value)}}])
                                      [{:label (tr "Yes") :value true}
                                       {:label (tr "No") :value false}])}]
          ;; NOTE: uncomment to show damage photo UI
          #_(when (and service damaged?)
              [mobile/table {:title (tr "Damage photos")
                             :items [[damage-photos {:entity-id (:ductile/id service)
                                                     :notice !notice
                                                     :!state !state}]]}])
          [toolbar {:disabled (nil? damaged?)
                    :loading @deframe/!loading?
                    :on-click #(deframe/call-lambda
                                'ductile.workflow/perform-service
                                {:ductile/id (:ductile/id fahrzeug)
                                 :service/result {:damaged? damaged?}
                                 :service/code (:service/code service)
                                 :service/attrs (if damaged?
                                                  {:fahrzeug/damages "performed-service"}
                                                  {})})}]]]))))

(defn confirm-ship-loading-view [{:exit/keys [transport] :as fahrzeug}
                                 {:as args :keys [service]}]
  (r/with-let [!notice (r/atom nil)]
    (let [loading-line (:transport/loading-line transport)]
      [:div
       [mobile/scroll-content
        [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
        [mobile/navbar {:title (:service/code service)
                        :back [compound-ops-backlink fahrzeug]}]
        [mobile/inline-prompt :car (tr "Please confirm the vehicle has been loaded into the ship")]
        [mobile/table {:title (tr "Vehicle")
                       :items [[mobile/list-item {:title (str (tr "VIN") ": " (:vehicle/vin fahrzeug))}]
                               (when (:vehicle/model fahrzeug)
                                 [mobile/list-item {:title (str (tr "Model") ": " (:vehicle/model fahrzeug))}])]}]
        [mobile/table {:title (tr "Transport")
                       :items [[mobile/list-item {:title (str (tr "Transport name") ": " (:transport/name transport))}]]}]
        [toolbar {:label (tr "Confirm Loading into ship")
                  :loading @deframe/!loading?
                  :on-click #(do
                               (-> (deframe/call-lambda 'ductile.workflow/perform-service {:service/code (:service/code service)
                                                                                           :ductile/id (:ductile/id fahrzeug)
                                                                                           :service/attrs {:fahrzeug/parking-spot (update loading-line :parking-spot/zone :parking-zone/name)}})
                                   (.then (fn [result]
                                            (if (:error result)
                                              (deframe/route-assoc-in! [:fetch :result :error] result)
                                              (router/navigate-to (router/path '[compound-ops :compound-name workflow exit ship :transport-id]
                                                                               {:compound-name (:compound/name fahrzeug)
                                                                                :transport-id (:ductile/id transport)})))))))}]]])))

(defn confirm-port-entry-view [{:entry/keys [transport] :as fahrzeug} {:as args :keys [service]}]
  (r/with-let [!notice (r/atom nil)]
    [:div
     [mobile/scroll-content
      [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
      [mobile/navbar {:title (:service/code service)
                      :back [compound-ops-backlink fahrzeug]}]
      [mobile/inline-prompt :car (tr "Please confirm the vehicle has entered the port")]
      [mobile/table {:title (tr "Vehicle")
                     :items [[mobile/list-item {:title (str (tr "VIN") ": " (:vehicle/vin fahrzeug))}]
                             (when (:vehicle/model fahrzeug) [mobile/list-item {:title (str (tr "Model") ": " (:vehicle/model fahrzeug))}])]}]
      [toolbar {:label (tr "Confirm Port Entry")
                :loading @deframe/!loading?
                :on-click (fn [_] (deframe/call-lambda
                                   {:on-result (fn [result]
                                                 (if (:error result)
                                                   (deframe/route-assoc-in! [:fetch :result :error] result)
                                                   (router/navigate-to (router/path '[compound-ops :compound-name workflow entry ship :transport-id]
                                                                                    {:compound-name (:compound/name fahrzeug)
                                                                                     :transport-id (:ductile/id transport)}))))}
                                   'ductile.workflow/perform-service
                                   {:service/code (:service/code service)
                                    :ductile/id (:ductile/id fahrzeug)
                                    :service/attrs {:fahrzeug/parking-spot {:parking-spot/zone "FPR"}}}))}]]]))

(defn confirm-train-unloading-view [fahrzeug {:as args :keys [service]}]
  (r/with-let [!notice (r/atom nil)]
    [:div
     [mobile/scroll-content
      [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
      [mobile/navbar {:title (entry-mode-label fahrzeug)
                      :back [compound-ops-backlink fahrzeug]}]
      [mobile/inline-prompt :car (tr "Please confirm the vehicle has been unloaded from the train.")]
      [mobile/table {:title (tr "Vehicle")
                     :items [[mobile/list-item {:title (str (tr "VIN") ": " (:vehicle/vin fahrzeug))}]
                             (when (:vehicle/model fahrzeug) [mobile/list-item {:title (str (tr "Model") ": " (:vehicle/model fahrzeug))}])]}]
      [toolbar {:label (tr "Confirm Train Unloading")
                :loading @deframe/!loading?
                :on-click (fn [_] (deframe/call-lambda
                                   'ductile.workflow/perform-service
                                   {:ductile/id (:ductile/id fahrzeug)
                                    :service/code (:service/code service)}))}]]]))

(defn confirm-entry-view [fahrzeug {:keys [service]}]
  (r/with-let [!notice (r/atom nil)]
    [:div
     [mobile/scroll-content
      [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
      [mobile/navbar {:title (entry-mode-label fahrzeug)
                      :back [compound-ops-backlink fahrzeug]}]
      [mobile/inline-prompt :car (tr "Please Confirm the entry of the vehicle")]
      [mobile/table {:title (tr "Vehicle")
                     :items [[mobile/list-item {:title (str (tr "VIN") ": " (:vehicle/vin fahrzeug))}]
                             (when (:vehicle/model fahrzeug) [mobile/list-item {:title (str (tr "Model") ": " (:vehicle/model fahrzeug))}])]}]
      [toolbar {:label (tr "Confirm Entry")
                :loading @deframe/!loading?
                :on-click (fn [_] (deframe/call-lambda 'ductile.workflow/perform-service {:ductile/id (:ductile/id fahrzeug)
                                                                                          :service/code (:service/code service)}))}]]]))

(defn warn-mode-required-view [_]
  [:div
   [mobile/scroll-content
    [mobile/navbar {:title (tr "Unknown entry mode")
                    :back [mobile/back-link {:route (router/path '[compound-ops :compound-name] {:compound-name (util/current-compound-name)})}]}]
    [mobile/inline-prompt (tr "To perform entry for this vehicle, first select an entry mode")]
    [toolbar {:label (tr "Back")
              :on-click #(router/navigate-to (router/path '[compound-ops :compound-name] {:compound-name (util/current-compound-name)}))}]]])

(defn scan-contains-vin? [{:as scan :scan/keys [classified]}]
  (and (contains? scan :scan/result)
       (contains? classified :vehicle/vin)))

(declare !vin-edit)
(declare vin-chars-input)
(declare vin-complete?)

(defn vin-scan-list-items []
  (let [{:as scan :scan/keys [ocr-details result]} @(deframe/!route-get-in [::state :scan])
        manual-vin-entry? @(deframe/!route-get ::manual-vin-entry?)
        vin-edit @(!vin-edit nil)]
    (if (or ocr-details manual-vin-entry?)
      [[:div
        (when-let [error (:error-message ocr-details)]
          [:div.text-sm.text-red-600.p-3.py-3.bg-white.border-t
           [:span {:data-testid "ocr-scan-error"} error]])
        (when (:scan/image ocr-details)
          [:div.bg-white.flex.justify-center.pt-1
           [:img.rounded-md
            {:width 250
             :src (str "data:image/png;base64, " (:scan/image ocr-details))}]])
        [:div.bg-white.flex.justify-center.pt-2
         [vin-chars-input
          {:on-change (fn [vin-edit]
                        (deframe/route-assoc! ::vin-edit vin-edit))}
          vin-edit]]
        [:div.py-2.font-bold.text-sm.text-center.border-b.bg-white
         (case (:state vin-edit)
           :empty [:span.text-orange-600 (tr "Please enter a 17-digit VIN")]
           :incomplete [:span.text-orange-600 (tr "Please complete the VIN")]
           :complete [:span.text-green-600 "✅ " (tr "VIN looks complete")])]]]
      (concat
       [[mobile/list-item
         {:icon (cond
                  (scan-contains-vin? scan) mobile/icon-task-complete
                  :else mobile/icon-scan)
          :title [:div {:class "text-[1rem]"}
                  (cond
                    (scan-contains-vin? scan) [:span (tr "Recognized VIN")]
                    :else (tr "Scan the VIN"))]
          :description result}]]))))

(defn arrival-vin-scan [{:as fahrzeug :keys [entry/mode compound-name]}]
  (let [{:as scan :scan/keys [ocr-details result]} @(deframe/!route-get-in [::state :scan])
        vin-edit @(!vin-edit "")]
    (with-hardware-scanner
      {:view `arrival-vin-scan
       :on-scan (fn [{:as scan :scan/keys [result classified]}]
                  (when scan
                    (deframe/tx! [[:op/assoc-in [:current-route/transient ::state :scan] scan]
                                  [:op/dissoc ::deframe/current-scan]])))}
      [mobile/scroll-content
       [mobile/navbar {:title (entry-mode-label fahrzeug)
                       :subtitle (tr "Entry")
                       :back [mobile/back-link {:route (router/path '[compound-ops :compound-name] {:compound-name compound-name})}]
                       :right-item [workflow-menu-dropdown]}]
       [:div
        (when-let [error @(deframe/!route-get-in [::state :error])]
          [ui/expected-error-view error])
        [mobile/inline-prompt :scan [:div {:data-testid "scan-vin-prompt"}
                                     (tr "Scan the VIN with the Hardware Scanner")
                                     (when-not ui/is-mobile? simulate-scan-hint)]]
        [mobile/table {:inset? true
                       :items (vin-scan-list-items)}]

        (when @(deframe/!route-get-in [::state :loading?])
          [:div.absolute.right-0.mr-4 {:class "bottom-[100px]"} [ui/spinner {:size 30}]])
        [toolbar {:disabled (or (not scan) (and ocr-details (not (vin-complete? (:state vin-edit)))))
                  :label (tr "Start Entry")
                  :on-click #(do (.preventDefault %) ;; necessary when this is a form
                                 (deframe/call-lambda {:on-result (fn [result]
                                                                    (if (:error result)
                                                                      (deframe/route-assoc-in! [::state :error] result)
                                                                      (router/navigate-to (router/path
                                                                                           '[compound-ops :compound-name workflow :fahrzeug-id]
                                                                                           {:fahrzeug-id (->fahrzeug-id "vin" (:vehicle/vin result))
                                                                                            :compound-name compound-name}))))}
                                                      'ductile.workflow.engine/handle-arrival-scan
                                                      {:vehicle/vin (if ocr-details (:vin vin-edit) result)
                                                       :entry/mode (or mode :mode/unknown)
                                                       :compound/name compound-name}))}]]])))

(defn elog-scanner [_result {:as match :keys [path-params]}]
  (with-hardware-scanner
    {:view `elog-scanner
     :on-scan (fn [{:as scan :scan/keys [result classified]}]
                (when scan
                  (-> (http/$enable-speculative-transactions! (assoc (deframe/namespace-path-params path-params)
                                                                     :ductile.elog.tx-import/record-missing-data true
                                                                     :vehicle/vin result
                                                                     :ductile.datomic.speculative-transactions/init-txd 'ductile.elog.tx-import/single-fahrzeug-txd
                                                                     :ductile.datomic.speculative-transactions/scope :single-browser-tab))
                      (.then (fn [{:as lambda-response :keys [error]}]
                               (if error
                                 (deframe/route-assoc-in! [::state :error] lambda-response)
                                 (router/navigate-to (router/path '[compound-ops :compound-name workflow :fahrzeug-id]
                                                                  (assoc path-params :fahrzeug-id result)))))))))}
    [mobile/scroll-content
     [mobile/navbar {:title "ELOG Scanner"
                     :back [mobile/back-link {:route (router/path '[compound-ops :compound-name] path-params)}]
                     :right-item [workflow-menu-dropdown]}]
     [:div
      (when-let [error @(deframe/!route-get-in [::state :error])]
        [ui/expected-error-view error])
      [mobile/inline-prompt :scan [:div {:data-testid "scan-vin-prompt"}
                                   (tr "Scan a VIN from ELOG with the Hardware Scanner to temporarily import it in Ductile")
                                   (when-not ui/is-mobile? simulate-scan-hint)]]
      [mobile/table {:inset? true
                     :items (vin-scan-list-items)}]

      (when @(deframe/!route-get-in [::state :loading?])
        [:div.absolute.right-0.mr-4 {:class "bottom-[100px]"} [ui/spinner {:size 30}]])]]))

(defn ship-entry [{:as result :keys [vins identifiers transport-name]} _match]
  (with-hardware-scanner {:view `ship-entry
                          :on-scan (fn [{:as scan :scan/keys [result classified]}]
                                     (let [vin (or (identifiers result) ;; using the customer identifiers
                                                   (:vehicle/vin classified))]  ;; straight 17 char VIN
                                       (deframe/reset-current-scan!)
                                       (cond
                                         (not vin)
                                         (deframe/route-assoc! ::notice {:type :error :message (str "Invalid VIN or customer identifier: " result)
                                                                         :on-close #(deframe/route-dissoc! ::notice)})


                                         (not (contains? (set vins) vin))
                                         (deframe/route-assoc! ::notice {:type :error :message (str "VIN " (:vehicle/vin classified) " not found on ship")
                                                                         :on-close #(deframe/route-dissoc! ::notice)})

                                         :else
                                         (router/navigate-to (router/path '[compound-ops :compound-name workflow exit ship :transport-id exit-service :fahrzeug-id]
                                                                          (assoc (:path-params @deframe/!current-route) :fahrzeug-id (->fahrzeug-id "vin" vin)))))))}
    [mobile/scroll-content
     [mobile/navbar {:title (tr "Ship entry")
                     :subtitle transport-name
                     :back [mobile/back-link {:route (router/path '[compound-ops :compound-name] (:path-params @deframe/!current-route))}]
                     :right-item [workflow-menu-dropdown]}]
     (when-let [notice @(deframe/!route-get ::notice)]
       [:div.overflow-hidden.rounded-lg.shadow-lg.mb-5 {:data-testid "ship-entry-notice"}
        [ui/flash-notice notice]])
     (if (:error result)
       [ui/expected-error-view result]
       [:div.m-4
        [transport/transport-progress result]])]))

(defn ship-entry-overview [ships _match]
  [mobile/scroll-content
   [mobile/navbar {:title (tr "Ship Entry")
                   :back [mobile/back-link {:route (router/path '[compound-ops :compound-name] (:path-params @deframe/!current-route))}]
                   :right-item [workflow-menu-dropdown]}]
   [mobile/inline-prompt :anchor (tr "Choose the ship you're doing entry for")]
   [mobile/table {:inset? true
                  :items (into []
                               (map (fn [ship]
                                      [mobile/list-item-disclose {:title (:transport/name ship)
                                                                  :icon [mobile/framed-icon "bg-blue-600" :anchor]
                                                                  :route (router/path '[compound-ops :compound-name workflow entry ship :transport-id]
                                                                                      (merge (:path-params @deframe/!current-route)
                                                                                             {:transport-id (:ductile/id ship)}))}])
                                    ships))}]])

(defn ship-exit-overview [ships _match]
  [mobile/scroll-content
   [mobile/navbar {:title (tr "Ship exit")
                   :back [mobile/back-link {:route (router/path '[compound-ops :compound-name] (:path-params @deframe/!current-route))}]
                   :right-item [workflow-menu-dropdown]}]
   [mobile/inline-prompt :anchor (tr "Choose the ship you're doing exit for")]
   [mobile/table {:inset? true
                  :items (into []
                               (map (fn [ship]
                                      [mobile/list-item-disclose {:title (:transport/name ship)
                                                                  :icon [mobile/framed-icon "bg-blue-600" :anchor]
                                                                  :route (router/path '[compound-ops :compound-name workflow exit ship :transport-id]
                                                                                      (merge (:path-params @deframe/!current-route)
                                                                                             {:transport-id (:ductile/id ship)}))}])
                                    ships))}]])

(defn bulk-arrival-scan-overview
  [loads _match]
  [mobile/scroll-content
   [mobile/navbar {:title (tr "Bulk Entry")
                   :back [mobile/back-link {:route (router/path '[compound-ops :compound-name] (:path-params @deframe/!current-route))}]
                   :right-item [workflow-menu-dropdown]}]
   [mobile/inline-prompt :help-circle (tr "Choose the load for bulk entry")]
   (if (empty? loads)
     [:div.p-4.text-center.text-gray-500
      (tr "No open loads found")]
     [mobile/table {:inset? true
                    :items (into []
                                 (map (fn [load]
                                        (let [mode (keyword (name (:transport/mode load)))]
                                          [mobile/list-item-disclose {:title (:transport/name load)
                                                                      :subtitle (tr "%1 vehicles" (:transport/fahrzeuge-count load))
                                                                      :icon [mobile/framed-icon (case mode
                                                                                                  :train "bg-green-600"
                                                                                                  :ship "bg-blue-600")
                                                                             (keyword (name (:transport/mode load)))]
                                                                      :route (router/path '[compound-ops :compound-name bulk-arrival-scan :transport-id]
                                                                                          (merge (:path-params @deframe/!current-route)
                                                                                                 {:transport-id (:ductile/id load)}))}])))
                                 loads)}])])

(defn bulk-arrival-scan
  [{:as progress :keys [transport-name error vins identifiers]} _match]
  (let [{:as scan :scan/keys [ocr-details result]} @(deframe/!route-get-in [::state :scan])
        vin-edit @(!vin-edit "")
        compound-name (get-in @deframe/!current-route [:path-params :compound-name])
        transport-id (get-in @deframe/!current-route [:path-params :transport-id])
        final-vin (if ocr-details
                    (:vin vin-edit)
                    (or (get identifiers (:scan/result scan))
                        (:vehicle/vin (:scan/classified scan))))]
    (if error
      [mobile/scroll-content
       [mobile/navbar {:title (tr "Bulk Entry")
                       :back [mobile/back-link {:route (router/path '[compound-ops :compound-name bulk-arrival-scan]
                                                                    {:compound-name compound-name})}]}]
       [ui/expected-error-view progress]]
      (with-hardware-scanner
        {:view `bulk-arrival-scan
         :on-scan (fn [scan]
                    (when scan
                      (deframe/route-dissoc! ::notice)
                      (let [vin (or (get identifiers (:scan/result scan))
                                    (:vehicle/vin (:scan/classified scan)))]
                        (if (not-empty vins)
                          ;; Manifest known: validate against manifest before storing
                          (cond
                            (not vin)
                            (deframe/route-assoc! ::notice {:type :error
                                                            :message (tr "%1 invalid" (:scan/result scan))
                                                            :on-close #(deframe/route-dissoc! ::notice)})

                            (not (contains? (set vins) vin))
                            (deframe/route-assoc! ::notice {:type :error
                                                            :message (tr "%1 not on manifest" vin)
                                                            :on-close #(deframe/route-dissoc! ::notice)})

                            :else
                            (deframe/tx! [[:op/assoc-in [:current-route/transient ::state :scan] scan]
                                          [:op/dissoc ::deframe/current-scan]]))
                          ;; Manifest unknown: accept any scan
                          (deframe/tx! [[:op/assoc-in [:current-route/transient ::state :scan] scan]
                                        [:op/dissoc ::deframe/current-scan]])))))}
        [mobile/scroll-content
         [mobile/navbar {:title (tr "Bulk Entry")
                         :subtitle transport-name
                         :back [mobile/back-link {:route (router/path '[compound-ops :compound-name bulk-arrival-scan]
                                                                      {:compound-name compound-name})}]
                         :right-item [workflow-menu-dropdown]}]
         [:div {:data-testid "bulk-arrival-content"}
          ;; Progress display
          (when-let [error @(deframe/!route-get-in [::state :error])]
            [ui/expected-error-view error])

          (when-let [notice @(deframe/!route-get ::notice)]
            [:div.overflow-hidden.rounded-lg.shadow-lg.mb-5 {:data-testid "bulk-entry-notice"}
             [ui/flash-notice notice]])

          [mobile/table {:inset? true
                         :items (vin-scan-list-items)}]

          [:div.m-2
           (if (not-empty vins)
             [transport/transport-progress progress]
             [mobile/inline-prompt :info (tr "%1 vehicles unloaded" (:done progress))])]

          (when @(deframe/!route-get-in [::state :loading?])
            [:div.absolute.right-0.mr-4 {:class "bottom-[100px]"} [ui/spinner {:size 30}]])
          [toolbar {:disabled (or (not scan) (and ocr-details (not (vin-complete? (:state vin-edit)))))
                    :label (tr "Confirm Entry")
                    :on-click #(do (.preventDefault %)
                                   (deframe/route-assoc-in! [::state :loading?] true)
                                   (deframe/call-lambda {:on-result (fn [result]
                                                                      (deframe/route-assoc-in! [::state :loading?] false)
                                                                      (if (:error result)
                                                                        (deframe/route-assoc-in! [::state :error] result)
                                                                        ;; On success, clear the scan and reload the page
                                                                        (do
                                                                          (deframe/tx! [[:op/dissoc-in [:current-route/transient ::state :scan]]])
                                                                          (deframe/route-assoc! ::notice {:type :success :message (tr "Arrival of %1 confirmed" final-vin)
                                                                                                          :on-close (fn [] (deframe/route-dissoc! ::notice))})
                                                                          (deframe/call-route-refetch))))}
                                                        'ductile.workflow.engine/handle-bulk-arrival-scan
                                                        {:vehicle/vin final-vin
                                                         :entry/transport-id (uuid transport-id)
                                                         :compound/name compound-name}))}]]]))))


(defn exited-fahrzeug [fahrzeug]
  [:div
   [mobile/scroll-content
    [mobile/navbar {:title (:vehicle/vin fahrzeug)
                    :back [mobile/back-link {:route (router/path (if (= :mode/ship
                                                                        (get-in fahrzeug [:exit/transport-order :transport/mode]))
                                                                   '[compound-ops :compound-name workflow exit-ship current]
                                                                   '[compound-ops :compound-name])
                                                                 {:compound-name (:compound/name fahrzeug)})}]
                    :right-item [workflow-menu-dropdown]}]
    [mobile/inline-prompt :car (str (tr "This vehicle has left the compound on") ": ") (time-format/->str {:format :datetime} (:exit/datetime fahrzeug))]]])

(defn exited-wheel-set [wheel-set]
  [:div
   [mobile/scroll-content
    [mobile/navbar {:title (:vehicle/vin wheel-set)
                    :back [mobile/back-link {:route (router/path '[compound-ops :compound-name flow wheel-set]
                                                                 {:compound-name (:compound/name wheel-set)})}]
                    :right-item [workflow-menu-dropdown]}]
    [mobile/inline-prompt :tire (str (tr "This wheel set has left the compound on") ": ") (time-format/->str {:format :datetime} (:exit/datetime wheel-set))]]])

(defn parking-mode [{:keys [compound-name]}]
  (with-hardware-scanner {:view `parking-mode
                          :on-scan (fn [{:as scan :scan/keys [result classified]}]
                                     (when scan
                                       (deframe/call-lambda
                                        'ductile.workflow/build-fahrzeug+scan (process-scan scan))
                                       (deframe/reset-current-scan!)))}

    [mobile/scroll-content
     [mobile/navbar {:title (tr "Parking mode")
                     :back [mobile/back-link {:route (router/path '[compound-ops :compound-name]
                                                                  {:compound-name compound-name})}]
                     :right-item [workflow-menu-dropdown]}]
     [:div
      (when-let [notice @(deframe/!route-get ::notice)]
        [ui/flash-notice notice])
      (when @(deframe/!route-get-in [:fetch :loading])
        [:div.absolute.right-0.mr-4 {:class "bottom-[100px]"} [ui/spinner {:size 30}]])
      (if @!fahrzeug
        (let [service-id (if (not-empty (filter (comp #{:service.id/storage} :service/id :service/template)
                                                (:workflow/active-services @!fahrzeug)))
                           :service.id/storage
                           :service.id/reparking)]
          [:div
           [mobile/table {:title (tr "Services")
                          :inset? true
                          :items (->> (:workflow/active-services @!fahrzeug)
                                      (remove (comp #{:optionality/can} :service/optionality))
                                      (mapv (fn [service]
                                              [mobile/list-item {:title [:div.flex.gap-2.items-center (:service/code service)]}])))}]
           [parking-spot-select
            (merge @!fahrzeug @(deframe/!route-get ::parking-mode-spot))
            {:service {:service/template {:service/id service-id}}
             :on-complete (partial parking-mode-confirm-spot! service-id)}]])
        [:<>
         [mobile/inline-prompt :scan [:div
                                      {:data-testid "scan-vin-prompt"}
                                      (tr "Scan the VIN with the Hardware Scanner")
                                      (when-not ui/is-mobile? simulate-scan-hint)]]])]]))

(defn render-parking-mode [_result {:keys [path-params] :as _match}]
  [parking-mode path-params])

(defn render-service-entry [fahrzeug {:as service :service/keys [code active? optionality]}]
  (cond
    (completed? service)
    [mobile/list-item {:title [complete-label code]
                       :icon [ui/icon {:size 16} :check]}]
    (not active?)
    ;; TODO: add subtitle with why the service is inactive
    [mobile/list-item {:title [complete-label code]
                       :right [ui/icon {:size 22 :class "flex-shrink-0 text-gray-400"} :chevron-right]}]
    :else
    [mobile/list-item
     {:title [:div.flex.gap-2.items-center
              {:class (when (contains? #{:optionality/must :optionality/should} optionality) "font-bold")}
              [ui/service+details service]
              [ui/optionality-badge optionality]]
      :route (fahrzeug-route fahrzeug)
      :right [ui/icon {:size 22 :class "flex-shrink-0"} :chevron-right]
      :on-click #(select-service! service)}]))

(defn select-service-group! [services]
  (deframe/route-assoc! ::selected-service-group services))

(defn deselect-service-group! []
  (deframe/route-dissoc! ::selected-service-group))

(def !selected-service-group (deframe/!route-get ::selected-service-group))

(defn service-group-view [services]
  (let [service (first services)]
    [mobile/scroll-content
     [mobile/navbar {:title [ui/service+details service]
                     :back [mobile/back-link {:on-click #(deselect-service-group!)}]
                     :right-item [workflow-menu-dropdown]}]
     [mobile/info-bar @!fahrzeug]
     [:div.mx-6.mt-3.mb-4.font-bold.text-sm
      (tr "Completed: %1 times" (count services))]
     (into [:<>]
           (mapv (fn [service]
                   (let [details (fahrzeug/service-details service)
                         completed-at (time-format/->str {:format :datetime} (:service/completed-at service))]
                     [:div.-mt-5
                      [mobile/table {:inset? true
                                     :items [[mobile/list-item
                                              (cond-> {:title [:div.flex.flex-col.gap-2.text-sm
                                                               [:div.flex.gap-2
                                                                [ui/icon-socket [ui/icon {:size 16} :check]]
                                                                [:span {:class "mt-[2px]"} completed-at]]
                                                               (when details
                                                                 [:div.flex.gap-2
                                                                  [ui/icon-socket [ui/icon {:size 16} :file-text]]
                                                                  [:span {:class "mt-[2px]"} details]])
                                                               [ui/service-notifications service]]})]]}]]))
                 (reverse services)))]))

(defn service-group-list-item [services]
  (let [{:as service :service/keys [completed-at]} (last services)]
    [mobile/list-item
     {:title [ui/service+details service]
      :as :button
      :subtitle (str (tr "Most recent") ": "
                     (time-format/->str {:format :datetime} completed-at))
      :right [:div.flex.items-center.gap-1
              [ui/badge {:variant :gray :size 2} (count services)]
              [ui/icon {:size 22} :chevron-right]]
      :icon [ui/icon {:size 16} :check]
      :on-click #(select-service-group! services)}]))

(defn service-step [service]
  [:div
   [:div.flex.items-center.gap-1
    [:span.font-bold (ui/service-label service)]
    [ui/optionality-badge (:service/optionality service)]
    (when (:service/active? service)
      [ui/badge {:size 1 :variant :blue} (tr "Active")])]
   (when-let [scheduled (seq (:service/scheduled-datetimes service))]
     [:div.flex.gap-1.mt-2
      [:div.font-bold.text-orange-600.flex.gap-1
       {:class ["text-[12px]"]}
       [ui/icon {:size 12 :style {:margin-top 1}} :clock]
       (tr "Scheduled for")]
      (into [:div]
            (map (fn [dt]
                   [:div.text-orange-600.tabular-nums
                    {:class "text-[12px]"}
                    ;; TODO: this should be a js date already for ->str to work
                    (time-format/->str (js/Date. dt))]))
            scheduled)])])

(defn format-storage-location [{:as location :shelf/keys [row place level]}]
  (->> [row place level] (filter some?) (str/join "/")))

(defn compound-view [fahrzeug {:keys [service]}]
  (let [parking-str (parking-utils/parking-spot->str fahrzeug)
        storage-location-str (some->> fahrzeug :storage/location format-storage-location)]
    (cond
      @!selected-service-group
      [service-group-view @!selected-service-group]

      @!selected-service
      (let [service @!selected-service]
        (when (:ui/view service)
          [:<>
           [scanner-view]
           [(:ui/view service) fahrzeug {:service service}]]))
      :else
      [:div
       [mobile/scroll-content
        [ui/error-view @(deframe/!route-get-in [:fetch :result :error])]
        (when @(deframe/!route-get-in [:fetch :result ::fahrzeug/services-modal-open?])
          [ui/modal {:on-close #(deframe/route-dissoc-in! [:fetch :result ::fahrzeug/services-modal-open?])}
           [fahrzeug/new-service-form fahrzeug]])
        [mobile/navbar {:title (:vehicle/vin fahrzeug)
                        :back [context-aware-compound-ops-backlink fahrzeug]
                        :right-item [workflow-menu-dropdown]}]
        [fahrzeug/vehicle-details-header fahrzeug]
        (let [!can-do-services-visible? (deframe/!route-get ::can-do-services-visible? false)
              optional->services (group-by (comp boolean #{:optionality/can} :service/optionality) (:workflow/active-services fahrzeug))]
          [:<>
           [mobile/table {:title (tr "Next up")
                          :data-testid "active-services"
                          :inset? true
                          :items (cond-> (mapv (partial render-service-entry fahrzeug) (cond-> (optional->services false)
                                                                                         @!can-do-services-visible?
                                                                                         (concat (optional->services true))))
                                   (seq (optional->services true))
                                   (concat [[mobile/list-item
                                             {:title [:span.text-indigo-600
                                                      {:data-testid "can-do-services-toggle"}
                                                      (str (if @!can-do-services-visible?
                                                             (tr "Show less")
                                                             (tr "Show more"))
                                                           "…")]
                                              :on-click #(deframe/route-assoc! ::can-do-services-visible? (not @!can-do-services-visible?))}]]))}]])
        [mobile/table {:inset? true
                       :items (concat
                               [[mobile/list-item {:title [:span
                                                           (if (and (nil? parking-str) (nil? storage-location-str))
                                                             (tr "Not parked")
                                                             (str (if storage-location-str
                                                                    (tr "Storage location")
                                                                    (tr "Parked at"))
                                                                  ": "))
                                                           [:span.font-bold {:data-testid "parking-spot"} (or parking-str storage-location-str)]]
                                                   :icon [ui/icon {:size 16} :map-pin]
                                                   :right (when-let [parking-service (first (filter :service/reparking (:workflow/active-services fahrzeug)))]
                                                            [ui/button
                                                             {:variant :primary
                                                              :size 3
                                                              :data-testid "repark-button"
                                                              :on-click #(select-service! parking-service)}
                                                             (if (nil? parking-str)
                                                               (tr "Park")
                                                               (tr "Repark"))])}]]
                               (when (:fahrzeug/damages fahrzeug)
                                 (let [damage-inspection-services (filter #(contains? #{"Ispezione Danni"} (:service/code %))
                                                                          (:service.event/completed fahrzeug))]
                                   [[mobile/list-item
                                     (cond-> {:title [:div.text-red-600.text-sm.font-bold.leading-tighter
                                                      (tr "This vehicle has been marked as damaged")]
                                              :icon [ui/icon {:size 16 :class "text-red-600"} :alert-triangle]}
                                       (seq damage-inspection-services)
                                       (assoc :right [ui/icon {:size 22} :chevron-right]
                                              :on-click #(select-service-group! damage-inspection-services)))]]))
                               (when-let [{:parking-spot/keys [zone row] :as _loading-line} (-> fahrzeug :exit/transport :transport/loading-line)]
                                 [[mobile/list-item {:title (str (tr "Loading line") ": " (:parking-zone/name zone) row)
                                                     :icon [ui/icon {:size 16} :truck]}]])
                               (when-let [exit-transport-order (:exit/transport-order fahrzeug)]
                                 [[mobile/list-item {:title [:span (tr "Destination") ": " (fahrzeug/location (:destination/location exit-transport-order))]
                                                     :icon [ui/icon {:size 16} :map]}]])
                               (when-let [date (:fahrzeug/expected-customer-delivery-datetime fahrzeug)]
                                 [[mobile/list-item {:title [:div
                                                             [:span.font-bold (tr "Appointment date") ": "]
                                                             (time-format/->str {:format :date :style :short}
                                                                                (:fahrzeug/expected-customer-delivery-datetime fahrzeug))]
                                                     :icon [ui/icon {:size 16} :calendar]}]]))}]
        (when-let [services (not-empty (:service.event/canceled fahrzeug))]
          [mobile/table {:title (tr "Service Cancelations")
                         :data-testid "service-cacelations"
                         :inset? true
                         :items (mapv (partial render-service-entry fahrzeug) services)}])

        (when-let [services (seq (:workflow/scheduled-services fahrzeug))]
          (let [!expanded? (deframe/!route-get ::workflow-plan-expanded? false)]
            [:div {:class (when-not @!expanded? "-mb-4")}
             [mobile/table {:title [:div.flex.gap-2.items-center.cursor-pointer.w-full.select-none
                                    {:on-click #(deframe/route-update! ::workflow-plan-expanded? not)}
                                    (tr "Workflow Plan")
                                    [:span.flex.items-center.gap-2
                                     (when-not @!expanded?
                                       [ui/badge {:variant :gray} (count services)])
                                     [ui/icon {:size 16 :class (str "transition " (when @!expanded? "rotate-90"))} :chevron-right]]]
                            :inset? true
                            :ui/margin-bottom :none
                            :data-testid "workflow-plan"
                            :items (when @!expanded?
                                     (mapv (fn [service]
                                             [mobile/list-item {:title [service-step service]}])
                                           services))}]]))

        (when (:service.event/completed fahrzeug)
          [mobile/table {:title (tr "Completed")
                         :inset? true
                         :data-testid "services-completed"
                         :items (->> (:service.event/completed fahrzeug)
                                     (group-by :service/code)
                                     (sort-by (comp :service/completed-at last val))
                                     reverse
                                     (mapv (fn [[_code services]]
                                             [service-group-list-item services])))}])]])))

(defn service-list-item [{:as service :keys [service/completed? service/action]}]
  [:div.px-3.py-2.border-b.last:border-b-0.text-sm.flex.items-center.justify-between
   {:on-click #(select-service! service)}
   [:div.flex.items-center.gap-3
    (when completed?
      [ui/icon {:size 16} :check])
    [ui/service+details service]]
   [ui/icon {:size 16} :chevron-right]])

(defn attr-field-label [{:as field :ui/keys [attr title optional? readonly?]}]
  [:span
   (or (some-> title tr)
       (tr attr))
   (when (and attr (not readonly?))
     (if optional?
       [:span.text-slate-400.ml-1 "(" (tr "optional") ")"]
       [:span.text-orange-400.ml-1 "(" (tr "required") ")"]))])

(defn prompt-view [{:ui/keys [prompt icon image]}]
  [mobile/inline-prompt
   (if image
     [:div.flex.justify-center.mb-3
      [:img.rounded-full.border-2.border-white.shadow.object-cover
       {:class ["w-[80px]"
                "h-[80px]"]
        :src image}]]
     (if (some? icon) icon :alert-circle))
   [:div.flex.flex-col
    [:span (cond-> prompt (not (vector? prompt)) tr)]]])

(defn fahrzeug-view [{:as fahrzeug :ui/keys [view-props]}]
  [fahrzeug/vehicle-details-header view-props fahrzeug])

(defn progress-bar-view [{:ui/keys [label progress-bar]}]
  (let [{:keys [total current]} progress-bar
        #_#_total (if (< total current) current total)]
    [:<>
     (into
      [:div.w-full.p-3.flex.justify-between.min-h-5]
      (map (fn [i]
             [:div.w-full.border.border-r-0.text-white.text-center
              {:class
               ["first:rounded-l-full" "last:border-r" "last:rounded-r-full"
                (cond (< i current) "bg-indigo-400"
                      (= i current) "bg-indigo-600"
                      :else "bg-white/80")]}
              (when (= i current) current)
              (when (< total i) " ⚠️")]))
      (range 1 (inc (max total current))))

     [:div.text-center {:class (if (< total current) "text-orange-700" "text-slate-700")}
      (tr (or label ":current of :total") {:current current :total total})
      (when (< total current) " ⚠️")]]))

(defn vehicle-list-view [field]
  (into [:div.m-3.mb-0.rounded-md.border.border-b-0.border-slate-300.overflow-hidden]
        (map (fn [vehicle]
               [fahrzeug/vehicle-details-header {:details? false :size :sm :layout :row} vehicle]))
        (get field (:ui/attr field))))


(defn accessories-preview-view
  "Renders a preview table for accessories import (KIA or Omoda-Jaecoo)."
  [{:as ui-context :ui/keys [attr]}]
  (let [preview-data (get ui-context attr)]
    (when (seq preview-data)
      [:div.p-4
       [:div.overflow-x-auto
        [table/table {:transform-columns {[:carpet-codes] #(str/join ", " (sort (:carpet-codes %)))
                                          [:accessories] #(str/join ", " (sort (:accessories %)))
                                          [:accessories-gpl] #(str/join ", " (sort (:accessories-gpl %)))}}
         preview-data]]])))

(defn data-table-view
  "Renders a preview table for license plate mounting orders."
  [{:as ui-context :ui/keys [attr]}]
  (let [preview-data (get ui-context attr)]
    (when (seq preview-data)
      [:div.p-4
       [:div.overflow-x-auto
        [table/table {} preview-data]]])))

(defn table-view [ui-context {:ui/keys [table]}]
  [:div.px-4.py-3.bg-white.border-t.border-b
   [:table.text-xs.mx-auto
    (into [:tbody]
          (map (fn [attr]
                 (when-let [v (get ui-context attr)]
                   [:tr
                    [:th.text-right.pr-2.align-top (tr attr)]
                    [:td.align-top
                     (case attr
                       (:vehicle/damages-outside :vehicle/damages-inside)
                       (into [:div]
                             (map (fn [damage]
                                    [:div (:damage/region damage)]))
                             v)
                       :vehicle/equipment
                       (into [:div]
                             (map (fn [equipment]
                                    [:div equipment]))
                             v)
                       (cond
                         (keyword? v) (tr v)
                         (boolean? v) (if v (tr "Yes") (tr "No"))
                         (iterable? v) (str/join ", " v)
                         :else (str v)))]])))
          table)]])

(defn todos-view [{:ui/keys [todos title]}]
  [mobile/table {:inset? true
                 :title title
                 :items (mapv (fn [todo]
                                [mobile/list-item {:title todo}])
                              todos)}])

(defn default-value [schema-type]
  (case schema-type
    (:string :string+ :re) ""
    :enum #{}
    :set #{}
    nil))

(defn property-list-view [m]
  [:table.border.text-sm
   (into [:tbody]
         (map (fn [[k v]]
                [:tr.border-b.last:border-b-0.align-top
                 [:th.text-left.border-r.px-2 (tr k)]
                 [:td.px-2 (str v)]]))
         m)])

(defscene property-list
  [:div.p-4
   [property-list-view magic-wand-defaults]])

(defn entity-diff-view [{:as field :ui/keys [attr]}]
  [:div.px-4.py-2.bg-white.border-b.overflow-auto
   (into [:div.flex.flex-col.gap-2]
         (map (fn [entity-map]
                [:div
                 [render/inspect
                  (v/with-viewers
                    (v/add-viewers [dcv/diff-viewer])
                    entity-map)]]))
         (get field attr))])

(defn datoms-view [{:as field :ui/keys [attr] :ui.view/keys [hide-tx? history-link?]}]
  [:table.text-xs.w-full
   [:thead.text-left.bg-white
    [:tr.border-b.border-gray-300
     [:th.pl-1.pr-1.py-1.border-r.border-gray-300 ""]
     [:th.pl-1.pr-2.border-r.py-1.border-gray-300.text-right (tr "Entity")]
     [:th.pl-2.pr-1.border-r.py-1.border-gray-300 (tr "Attribute")]
     [:th.px-1.py-1
      {:class (when-not hide-tx? ["border-r" "border-gray-300"])}
      (tr "Value")]
     (when-not hide-tx?
       [:th.px-1.py-1 "tx"])]]
   (into [:tbody]
         (map (fn [{:keys [e a v tx added]}]
                [:tr.border-b.border-gray-300
                 {:class [(if added "bg-green-50" "bg-red-50")]}
                 [:td.pl-1.pr-2.border-r.border-gray-300.text-center
                  (if added
                    [:span.font-bold.text-green-600 "+"]
                    [:span.font-bold.text-red-600 "-"])]
                 [:td.pl-1.pr-2.border-r.border-gray-300.font-mono.text-right
                  (if history-link?
                    [:a {:href (router/path '[entity-history :entity/id :tx] {:entity/id e :tx tx})} e]
                    e)]
                 [:td.pl-2.pr-1.border-r.border-gray-300.font-mono (str a)]
                 [:td.px-1
                  {:class (when-not hide-tx?
                            ["border-r" "border-gray-300"])}
                  [render/inspect v]]
                 (when-not hide-tx?
                   [:td.px-1 tx])]))
         (get field attr))])

(defn data-view [{:as field :ui/keys [attr]}]
  [:div.px-4.py-2.bg-white.border-b.overflow-auto
   [render/inspect {:nextjournal/value (get field attr)}]])

(comment
  @(deframe/!route-get ::recorded-attrs))

(defn- valid-lat? [v]
  (and (number? v) (js/isFinite v) (<= -90 v 90)))

(defn- valid-lon? [v]
  (and (number? v) (js/isFinite v) (<= -180 v 180)))

(defn- round-coord [v]
  (/ (js/Math.round (* v 100000)) 100000))

(comment
  @(deframe/!route-get-in []))

(defn coordinate-picker-field-view
  [{:as field :ui/keys [latitude-attr longitude-attr]}]
  (let [lat-attr (or latitude-attr :location/latitude)
        lon-attr (or longitude-attr :location/longitude)
        lat-path (recorded-attr-path (assoc field :ui/attr lat-attr))
        lon-path (recorded-attr-path (assoc field :ui/attr lon-attr))
        lat-raw @(deframe/!route-get-in lat-path)
        lon-raw @(deframe/!route-get-in lon-path)
        lat (when (valid-lat? lat-raw) lat-raw)
        lon (when (valid-lon? lon-raw) lon-raw)
        has-coords? (and lat lon)
        form-path (vec (butlast lat-path))
        address (select-keys @(deframe/!route-get-in form-path) [:address/street :address/postcode :address/city :address/country])
        can-geocode? (and (:address/street address) (:address/city address))
        !map-ref (hooks/use-ref nil)
        !geocoding? (hooks/use-state false)
        !map-instance (hooks/use-state nil)
        !marker-ref (hooks/use-state nil)
        !centered? (hooks/use-state false)]
    (hooks/use-effect
     (fn []
       (when-let [container @!map-ref]
         (let [center (if has-coords?
                        [lon lat]
                        (:center mapbox/map-opts-defaults))
               zoom (if has-coords? 14 9)
               ^js the-map (js/mapboxgl.Map.
                            (clj->js (merge mapbox/map-opts-defaults
                                            {:container container
                                             :center center
                                             :zoom zoom})))]
           (reset! !map-instance the-map)
           (mapbox/ensure-locale! the-map)
           (.addControl the-map (js/mapboxgl.NavigationControl.))
           (when has-coords?
             (let [marker (js/mapboxgl.Marker.)]
               (.setLngLat marker #js [lon lat])
               (.addTo marker the-map)
               (reset! !marker-ref marker)))
           (.on the-map "click"
                (fn [^js e]
                  (let [lnglat ^js (.-lngLat e)
                        new-lon (round-coord (.-lng lnglat))
                        new-lat (round-coord (.-lat lnglat))]
                    (deframe/route-assoc-in! lat-path new-lat)
                    (deframe/route-assoc-in! lon-path new-lon))))
           (fn []
             (when-let [^js marker @!marker-ref]
               (.remove marker))
             (.remove the-map)))))
     [])
    (hooks/use-effect
     (fn []
       (when-let [^js the-map @!map-instance]
         (when-let [old-marker @!marker-ref]
           (.remove old-marker)
           (reset! !marker-ref nil))
         (when has-coords?
           (let [^js marker (js/mapboxgl.Marker.)]
             (.setLngLat marker #js [lon lat])
             (.addTo marker the-map)
             (reset! !marker-ref marker))
           (when-not @!centered?
             (reset! !centered? true)
             (.flyTo the-map #js {:center #js [lon lat] :speed 4 :zoom 14})))))
     [lat lon])
    [:div.px-4.pb-4
     #_[:div (pr-str :lat lat :lon lon
                     :lat-path lat-path
                     :lon-path lon-path
                     :form-path form-path
                     :address address)]
     [:div.mb-2.flex.items-center.justify-between
      [:span.text-xs.text-gray-400
       (tr "Click on the map to set coordinates")]
      [ui/button
       {:disabled (or (not can-geocode?) @!geocoding?)
        :variant :white
        :size 1
        :on-click (fn []
                    (reset! !geocoding? true)
                    (-> (deframe/call-lambda
                         {:result-path [:_]
                          :on-success (fn [{:keys [latitude longitude]}]
                                        (when (and latitude longitude)
                                          (reset! !centered? false)
                                          (deframe/route-assoc-in! lat-path (round-coord latitude))
                                          (deframe/route-assoc-in! lon-path (round-coord longitude))))}
                         'ductile.locations/geocode-address address)
                        (.finally (fn [] (reset! !geocoding? false)))))}
       (tr "Geocode address")]]
     [:div.rounded-lg.overflow-hidden.border.border-gray-300.shadow-sm
      {:ref !map-ref
       :style {:height "300px"}}]]))

(defn parking-spot-field-view [{:as field :ui/keys [attr] ::keys [fahrzeug service]}]
  (r/with-let [_ (when-not (contains? @(!recorded-attrs) attr)
                   (maybe-set-selected-parking-spot! service))]
    (let [path (recorded-attr-path field)
          assignment (parking-assignment (merge (->parking-keys (:service/default-attrs service))
                                                fahrzeug
                                                field))
          {:as spot :parking-spot/keys [zone row place]} @!selected-parking-spot
          value @(deframe/!route-get-in path {})
          _ @(r/track #(if-let [parking-spot @!selected-parking-spot]
                         (deframe/route-assoc-in! path @!selected-parking-spot)
                         (deframe/route-dissoc-in! path)))]
      (when (and zone
                 (not= :parking.assignment/free-spot assignment)
                 (not @(!rows->spots->n-occupied zone)))
        (fetch-rows->spots->n-occupied! zone))
      [:div.px-4.py-2
       [:div.-ml-2.relative {:class "-left-[1px]"}
        [parking-spot-selectors (merge fahrzeug field) spot]]
       (when (= :parking.assignment/predefined-spot assignment)
         [:div.flex.items-center.mt-3.gap-2
          [:button.bg-white.text-indigo-700.font-bold.text-xs.px-3.py-1.border-2.border-indigo-700.rounded-lg.shadow.disabled:opacity-50
           {:type :button
            :on-click #(fetch-suggested-parking-spot! {:service service})}
           (tr "New free spot")]])])))

(defn next-empty-index
  "Return the index of the next empty string in `values` strictly after
  `from-idx`. If none exists, return nil."
  [values from-idx]
  (let [n (count values)]
    (->> (range (inc from-idx) n)
         (filter #(str/blank? (nth values %)))
         (first))))

(defn focus-input! [refs idx]
  (when-let [^js el (get @refs idx)]
    (.focus el)
    (.select el)))

(defn focus-first-empty! [refs values]
  (let [idx (->>  values
                  (map-indexed vector)
                  (filter (fn [[_ v]] (str/blank? v)))
                  ffirst)]
    (when (some? idx)
      (js/requestAnimationFrame (fn [] (focus-input! refs idx))))))

(def vin-confidence-threshold 0.8)
(def vin-length 17)

(defn vin-edit-state [vin]
  (cond
    (str/blank? vin) :empty
    (< (count vin) vin-length) :incomplete
    :else :complete))

(defn vin-complete? [state]
  (contains? #{:complete} state))

(defn vin-chars-input [{:keys [on-change]}
                       {:keys [char-confidences focus values]}]
  (r/with-let [input-refs (r/atom (vec (repeat vin-length nil)))
               set-ref! (fn [idx el]
                          (swap! input-refs assoc idx el))
               ;; Autofocus the first empty input after mount
               _ (focus-first-empty! input-refs values)]
    ;; Autofocus based on edit info (e.g. when OCR info has changed)
    (when focus
      (focus-first-empty! input-refs values))
    [:div
     {:class "flex items-center justify-center gap-[3px]"}
     (doall
      (for [idx (range vin-length)]
        (let [value (get values idx "")]
          ^{:key idx}
          [:input
           {:type "text"
            :maxLength 1
            :ref (partial set-ref! idx)
            :class (ui/class-vec "border border-slate-300 rounded text-center shrink-0 "
                                 "w-[15px] px-0 py-[2px] shadow-inner "
                                 "font-mono tracking-widest text-[13px] "
                                 "focus:outline-none focus:ring-2 focus:ring-indigo-600")
            :value value
            :on-change
            (fn [^js e]
              (let [raw (.-value (.-target e))
                    ch  (-> raw
                            (subs 0 (min 1 (count raw)))
                            str/upper-case
                            ((:mask/prepare (ui/mask-preset :ui.mask/vin))))
                    new-values (assoc values idx ch)]
                (when on-change
                  (let [vin-str (apply str new-values)
                        state (vin-edit-state vin-str)]
                    (on-change (cond-> {:values new-values
                                        :char-confidences char-confidences
                                        :state state}
                                 (vin-complete? state) (assoc :vin vin-str)))))
                (when (not (str/blank? ch))
                  (if-let [empty-idx (next-empty-index new-values idx)]
                    (focus-input! input-refs empty-idx)
                    (when (< idx (dec vin-length))
                      (focus-input! input-refs (inc idx)))))))
            :on-key-down
            (fn [^js e]
              (let [k (.-key e)
                    curr (get values idx "")]
                (cond
                  ;; Backspace in an empty input: move to previous
                  (= k "Backspace") (when (str/blank? curr)
                                      (.preventDefault e)
                                      (when (pos? idx)
                                        (focus-input! input-refs (dec idx))))

                  ;; Arrow navigation between inputs
                  (= k "ArrowLeft") (do (.preventDefault e)
                                        (when (pos? idx)
                                          (focus-input! input-refs (dec idx))))
                  (= k "ArrowRight") (do (.preventDefault e)
                                         (when (< idx (dec vin-length))
                                           (focus-input! input-refs (inc idx)))))
                nil))
            :on-paste
            (fn [^js e]
              (.preventDefault e)
              (let [prepare (:mask/prepare (ui/mask-preset :ui.mask/vin))
                    text (.. e -clipboardData (getData "text/plain"))
                    chars (->> text
                               (map (comp prepare str/upper-case str))
                               (remove str/blank?)
                               (take (- vin-length idx))
                               vec)
                    new-values (reduce-kv
                                (fn [vs i ch]
                                  (assoc vs (+ idx i) ch))
                                values
                                chars)]
                (when (and on-change (seq chars))
                  (let [vin-str (apply str new-values)
                        state (vin-edit-state vin-str)]
                    (on-change (cond-> {:values new-values
                                        :char-confidences char-confidences
                                        :state state}
                                 (vin-complete? state) (assoc :vin vin-str)))))
                (let [next-idx (min (+ idx (count chars)) (dec vin-length))]
                  (if-let [empty-idx (next-empty-index new-values (dec next-idx))]
                    (focus-input! input-refs empty-idx)
                    (focus-input! input-refs next-idx)))))
            :on-focus (fn [^js e]
                        ;; Select existing content for quick replacement
                        (some-> e .-target .select))}])))]))

(defscene vin-correction
  [vin-chars-input
   {:char-confidences [{:confidence 0.92 :text "W"}
                       {:confidence 0.85 :text "V"}
                       {:confidence 0.95 :text "W"}
                       {:confidence 0.10 :text "Z"}
                       {:confidence 0.60 :text "Z"}
                       {:confidence 0.99 :text "Z"}
                       {:confidence 0.88 :text "1"}
                       {:confidence 0.40 :text "J"}
                       {:confidence 0.70 :text "Z"}
                       {:confidence 0.91 :text "X"}
                       {:confidence 0.83 :text "W"}
                       {:confidence 0.20 :text "0"}
                       {:confidence 0.75 :text "0"}
                       {:confidence 0.93 :text "0"}
                       {:confidence 0.82 :text "0"}
                       {:confidence 0.77 :text "0"}
                       {:confidence 0.66 :text "1"}]}])

(def !manual-vin-entry?
  (r/track (fn []
             (or @(deframe/!route-get ::ocr-details)
                 @(deframe/!route-get ::manual-vin-entry?)))))

(defn !vin-edit [value]
  (r/track (fn []
             (let [new-char-confidences (or
                                         ;; Used by legacy arrival-scan-vin and vin-list-items,
                                         ;; this can be safely removed once migrated
                                         @(deframe/!route-get-in [::state :scan :scan/ocr-details :char-confidences])
                                         ;; Used by all fields-based services
                                         (:char-confidences @(deframe/!route-get ::ocr-details)))
                   {:keys [values char-confidences]} @(deframe/!route-get ::vin-edit)]
               (cond
                 ;; New OCR info is present
                 ;; - apply new values, state, confidences
                 ;; - focus first empty input
                 (and new-char-confidences (not= new-char-confidences char-confidences))
                 (let [values (mapv (fn [idx]
                                      (let [{:keys [confidence text]} (or (get new-char-confidences idx) {})]
                                        (if (> (or confidence 0) 0.8)
                                          (or text "")
                                          "")))
                                    (range vin-length))
                       vin-str (apply str values)
                       state (vin-edit-state vin-str)]
                   (cond-> {:values values
                            :char-confidences new-char-confidences
                            :state state
                            :focus :first-empty}
                     (vin-complete? state) (assoc :vin vin-str)))
                 ;; No OCR info, no existing edit, but an existing value (e.g. from the hardware scanner)
                 ;; - this is only relevant if the user enters manual edit mode
                 (not values)
                 (let [chars (mapv str value)
                       missing (- vin-length (count chars))]
                   {:values (or values (into chars (repeat missing "")))
                    :char-confidences char-confidences
                    :state (vin-edit-state value)})
                 ;; Currently editing
                 :else
                 (let [vin-str (apply str values)
                       state (vin-edit-state vin-str)]
                   (cond-> {:values values
                            :char-confidences char-confidences
                            :state state}
                     (vin-complete? state) (assoc :vin vin-str))))))))

(defn scan-vin-field-value [{:as field :ui/keys [attr]}]
  (get field attr
       (when (:ui.scan/saved-vin field)
         ;; BUG: removes runs of X form start when switching to edit mode, even if they might be part of a valid vin
         ;; @Albert: Why is this needed? What's the actual problem?
         (str/replace (:ui.scan/saved-vin field) #"^X+" ""))))

(defn scan-vin-view [{:as field
                      :ui/keys [attr placeholder schema]
                      :ui.scan/keys [prompt success force-manual-entry? manual-entry? options allow-switching-manual-entry? saved-vin]}]
  (let [path (recorded-attr-path field)
        value (scan-vin-field-value field)
        ocr-details @(deframe/!route-get ::ocr-details)
        vin-edit @(!vin-edit value)]
    [:<>
     (if (or force-manual-entry?
             @!manual-vin-entry?)
       [:div
        (when-let [error (:error-message ocr-details)]
          [:div.text-sm.text-red-600.p-3.py-3.bg-white.border-t
           [:span {:data-testid "ocr-scan-error"} (:error-message ocr-details)]])
        (when (:scan/image ocr-details)
          [:div.bg-white.flex.justify-center.pt-1
           [:img.rounded-md
            {:width 250
             :src (str "data:image/png;base64, " (:scan/image ocr-details))}]])
        [:div.bg-white.flex.justify-center.pt-2
         [vin-chars-input
          {:on-change (fn [{:as vin-edit :keys [state vin]}]
                        (deframe/route-assoc! ::vin-edit vin-edit)
                        (when (vin-complete? state)
                          (deframe/route-assoc-in! path vin)))}
          vin-edit]]
        [:div.py-2.font-bold.text-sm.text-center.border-b.bg-white
         (case (:state vin-edit)
           :empty [:span.text-orange-600 (tr "Please enter a 17-digit VIN")]
           :incomplete [:span.text-orange-600 (tr "Please complete the VIN")]
           :complete [:span.text-green-600 "✅ " (tr "VIN looks complete")])]]
       (cond
         (seq options)
         (into [:<>]
               (map (fn [option]
                      [mobile/radio-button
                       {:label-view (fn []
                                      [:div
                                       [fahrzeug/vehicle-details-header {:details? false :size :sm :layout :prioritize-vin} option]])
                        :input {:name attr
                                :checked (= value (:vehicle/vin option))
                                :on-change #(deframe/route-assoc-in! path (:vehicle/vin option))
                                :data-testid (str attr "=" (:vehicle/vin option))}}]))
               options)

         value
         [mobile/list-item {:icon mobile/icon-task-complete
                            :title [:span {:class "text-[1rem]"} (tr success)]
                            :data-testid attr
                            :description [ui/vin value]}]
         :else
         [mobile/list-item {:icon mobile/icon-scan
                            :data-testid (str attr)
                            :title [:span {:class "text-[1rem]" :data-testid (str attr "=" value)} (tr prompt)]}]))

     (when (and allow-switching-manual-entry?
                (not force-manual-entry?)
                (not ocr-details))
       [:<>
        [:div.text-center.text-indigo-600.mt-3.cursor-pointer
         {:on-click #(deframe/route-assoc! ::manual-vin-entry? (not @!manual-vin-entry?))
          :data-testid (str attr "[manual-entry-toggle]")}
         (if @!manual-vin-entry?
           (tr "hide manual entry")
           (tr "enter VIN manually"))]])]))

(defn scan-view [{:as field :ui/keys [attr placeholder schema mask] :ui.scan/keys [prompt success allow-manual-entry?]}]
  (r/with-let [!manual-entry? (r/atom false)]
    (let [path (recorded-attr-path field)
          value @(deframe/!route-get-in path)
          valid? (if schema (m/validate schema value) true)]
      [:<>
       (if (and value valid?)
         [mobile/list-item {:icon mobile/icon-task-complete
                            :title [:span {:class "text-[1rem]"}
                                    (tr (if @!manual-entry? (:ui.manual-entry/success field) success))]
                            :data-testid attr
                            :description [:div value]}]
         [mobile/list-item {:icon mobile/icon-scan
                            :data-testid (str attr)
                            :title [:span {:class "text-[1rem]" :data-testid (str attr "=" value)}
                                    (tr (if @!manual-entry? (:ui.manual-entry/prompt field) prompt))]
                            :description (when @!manual-entry?
                                           (:ui.manual-entry/hint field))}])
       (when allow-manual-entry?
         [:<>
          (when @!manual-entry?
            [mobile/list-item-input {:input {:placeholder placeholder
                                             :auto-focus true
                                             :mask mask ; TODO mask ars-id
                                             :value value
                                             :on-change #(deframe/route-assoc-in! path (.. % -target -value))
                                             :class "tabular-nums"
                                             :data-testid (str attr "[manual-entry]")}
                                     :icon [:div.relative
                                            {:class "w-[2rem]"}
                                            [:div.absolute.text-gray-400
                                             {:class "left-[20px] top-[17px]"}
                                             [ui/icon {:size 18} :edit-3]]]}])
          [:div.text-center.text-indigo-600.mt-3.cursor-pointer
           {:on-click #(swap! !manual-entry? not)
            :data-testid (str attr "[manual-entry-toggle]")}
           (if @!manual-entry?
             (tr "hide manual entry")
             (tr "or enter manually"))]])])))

(defn photo-comment-field [{:as field :ui/keys [attr recorded-attrs-path]}]
  (let [!el (hooks/use-ref nil)
        !photo-preview (hooks/use-state nil)]
    [:div.bg-white.flex.items-center.gap-2.justify-center.p-3.text-indigo-600.hover:bg-indigo-300.active:bg-indigo-600.hover:text-white.cursor-pointer
     {:data-testid (pr-str attr)
      :on-click #(when-let [el @!el] (.click el))}
     [:input.hidden
      {:type :file
       :accept "image/*"
       :capture :environment
       :ref !el
       :on-change (fn [_event]
                    (let [file (first (.-files @!el))
                          reader (js/FileReader.)]
                      (j/assoc! reader :onloadend (fn []
                                                    (let [base64-image (.-result reader)]
                                                      (reset! !photo-preview base64-image)
                                                      (deframe/call-lambda {:on-success (fn [result]
                                                                                          (deframe/route-assoc-in!
                                                                                           (conj (or recorded-attrs-path [::recorded-attrs])
                                                                                                 attr)
                                                                                           result))
                                                                            :result-path [:current-route/transient ::photo-comments attr]}
                                                                           'ductile.damages/upload-image
                                                                           {:image base64-image})
                                                      (j/assoc! @!el :value ""))))
                      (when file
                        (.readAsDataURL reader file))))}]
     (if-let [photo (or @!photo-preview
                        (when-let [cas (get @(!recorded-attrs recorded-attrs-path) attr)] (routes/cas-path nil cas)))]
       [:div.relative
        [:img.rounded-md.border.w-full {:src photo :class ["max-h-[100px]" "object-cover"]}]
        (when (:loading @!photo-preview)
          [:div.absolute.left-0.top-0.w-full.h-full.bg-white.bg-opacity-20.flex.items-center.justify-center
           [ui/loading-indicator]])]
       [:<>
        [ui/icon {:size 24} :camera]
        (tr "Add photo")])]))

(defn multi-photo-field [{:as field :ui/keys [attr recorded-attrs-path]}]
  (let [!el (hooks/use-ref nil)
        !uploading-preview (hooks/use-state nil)
        path (conj (or recorded-attrs-path [::recorded-attrs]) attr)
        photos (or (get @(!recorded-attrs recorded-attrs-path) attr) [])]
    [:div {:data-testid (pr-str attr)}
     (when (seq photos)
       [:div.flex.flex-wrap.gap-2.p-3
        (for [i (range (count photos))]
          (let [cas (nth photos i)]
            ^{:key i}
            [:div.relative.group
             [:img.rounded-md.border {:src (routes/cas-path nil cas) :class ["w-[80px]" "h-[80px]" "object-cover"]}]
             [:button.absolute.-top-1.-right-1.bg-red-500.text-white.rounded-full.w-6.h-6.flex.items-center.justify-center.text-xs.leading-none.hover:bg-red-600.active:bg-red-700.shadow
              {:on-click (fn [e]
                           (.stopPropagation e)
                           (let [updated (into (subvec photos 0 i) (subvec photos (inc i)))]
                             (deframe/route-assoc-in! path updated)))}
              [ui/icon :x]]]))])
     [:div.bg-white.flex.items-center.gap-2.justify-center.p-3.text-indigo-600.hover:bg-indigo-300.active:bg-indigo-600.hover:text-white.cursor-pointer
      {:on-click #(when-let [el @!el] (.click el))}
      [:input.hidden
       {:type :file
        :accept "image/*"
        :capture :environment
        :ref !el
        :on-change (fn [_event]
                     (let [file (first (.-files @!el))
                           reader (js/FileReader.)]
                       (j/assoc! reader :onloadend (fn []
                                                     (let [base64-image (.-result reader)]
                                                       (reset! !uploading-preview base64-image)
                                                       (deframe/call-lambda {:on-success (fn [result]
                                                                                           (let [current (or (get @(!recorded-attrs recorded-attrs-path) attr) [])]
                                                                                             (deframe/route-assoc-in! path (conj current result)))
                                                                                           (reset! !uploading-preview nil))
                                                                             :result-path [:current-route/transient ::photo-comments attr]}
                                                                            'ductile.damages/upload-image
                                                                            {:image base64-image})
                                                       (j/assoc! @!el :value ""))))
                       (when file
                         (.readAsDataURL reader file))))}]
      (if @!uploading-preview
        [:div.relative
         [:img.rounded-md.border {:src @!uploading-preview :class ["max-h-[60px]" "object-cover"]}]
         [:div.absolute.left-0.top-0.w-full.h-full.bg-white.bg-opacity-20.flex.items-center.justify-center
          [ui/loading-indicator]]]
        [:<>
         [ui/icon {:size 24} :camera]
         (tr "Add photo")])]]))

(defn basename [filename]
  (let [[_full basename _ext] (re-matches #"(.+)\.([^.]+)$" filename)]
    basename))

(defn file-ext [filename]
  (let [[_full _basename ext] (re-matches #"(.+)\.([^.]+)$" filename)]
    ext))

(defn file-upload-field [{:as field :ui/keys [attr accepted-file-types recorded-attrs-path]}]
  (let [!el (hooks/use-ref nil)
        !dragging? (hooks/use-state false)
        !file-name (hooks/use-state {:base-name nil :file-ext nil})
        handle-file (fn [file]
                      (when file
                        (let [reader (js/FileReader.)]
                          (j/assoc! reader :onloadend
                                    (fn []
                                      (let [base64-file (.-result reader)]
                                        (deframe/call-lambda {:on-success (fn [result]
                                                                            (let [filename (.-name file)]
                                                                              (deframe/route-assoc-in!
                                                                               (conj (or recorded-attrs-path [::recorded-attrs])
                                                                                     attr)
                                                                               (assoc result :file/name filename))
                                                                              (reset! !file-name {:base-name (basename filename)
                                                                                                  :file-ext (file-ext filename)})))
                                                              :result-path [:current-route/transient ::files attr]}
                                                             'ductile.damages/upload-file
                                                             {:file base64-file})
                                        (when-let [el @!el]
                                          (j/assoc! el :value "")))))
                          (.readAsDataURL reader file))))]
    [:<>
     [:div.bg-white.flex.flex-col.items-center.gap-2.justify-center.p-3.text-indigo-600.hover:bg-indigo-300.active:bg-indigo-600.hover:text-white.cursor-pointer.focus-visible:bg-indigo-100.focus-visible:outline-none
      {:data-testid (pr-str attr)
       :class (when @!dragging? "ring-2 ring-indigo-500 bg-indigo-100")
       :tab-index 0
       :on-key-down (fn [^js e]
                      (when (contains? #{"Enter" "Space"} (.-code e))
                        (.preventDefault e)
                        (when-let [el @!el] (.click el))))
       :on-click #(when-let [el @!el] (.click el))
       :on-drag-over (fn [e]
                       (.preventDefault e)
                       (.stopPropagation e))
       :on-drag-enter (fn [e]
                        (.preventDefault e)
                        (.stopPropagation e)
                        (reset! !dragging? true))
       :on-drag-leave (fn [e]
                        (.preventDefault e)
                        (.stopPropagation e)
                        (reset! !dragging? false))
       :on-drop (fn [e]
                  (.preventDefault e)
                  (.stopPropagation e)
                  (reset! !dragging? false)
                  (when-let [file (first (array-seq (.. e -dataTransfer -files)))]
                    (handle-file file)))}
      [:input.hidden
       (cond-> {:type :file
                :capture :environment
                :ref !el
                :on-change (fn [_event]
                             (handle-file (first (.-files @!el))))}
         accepted-file-types
         (assoc :accept (cond->> accepted-file-types
                          (coll? accepted-file-types) (str/join ","))))]
      [:div.pointer-events-none.flex.items-center.justify-center.text-center
       (if-let [name @(deframe/!route-get-in (concat (or recorded-attrs-path [::recorded-attrs])
                                                     [attr :file/name]))]
         [:div
          [:div.flex.gap-1.items-center [ui/icon {:size 14} :file] name]
          [:div.text-xs (tr "Change file")]]
         [:div
          [:div (tr "Add file")]
          (when-not ui/is-mobile?
            [:div.text-xs (tr "Click to select or drag & drop")])])]]
     (when @(deframe/!route-get-in (concat (or recorded-attrs-path [::recorded-attrs]) [attr]))
       [mobile/table
        {:title (tr :file/name)
         :items [[mobile/list-item-input {:input {:value (:base-name @!file-name)
                                                  :on-change (fn [e] (swap! !file-name assoc :base-name (.. e -target -value)))
                                                  :on-blur (fn [e]
                                                             (let [base-name (.. e -target -value)
                                                                   file-name (str base-name "." (-> @!file-name :file-ext))]
                                                               (when-not (str/blank? base-name)
                                                                 (swap! !file-name assoc :base-name base-name)
                                                                 (deframe/route-assoc-in!
                                                                  (concat (or recorded-attrs-path [::recorded-attrs])
                                                                          [attr :file/name])
                                                                  file-name))))}}]]}])]))

(defn signature-preview [{:as field :ui/keys [placeholder]} value]
  (if (get-in value [:signature/image :cas/multihash])
    [:div
     [:img {:src (routes/cas-path nil (:signature/image value))}]
     [:div (str/join ", " [(:signature/signer value)
                           (:signature/place value)
                           (when-let [dt (:signature/datetime value)]
                             (time-format/->str dt))])]]
    [:div placeholder]))

(defn auxiliary-label [{:as field :ui/keys [auxillary-field-view placeholder options label-key lookup-key]} value]
  (let [lookup-value (some (fn [o] (when (or (= value o)
                                             (when (and (map? o) (contains? o :ui/value))
                                               (or (= value (get-in o [:ui/value] ::nf))
                                                   (= value (get-in o [:ui/value lookup-key] ::nf))))
                                             (when lookup-key (or (= value (get o lookup-key ::nf))
                                                                  (and (vector? value)
                                                                       (= lookup-key (first value))
                                                                       (= (second value) (get o lookup-key ::nf)))
                                                                  (= (get value lookup-key) (get o lookup-key ::nf)))))
                                     o))
                           options)]
    (cond
      (empty? value) (or placeholder (tr "Nothing selected yet"))
      (= auxillary-field-view :ui.view/signature) [signature-preview field value]
      (= auxillary-field-view :ui.view/carrier-ref) [combobox/carrier-choice lookup-value]
      (= auxillary-field-view :ui.view/transporter-ref) [combobox/mobile-transporter-choice lookup-value]
      (map? lookup-value) (get lookup-value label-key)
      (coll? value) (interpose ", " (cond
                                      (or (sequential? value) (set? value))
                                      ;; TODO: Can we use the schema for this?
                                      (if (contains? (first value) :damage/region)
                                        (map :damage/region value)
                                        (map #(tr %) value))
                                      (map? value)
                                      (map #(tr %) (keys value))))
      :else (str value))))

(defn signature-view [path field]
  (r/with-let [!b64-signature (r/atom nil)
               _ (deframe/route-assoc!
                  ::on-auxiliary-save
                  (fn []
                    (deframe/call-lambda {:on-success (fn [result]
                                                        (let [value (assoc (get @(!recorded-attrs (butlast path)) (last path))
                                                                           :signature/image result)]
                                                          (deframe/route-assoc-in! path value)))
                                          :result-path [::_ignore]}
                                         'ductile.damages/upload-image
                                         {:image @!b64-signature})))]
    (let [value (get @(!recorded-attrs (butlast path)) (last path))
          on-change (fn [attr]
                      #(deframe/route-assoc-in! path (assoc value attr (.. % -target -value))))]
      [:<>
       [mobile/list-item
        {:title [mobile/sign-here
                 {:data-testid (str (:ui/attr field) "-signature-pad")
                  :from-image @!b64-signature
                  :on-signature (fn [b64-image]
                                  (reset! !b64-signature b64-image))}]}]
       [mobile/list-item {:data-testid (str (:ui/attr field) "-datetime")
                          :title [:div.flex.items-center.justify-between.text-base
                                  [:div (tr :signature/datetime)]
                                  (time-format/->str (or @(deframe/!route-get-in (vec (conj path :signature/datetime)))
                                                         (js/Date.)))]}]
       [mobile/list-item-input {:input {:data-testid (str (:ui/attr field) "-signer")
                                        :type :text
                                        :value (:signature/signer value)
                                        :on-change (on-change :signature/signer)
                                        :placeholder (tr :signature/signer)}}]
       [mobile/list-item-input {:input {:data-testid (str (:ui/attr field) "-place")
                                        :type :text
                                        :value (:signature/place value)
                                        :on-change (on-change :signature/place)
                                        :placeholder (tr :signature/place)}}]])))

(defn option-value [option]
  (if (and (map? option) (contains? option :ui/value))
    (:ui/value option)
    option))

(defn same-entity? [a b]
  (let [a-id (some-> a :ductile/id)
        b-id (some-> b :ductile/id)]
    (and a-id (= a-id b-id))))

(defn option-matches-value? [selected-value option lookup-key]
  (let [value (option-value option)]
    (or (= selected-value option)
        (= selected-value value)
        (same-entity? selected-value value)
        (and lookup-key
             (= selected-value (get value lookup-key ::nf)))
        (and lookup-key
             (= selected-value (get option lookup-key ::nf)))
        (and (set? selected-value)
             (or (contains? selected-value option)
                 (contains? selected-value value))))))

(defn ref-view [value path {:as field :ui/keys [attr choice-view options lookup-key label-key component mobile? placeholder readonly? style choices-style update-attrs-fn]}]
  (let [selected-value (cond (and (vector? value)
                                  lookup-key
                                  (= lookup-key (first value)))
                             (second value)
                             (and (map? value)
                                  (contains? value lookup-key))
                             (get value lookup-key)
                             :else
                             value)
        get-label (fn [the-entity k]
                    (or (some #(get the-entity %) (if (sequential? k) k [k]))
                        the-entity))
        selected-entity (some (fn [option]
                                (when (option-matches-value? selected-value option lookup-key)
                                  option))
                              options)
        ->choice (fn [entity]
                   (if (and (map? entity) (contains? entity :ui/value))
                     entity
                     (let [label (get-label entity label-key)]
                       {:ui/value entity
                        :ui/label (if (keyword? label) (tr label) label)})))]
    (if (#{:readonly-values} style)
      (choice-view selected-entity)
      [combobox/view
       (cond-> {:variant :flex
                :placeholder (or placeholder (if (some? selected-entity) (get-label selected-entity label-key) (tr "Unassigned")))
                :value (->choice selected-entity)
                :disabled? readonly?
                :data-testid (pr-str attr)
                :choices (map ->choice options)
                :extra-keys [:address/name]
                :choice-view choice-view
                :on-clear #(deframe/route-assoc-in! path nil)
                :on-change (fn [{:ui/keys [value]}]
                             (deframe/route-assoc-in! path (if lookup-key
                                                             [lookup-key (get value lookup-key)]
                                                             value))
                             (when update-attrs-fn
                               (deframe/route-update-in! (pop path) update-attrs-fn)))
                ;; Check when touching this:
                ;; editing locations: country picker should show desktop popover dropdown
                ;; gate book: "add exit load" should show mobile choices
                ;; gate book "record transport" wizard: should show mobile choices
                :ui/mobile? (or (= :style/mobile choices-style)
                                (if style false mobile?))}
         style (assoc :ui/style style)
         (:combobox/new-option-form-handler field)
         (assoc :combobox/new-option-form-handler (:combobox/new-option-form-handler field)
                :combobox/new-option-prefill-key (if (sequential? label-key) (first label-key) label-key)
                :combobox/new-option-on-pop (fn [{:keys [result]}]
                                              (when-let [created-id (and lookup-key (:created-id result))]
                                                (deframe/route-assoc-in! path [lookup-key created-id])))))])))

(defn toggle-view [value path {:as field :ui/keys [label]}]
  [:div.text-center.p-3.-mt-6.select-none {:class mobile/bg-page}
   [ui/link {:on-click (fn [] (deframe/route-update-in! path not))}
    (tr (get label (boolean value)))]])

(defn carrier-ref-view [value path field]
  [ref-view value path (assoc field :ui/choice-view combobox/carrier-choice)])

(defn transporter-ref-view [value path field]
  [ref-view value path (assoc field :ui/choice-view combobox/mobile-transporter-choice)])

(defn transport-view [value path field]
  [ref-view value path (assoc field
                              :ui/lookup-key :ductile/id
                              :ui/lookup-value-type :uuid
                              :ui/label-key [:transport/number :transport/name]
                              :ui/mobile? true
                              :ui/choices-style :style/mobile
                              :ui/choice-view combobox/transport-choice
                              :ui/placeholder (tr "Search for transport name, load id, VIN or license plate"))])

(defn transporter-or-carrier-ref-view [value path field]
  [ref-view value path (assoc field :ui/choice-view combobox/transporter-or-carrier-choice)])


(defn location-ref-view [value path {:as field :ui/keys [attr options lookup-key style readonly? overridden? country-code]}]
  (let [country-code (cond
                       (= :all country-code) nil ;; :all means no country filter
                       (some? country-code) country-code
                       :else "DEU") ;; default to DEU for backward compatibility
        component-key (conj path attr)
        ; options (populated by server on initial form load) might not contain a newly selected value that came in later from server via autocomplete,
        ; so store any selected value just in case (but prefer value prop)
        ; cache needs to be route-global (not depedent on field path) for access across batch table + modal views
        extra-options-path [::lookup-ref-options-cache]
        resolved-value (util/find-by-lookup-ref (into options @(deframe/!route-get-in extra-options-path)) value)]
    [:div.inline-flex.w-full.justify-start
     (if (#{:readonly-values} style)
       [:div.flex-grow
        {:class (when overridden? "line-through")}
        [vehicle-call-off/item-view resolved-value]]
       [ui/locations-autocomplete
        {:component-key path
         :data-testid (str attr)
         :filter (fn [{:keys [search-text search-by-dealer-code?]}]
                   (deframe/call-lambda
                    {:result-path (ui/locations-autocomplete-path path [:filtered-items])}
                    'ductile.locations/search*
                    {:search-params
                     (cond-> {}
                       country-code
                       (assoc :country/code country-code)
                       (and search-by-dealer-code? (= "DEU" country-code))
                       (assoc :external.bmw/location-codes {:ductile.entity-search/regex search-text})
                       (and search-by-dealer-code? (= "ITA" country-code))
                       (assoc :external.kia/location-codes {:ductile.entity-search/regex search-text})
                       (and (not search-by-dealer-code?) (some-> search-text parse-long))
                       (assoc :address/postcode {:ductile.entity-search/regex search-text})
                       (and (not search-by-dealer-code?) (not (some-> search-text parse-long)))
                       (assoc :address/name {:ductile.entity-search/regex (str "(?i)" search-text)}))}))
         :input-placeholder (str (tr "Type to filter") "…")
         :button-placeholder [:div.flex.items-center.gap-2
                              (str (tr "Click to select") "…")
                              [ui/icon {:size 12} :map]]
         :value resolved-value
         :readonly? readonly?
         :overridden? overridden?
         :on-clear (fn []
                     (deframe/route-assoc-in! path :ductile/retract))
         :on-select-item (fn [selected-item]
                           (deframe/route-update-in! extra-options-path (fnil conj #{}) selected-item)
                           (deframe/route-assoc-in! path [lookup-key (get selected-item lookup-key)]))
         :selected-item-view vehicle-call-off/item-view
         :item-view vehicle-call-off/item-view
         :ui/style :style/mobile}])]))


(defn service-select-view [{:as field :ui/keys [options style]} path value]
  (let [single-editing? (uuid? (:ductile/id field))
        bulk-editing? (set? (:ductile/id field))

        options (cond bulk-editing?
                      (->> (vals options)
                           (apply concat)
                           set
                           (sort-by :service/code))

                      single-editing?
                      (get options (:ductile/id field) [{}]))]
    [:div
     (if (#{:readonly-values} style)
       [:div.text-xs
        (into [:ul.list-disc.mx-2.pt-1.text-left]
              (map (fn [service] [:li.mb-1 [ui/service-label service]]))
              value)]

       (into [:div]
             (map (fn [service]
                    [mobile/list-item-checkbox {:label [ui/service-label service]
                                                :input {:checked (contains? value service)
                                                        :data-testid (:service/code service)
                                                        :on-change (fn []
                                                                     (deframe/route-update-in!
                                                                      (vec path)
                                                                      (fn [services]
                                                                        (if (contains? services service)
                                                                          ((fnil disj #{}) services service)
                                                                          ((fnil conj #{}) services service)))))}}]))
             options))]))

(declare render-fields)

(defscene signature
  [render-fields {} [{:ui/attr :entry-protocol/signature
                      :ui/field-view :ui.view/signature
                      :ui/schema-type :map
                      #_#_:ui/schema-children [:signature/image :signature/signer :signature/datetime :signature/place]}]])

(comment
  @(deframe/!route-get-in [::recorded-attrs])
  @(deframe/!route-get-in [::photo-comments])
  @(deframe/!route-get-in [:service/result]))

(defn !recorded-attrs
  ([] (!recorded-attrs nil))
  ([path]
   (r/track (fn []
              (let [attrs @(deframe/!route-get-in (or path [::recorded-attrs]))
                    default-attrs (:service/default-attrs @!selected-service)]
                (merge default-attrs attrs))))))

(defn without-retractions [m]
  (update-vals m (fn [v] (if (= :ductile/retract v) nil v))))

(defn get-value [{:as ui-context :ui/keys [attr schema-type recorded-attrs-path]}]
  (let [dv (:ui/default-value ui-context)
        resolved-default (if (fn? dv) (dv ui-context) dv)
        v (get @(!recorded-attrs recorded-attrs-path) attr
               (get ui-context attr
                    (if (some? resolved-default) resolved-default (default-value schema-type))))]
    (if (= :ductile/retract v) nil v)))

(defn special-equipment-view [{:keys [field path]}]
  (let [selected (equipment-utils/equipment-set->map (get-value field))
        options (equipment-utils/equipment-spec->options (:ui/options field))]
    (into [:<>]
          (map (fn [{:keys [option max]}]
                 (if max
                   [mobile/list-item-checkbox-stepper
                    {:label (tr option)
                     :input {:value (get selected option)
                             :max max
                             :on-change #(deframe/route-assoc-in! path
                                                                  (equipment-utils/equipment-map->set
                                                                   (assoc selected option %)))
                             :data-testid (str (last path) "=" option)}}]
                   [mobile/list-item-checkbox
                    {:label (tr option)
                     :input {:checked (contains? selected option)
                             :on-change #(deframe/route-assoc-in!
                                          path
                                          (equipment-utils/equipment-map->set
                                           (if (contains? selected option)
                                             (dissoc selected option)
                                             (assoc selected option true))))
                             :data-testid (str (last path) "=" option)}}]))
               options))))

(defn transport-label [field]
  [combobox/transport-choice (:ui/transport field) {:ui/inset? false}])

(defn fahrzeug-label [field]
  [combobox/fahrzeug-choice (:ui/fahrzeug field) {:ui/inset? false}])

(defn label-view [view-name field]
  (case view-name
    :ui.label-view/transport [transport-label field]
    :ui.label-view/fahrzeug [fahrzeug-label field]
    [:span.text-red-600 (tr "No label view for view-name: %1" view-name)]))

(defn code-field-view [field]
  (let [path (recorded-attr-path field)]
    [code/editor {:value (if (string? (get-value field))
                           (get-value field)
                           "")
                  :on-change #(deframe/route-assoc-in! path %)
                  :class ["bg-white" "px-4" "pt-2"]}]))

(declare tabs-view)

(defn field-items [{:as field :ui/keys [attr schema-type label labels label-views field-view options options-order placeholder mask unit min max schema-children numeric? mobile? readonly? toggle-all? style]}]
  (use-magic-wand field)
  (let [path (recorded-attr-path field)
        value (get-value field)
        readonly-values? (#{:readonly-values} style)]
    (case field-view
      :ui.view/checkbox
      [[mobile/list-item-checkbox {:label (if label (tr label) (tr attr))
                                   :input {:checked (true? value)
                                           :on-change #(deframe/route-assoc-in! path (not value))
                                           :data-testid (str attr "=" (boolean value))}}]]
      :ui.view/radio-list
      (mapv (fn [option]
              (let [[option label] (if (and (map? option) (contains? option :ui/value))
                                     [(:ui/value option) (:ui/label option)]
                                     [option nil])
                    label-view (when-let [view-name (get label-views option)]
                                 [label-view view-name field])
                    label (or label
                              (case schema-type
                                :enum (or label-view (tr (get labels option option)))
                                :boolean (or label-view (some-> (get (merge {true (tr "Yes") false (tr "No")} labels) option) tr))))
                    checked? (= value option)]
                (if readonly-values?
                  (when checked? label)
                  [mobile/radio-button
                   {:label label
                    :description (when (:ui/descriptions field) (tr (get (:ui/descriptions field) option)))
                    :input {:name attr
                            :checked checked?
                            :on-change #(deframe/route-assoc-in! path option)
                            :data-testid (if (boolean? option)
                                           (str attr "=" option)
                                           (str option))}}])))
            (cond-> (case schema-type
                      :enum (or options schema-children)
                      :boolean [true false]
                      options)
              (= options-order :ui.order/reverse) reverse))
      :ui.view/multi-select
      (if readonly-values?
        [(into [:ul.list-disc.mx-2.pt-1.text-left]
               (map (fn [option]
                      [:li.mb-1 (if (:service/code option)
                                  [ui/service-label option]
                                  (tr option))]))
               value)]
        (into (cond-> []
                toggle-all?
                (conj [mobile/list-item-checkbox
                       {:label (tr "All")
                        :input {:checked (every? (fn [o] (contains? value o)) options)
                                :on-change #(deframe/route-assoc-in! path (if (every? (fn [o] (contains? value o)) options) #{} (set options)))
                                :data-testid "all"}}]))
              (map (fn [option]
                     [mobile/list-item-checkbox
                      {:label (if (:service/code option)
                                [ui/service-label option]
                                (tr option))
                       :description (when (:ui/descriptions field)
                                      (tr (get (:ui/descriptions field) option)))
                       :input {:checked (contains? value option)
                               :on-change #(deframe/route-assoc-in! path ((if (contains? value option)
                                                                            disj
                                                                            (fnil conj #{}))
                                                                          value
                                                                          option))
                               :data-testid (str option)}}]))
              options))

      :ui.view/dependent-selects
      [[ui.dependent-selects/dependent-selects {:levels (dependent-selects/levels-from-root
                                                         (:dependent-selects/choice-tree field)
                                                         value
                                                         (:ignore-key? field))
                                                :value value
                                                :on-change #(deframe/route-assoc-in! path %)}]]

      :ui.view/service-select
      [[service-select-view field path value]]
      :ui.view/special-equipment
      [[special-equipment-view {:field field :path path}]]

      :ui.view/code
      [[code-field-view field]]

      (:ui.view/text :ui.view/number :ui.view/textarea :ui.view/currency)
      (let [value' (cond->> value (and (coll? value)
                                       (= :set schema-type)
                                       (= :ui.view/textarea field-view))
                     (str/join "\n"))

            props (merge
                   (when (= field-view :ui.view/textarea)
                     {:component :textarea
                      :rows (:ui/rows field 5)})
                   (when (#{:ui.view/number :ui.view/currency} field-view)
                     {:style {:text-align :right}
                      :input-mode (if (= schema-type :int) "numeric" "decimal")
                      :step (case schema-type
                              :int 1
                              :double ".01"
                              "any")})

                   (if mask ; controlled input
                     {:value value'
                      :on-change (fn [event]
                                   (if-let [value (.. event -target -value)]
                                     (deframe/route-assoc-in! path value)
                                     (deframe/route-assoc-in! path :ductile/retract)))}

                     {:default-value value'
                      :on-blur (fn [event]
                                 (let [value (.. event -target -value)
                                       value (if (and (= :set schema-type)
                                                      (= :ui.view/textarea field-view))
                                               (->> (str/split value #"[ ,;\n]")
                                                    (remove str/blank?)
                                                    vec)
                                               value)]
                                   (deframe/route-assoc-in! path value)))})


                   {:type (if mask :text (name field-view)) ; imask needs text
                    :disabled? readonly?
                    :placeholder (or placeholder
                                     (case schema-type
                                       :int "0"
                                       :double (tr "0.00")
                                       placeholder))
                    :min min
                    :max max
                    :mask mask
                    :right (when unit (tr unit))
                    :data-testid (str attr)})]
        (if readonly-values?
          (case mask
            :ui.mask/currency
            (when value'
              (locale/format-number {:maximumFractionDigits 2 :minimumFractionDigits 2} (/ value' 100)))


            :ui.mask/factor
            (when value'
              (locale/format-number {:maximumFractionDigits 10 :minimumFractionDigits 2} value'))


            :ui.mask/int
            (when value'
              (locale/format-number {:maximumFractionDigits 0 :minimumFractionDigits 0} value'))

            (str value'))

          [[mobile/list-item-input
            {:input props}]]))
      :ui.view/datetime
      (if readonly-values?
        (time-format/->str {:format :datetime :style :short} value)
        [[mobile/list-item
          {:title [mobile/datetime-picker
                   {:days-before (:ui.datetime/days-before field)
                    :days-after (:ui.datetime/days-after field)
                    :value value
                    :on-change #(deframe/route-assoc-in! path %)
                    :data-testid (str attr)}]}]])

      :ui.view/date
      (if readonly-values?
        (time-format/->str {:format :date :style :short} value)
        [[mobile/list-item
          {:title [mobile/date-picker
                   {:days-before (:ui.datetime/days-before field)
                    :days-after (:ui.datetime/days-after field)
                    :value value
                    :on-change #(deframe/route-assoc-in! path %)
                    :data-testid (str attr)}]}]])

      :ui.view/time
      (if readonly-values?
        (time-format/->str {:format :time :style :short} value)
        [[mobile/list-item
          {:title [mobile/time-picker
                   {:value value
                    :on-change #(deframe/route-assoc-in! path %)
                    :data-testid (str attr)}]}]])

      :ui.view/auxiliary
      [[mobile/list-item-disclose {:title [auxiliary-label field value]
                                   :data-testid (str attr)
                                   :on-click #(enter-auxiliary! attr [(set/rename-keys field {:ui/auxillary-field-view :ui/field-view})])}]]
      :ui.view/damages-outside
      [[damages/selector {:path path
                          :diagram :explosion
                          :regions damages/default-outside-regions
                          :kinds damages/default-outside-kinds}]]
      :ui.view/damages-inside
      [[damages/selector {:path path
                          :regions damages/default-inside-regions
                          :kinds damages/default-inside-kinds}]]
      :ui.view/parking-spot
      [[parking-spot-field-view field]]
      :ui.view/fuel-tank-level
      (mapv (fn [{:keys [option label]}]
              [mobile/radio-button {:label label
                                    :input {:checked (= value option)
                                            :on-change #(deframe/route-assoc-in! path option)
                                            :data-testid (str attr "=" option)}}])
            [{:option 100 :label (tr "Full")}
             {:option 75 :label "¾"}
             {:option 50 :label "½"}
             {:option 25 :label "¼"}
             {:option 0 :label (tr "Empty")}])


      :ui.view/photo
      [[photo-comment-field field]]

      :ui.view/photos
      [[multi-photo-field field]]
      #_[[mobile/table {:inset? true
                        :title (tr (:ui/photo-attr field))
                        :items [[photo-comment-field field]]}]]

      :ui.view/file-upload
      [[file-upload-field field]]
      :ui.view/toggle-link
      [[toggle-view value path field]]
      :ui.view/scan-vin
      [[scan-vin-view field]]
      :ui.view/scan
      [[scan-view field]]
      :ui.view/signature
      [[signature-view path field]]
      :ui.view/carrier-ref
      [[carrier-ref-view value path field]]
      :ui.view/transporter-ref
      [[transporter-ref-view value path field]]
      :ui.view/transporter-or-carrier-ref
      [[transporter-or-carrier-ref-view value path field]]
      :ui.view/location-ref
      [[location-ref-view value path field]]
      :ui.view/transport
      [[transport-view value path field]]
      :ui.view/storage-care-start-date-view
      [[storage-care-start-date-view value path field]]
      :ui.view/combobox
      [[ref-view value path field]]
      :ui.view/delivery-modalities
      [[fields/delivery-modalities-field-view field get-value recorded-attr-path]]
      :ui.view/entity-diff
      [[entity-diff-view field]]
      :ui.view/datoms
      [[datoms-view field]]
      :ui.view/tabs
      [[tabs-view field]]
      :ui.view/data
      [[data-view field]]
      :ui.view/vehicle-list
      [[vehicle-list-view field]]
      :ui.view/accessories-preview
      [[accessories-preview-view field]]
      :ui.view/data-table
      [[data-table-view field]]
      :ui.view/vin
      [[ui/vin (get field (:ui/attr field))]])))

(defn tabs-view [{:as field :ui/keys [state-key tabs]}]
  (let [active-attr @(deframe/!route-get state-key (:ui/attr (first tabs)))
        active-field (first (filter (comp #{active-attr} :ui/attr) tabs))]
    [:div.relative
     (into [:div.flex.gap-2.text-xs.absolute.right-4.-mt-6]
           (map (fn [{:ui/keys [attr label]}]
                  [:button.text-indigo-600.hover:underline.cursor-pointer
                   {:on-click #(deframe/route-assoc! state-key attr)
                    :class (when (= attr active-attr) ["font-bold"])}
                   (tr (or label attr))]))
           tabs)
     (first (field-items (-> field (merge active-field) (dissoc :ui/tabs))))]))

(defn humanize-error [error-map]
  ;;TODO nicer error messages matching with malli
  (cond (:signature/image error-map) (tr "Failed to save signature, please sign again.")
        :else (pr-str error-map)
        #_(->> (tree-seq map? vals error)
               (filter vector?)
               (apply concat)
               distinct
               (map tr)
               (str/join ", "))))

(defn example-view [example]
  [:div.mt-1.font-mono {:class ["text-[12px]"]} (str example)])

(defn form-path [form-map]
  (or (:form/path form-map)
      (when (:form/name form-map) [(:form/name form-map)])
      [::recorded-attrs]))

(defn attr-field-view [{:as field :ui/keys [attr example field-view title subtitle attached-to-previous-field? margin-bottom style]}]
  (let [error @(deframe/!route-get-in [:fetch :error :validation/errors attr])]
    (if (and (or attr title)
             (not (#{:readonly-values} style)))
      [mobile/table
       {:title (when-not attached-to-previous-field? [attr-field-label field])
        :separated? (and (not (contains? #{:ui.view/radio-list
                                           :ui.scan/options
                                           :ui.view/fuel-tank-level} field-view))
                         (not attached-to-previous-field?))
        :ui/margin-bottom margin-bottom
        :class (when attached-to-previous-field? "-mt-2")
        :subtitle (when (or subtitle example)
                    [:<>
                     (tr subtitle)
                     (when example [example-view example])])
        :items (field-items field)
        :footer (when error
                  [:div.text-red-500.text-xs {:data-testid (str attr "=error")}
                   (cond
                     (vector? error) (str/join ", " (map tr error))
                     (map? error) (humanize-error error))])
        :data-testid (str "attr-" attr)}]
      (into [:<>]
            (field-items field)))))

(defn dissoc-invalid-fields [ui-context]
  (let [wrong-attrs (into []
                          (comp
                           (filter (fn [{:as field :ui/keys [attr lookup-key]}]
                                     (when (seq (:ui/options field))
                                       (let [recorded-value (get ui-context attr)
                                             valid-option (first (filter (fn [o]
                                                                           (option-matches-value? recorded-value o lookup-key))
                                                                         (:ui/options field)))]
                                         (and (not (vector? recorded-value)) ; ignore refs
                                              (not (some? valid-option)))))))
                           (map :ui/attr))
                          (:ui/fields ui-context))]
    (apply dissoc ui-context wrong-attrs)))

(defn field-active? [ui-context {:as field :ui/keys [pred]}]
  (or (not pred) (pred ui-context)))

(defn render-fields-inner
  [{:as ui-context :ui/keys [mobile? recorded-attrs-path style]
    :or {mobile? true}}
   fields]
  (into [:<>]
        (for [{:as field :ui/keys [view pred prompt progress-bar table todos header hidden?]} fields
              :when (not hidden?)]
          (when (field-active? ui-context field)
            (with-meta (case view
                         :ui.view/coordinate-picker [coordinate-picker-field-view (merge ui-context field)]
                         (cond
                           (#{:fahrzeug} header) [fahrzeug-view (merge ui-context field)]
                           prompt [prompt-view field]
                           progress-bar [progress-bar-view field]
                           table [table-view ui-context field]
                           todos [todos-view field]
                           :else [attr-field-view (merge ui-context field {:ui/mobile? mobile? :ui/style style})]))

              {:key field})))))

(defn- scroll-focused-field-into-view [^js e]
  (when (= "Tab" (.-key e))
    (let [form-fields (.-currentTarget e)]
      (js/requestAnimationFrame
       (fn []
         (let [active-el js/document.activeElement]
           (when (.contains form-fields active-el)
             (ui/scroll-into-view-with-margin! active-el))))))))

(defn render-fields [ui-context fields]
  (let [auxiliary-fields (auxiliary-fields)]
    (auxiliary-focus-effect auxiliary-fields)
    (if auxiliary-fields
      [:div.auxiliary-view.form-fields
       {:on-key-down scroll-focused-field-into-view
        :tab-index -1}
       [render-fields-inner ui-context auxiliary-fields]]
      [:div.form-fields.focus:outline-none
       {:on-key-down scroll-focused-field-into-view
        :tab-index -1}
       [render-fields-inner ui-context fields]])))

(defscene field-special-equipment-auxiliary
  [render-fields {} [{:ui/attr :vehicle/equipment
                      :ui/optional? true
                      :ui/schema-type :set
                      :ui/field-view :ui.view/auxiliary
                      :ui/children [{:ui/attr :vehicle/equipment
                                     :ui/schema-type :enum
                                     :ui/field-view :ui.view/special-equipment-view
                                     :ui/optional? true
                                     :ui/options ["Fahrzeugschein"
                                                  "1 Schlüssel"
                                                  "2 Schlüssel"
                                                  "1 Kennzeichen"
                                                  "2 Kennzeichen"
                                                  "1 Ladekabel"
                                                  "2 Ladekabel"
                                                  "Zweiter Radsatz"
                                                  "Keycard"
                                                  "Displaykey"]}]}]])

(defscene field-damages-outside
  [render-fields {} [{:ui/attr :vehicle/damages-outside
                      :ui/optional? true
                      :ui/schema-type :sequential
                      :ui/field-view :ui.view/auxiliary
                      :ui/children [{:ui/attr :vehicle/damages-outside
                                     :ui/optional? true
                                     :ui/schema-type :map
                                     :ui/field-view :ui.view/damages-outside}]}]])

(defn info-view [info]
  [:div.bg-sky-100.border-t.border-b.py-2.px-3.-mt-3.first:mt-0.mb-3
   {:data-testid "info"}
   [:div.flex.gap-3
    [:div.rounded-full.border-2.border-white.shrink-0.text-blue-500.flex.items-center.justify-center.bg-white.shadow
     {:class ["w-[30px]" "h-[30px]"]}
     [ui/icon {:size 30} :info]]
    [:div.text-sm
     (:info/message info)]]])

(defn map-values->lookup-refs
  "Converts map values for fields that are `:ui/schema-type` `:ref` to lookup refs using `:ui/lookup-key`"
  [fields submittable-attrs]
  (let [lookup-fields (filter (comp #{:ref} :ui/schema-type) fields)
        attr->lookup-key (into {} (map (juxt :ui/attr :ui/lookup-key)) lookup-fields)
        lookup-attrs (set (map :ui/attr fields))]
    (reduce (fn [updated-attrs k]
              (let [v (get submittable-attrs k ::nf)
                    lookup-key (attr->lookup-key k)]
                (if (and (contains? lookup-attrs k)
                         lookup-key
                         (map? v)
                         (contains? v lookup-key))
                  (assoc updated-attrs k [lookup-key (get v lookup-key)])
                  updated-attrs)))
            submittable-attrs
            lookup-attrs)))

(defn form-context [form-map]
  (merge (:workflow-map form-map)
         (dissoc form-map :workflow-map)))

(defn submittable-params [{:as form-map :ui/keys [fields]}]
  (let [path (form-path form-map)
        context-form-map (form-context form-map)
        field-defaults (into {} (comp (filter #(contains? % :ui/default-value))
                                      (map (fn [{:ui/keys [attr default-value]}]
                                             [attr (if (fn? default-value) (default-value context-form-map) default-value)])))
                             fields)
        attrs (merge field-defaults
                     context-form-map
                     (:service/default-attrs form-map)
                     @(deframe/!route-get-in path))]
    (if (:form/bulk-rows form-map)
      (let [bulk-fields (or (:form/bulk-cols form-map)
                            (:ui/fields form-map))
            bulk-attrs (map :ui/attr bulk-fields)
            bulk-key (or (:form/bulk-key form-map)
                         (:ui/attr (first bulk-fields)))]
        (into {}
              (map
               (fn [row]
                 {(bulk-key row)
                  (->> (assoc
                        (select-keys (merge row
                                            (get @(deframe/!route-get-in path) (bulk-key row)))
                                     bulk-attrs)
                        bulk-key (bulk-key row))
                       (map-values->lookup-refs fields))}))
              (:form/bulk-rows form-map)))
      (let [active-attrs (into [:ductile/id]
                               (comp (filter (partial field-active? attrs))
                                     (map :ui/attr))
                               fields)]
        (->> (select-keys attrs active-attrs)
             (map-values->lookup-refs fields))))))


(defn on-scan-record-attr [{:as scan :scan/keys [attr result classified ocr-details]}]
  (if (and (:service/augment-scan? @!selected-service)
           (not (contains? scan :scan/ocr-details)))
    ;; barcode scan
    ;; NOTE: barcode scans aren't linked with the service being performed, but
    ;; we don't need this because they don't have errors like OCR scans
    (deframe/call-lambda
     'ductile.workflow/fahrzeug-with-scan-match
     (merge
      (scanner/fetch-device-location!)
      (select-keys @!fahrzeug [:compound/name :ductile/id :workflow/type :arrival-scan-v2])
      (select-keys @!selected-service [:service/code :service/template])
      scan))
    ;; OCR scan
    (deframe/tx! (vec (concat [[:op/assoc-in
                                [:current-route/transient ::recorded-attrs attr]
                                (get classified attr)]]
                              ;; for linking the scan with the (entry) service being performed
                              [[:op/assoc-in
                                [:current-route/transient ::recorded-attrs :scan/id]
                                (-> ocr-details :scan :ductile/id)]]
                              [(if ocr-details
                                 [:op/assoc-in [:current-route/transient ::ocr-details] ocr-details]
                                 [:op/dissoc-in [:current-route/transient ::ocr-details]])]))))
  (deframe/reset-current-scan!))

(defn undo-service-view [fahrzeug service]
  [:div.rounded-md.border.border-gray-300.bg-gray-100.shadow.m-2.p-2.flex.items-center.justify-between
   [:div
    [:div.text-slate-500 {:class ["text-[11px]"]} (tr "Recently completed")]
    [:div.font-bold {:class ["text-[12px]"]}
     [ui/service+details service]]]
   [ui/button
    {:type :button
     :variant :primary
     :data-testid "undo-last-service-button"
     :on-click (fn []
                 (deframe/call-lambda
                  {:on-success (fn []
                                 (deframe/route-dissoc! ::recorded-attrs)
                                 (when @(deframe/!route-get ::ocr-details)
                                   (deframe/route-dissoc! ::ocr-details))
                                 (deframe/route-dissoc! ::cancel-fields?))}
                  'ductile.workflow/undo-last-own-event
                  {:ductile/id (:ductile/id fahrzeug)
                   :compound/name (:compound/name fahrzeug)}))}
    [ui/icon {:size 14} :rotate-ccw]
    (tr "Undo")]])

(defn storage-location-header [m]
  (let [location (:storage/location m)
        {:shelf/keys [row place]} location]
    [:div.rounded-md.border.border-gray-300.bg-gray-100.shadow.m-2.p-2
     [:span.text-slate-500 {:class ["text-[11px]"]} (tr "Current Location") " "]
     [:span.font-bold {:class ["text-[12px]"]} (format-storage-location location)]]))

(defscene storage-location-header-card
  (let [wheel-set {:storage/location {:shelf/row "A" :shelf/place 3}}]
    [mobile/scroll-content
     [mobile/navbar {:title "Eingangsinspektion" :back [mobile/back-link]}]
     [storage-location-header wheel-set]
     [mobile/table {:title "License Plate" :items [[:div.h-10]]}]]))

;; If there is a manual VIN Scan edit in progress, the service's confirm button
;; should be disabled.
(def !confirm-disabled
  (r/track (fn []
             (let [fields (:ui/fields @!selected-service)
                   scan-vin-field (some-> (and (seq fields)
                                               (first (filter
                                                       #(contains? #{:ui.view/scan-vin}
                                                                   (:ui/field-view %))
                                                       fields)))
                                          (merge @!fahrzeug))]
               (or @deframe/!loading?
                   @(deframe/!route-get ::submitting?)
                   (and scan-vin-field
                        @!manual-vin-entry?
                        (contains? #{:empty :incomplete}
                                   (:state @(!vin-edit (scan-vin-field-value scan-vin-field))))))))))

(defn classify-scan-for-service [service scan-string]
  (or (some (fn [{:keys [ui/attr ui.scan/match-scan-values ui.scan/match-scan-fn]}]
              (cond
                (ifn? match-scan-fn)
                (when-let [result (match-scan-fn scan-string)]
                  {attr result})

                (seq match-scan-values)
                (when (contains? match-scan-values scan-string)
                  {attr scan-string})))
            (:ui/fields service))
      (scanner/classify-scan scan-string)))

(comment
  (classify-scan-for-service {:ui/fields [{:ui/attr :sixt.tracker/serial-number
                                           :ui.scan/match-scan-fn (fn [s] (when (re-matches #"\d{15}" s)
                                                                            (js/parseInt s)))}]}
                             "123456789012345")
  ;; => {:sixt.tracker/serial-number 123456789012345}

  (classify-scan-for-service {:ui/fields [{:ui/attr :sixt.tracker/serial-number
                                           :ui.scan/match-scan-fn (fn [s] (re-matches #"\d{15}" s))}]}
                             "123")
  ;; => {:error :workflow.error/unable-to-classify-scan, :scan "123", ...}

  ;; Deterministic order example (first matching field wins):
  (classify-scan-for-service {:ui/fields [{:ui/attr :special/id
                                           :ui.scan/match-scan-fn (fn [s] (when (= s "SPECIAL") :special))}
                                          {:ui/attr :sixt.tracker/serial-number
                                           :ui.scan/match-scan-fn (fn [s] (re-matches #"\d+" s))}]}
                             "SPECIAL")
  ;; => {:special/id :special}
  )

(defn record-attrs-view [workflow-map {:keys [service]}]
  [mobile/layout
   {:component :form
    :on-submit
    (fn [e]
      (.preventDefault e)
      ;; Ignore submit when a form modal is open on the view stack (e.g. "Create new" from combobox)
      (when-not (#{:form :form-direct} (:view/type (peek @(deframe/!route-get ::view-stack))))
        (.. js/document -activeElement blur)
        ;; Used for UI Tests to prevent Playwright from clicking the same submit button twice
        ;; when the test script has two subsequent confirm button clicks (e.g. one for the auxiliary
        ;; view and one for the main confirm)
        (deframe/route-assoc! ::submitting? true)
        (js/requestAnimationFrame
         (fn []
           (deframe/route-assoc! ::submitting? false)
           (if (auxiliary-fields)
             (do (when-let [on-auxiliary-save! @(deframe/!route-get ::on-auxiliary-save)]
                   (on-auxiliary-save!)
                   (deframe/route-dissoc! ::on-auxiliary-save))
                 (exit-auxiliary!))
             (deframe/call-lambda
              {:on-success #(do
                              (deframe/route-dissoc! ::recorded-attrs)
                              (when @(deframe/!route-get ::ocr-details)
                                (deframe/route-dissoc! ::ocr-details))
                              (deframe/route-dissoc! ::cancel-fields?))}
              (if @(deframe/!route-get ::cancel-fields?)
                'ductile.workflow/cancel-service
                (:service/on-perform service 'ductile.workflow/perform-service))
              (cond-> (merge
                       (scanner/fetch-device-location!)
                       (select-keys workflow-map [:compound/name :ductile/id :workflow/type :arrival-scan-v2 :use-gate-control])
                       {:service/code (:service/code service)
                        :service/attrs (dissoc (submittable-params (merge workflow-map service)) :ductile/id)
                        :compound/name (:compound/name workflow-map)})
                @(deframe/!route-get :service/result)
                (assoc :service/result @(deframe/!route-get :service/result)))))))))}
   (when (:service/scannable? service)
     [with-hardware-scanner {:view `record-attrs-view
                             :on-scan on-scan-record-attr
                             :classify-scan (partial classify-scan-for-service service)}])
   (let [{:navbar/keys [back right title]} (:ui/navbar service)]
     [mobile/navbar (cond-> {:title (tr (or title (:service/code service)))
                             :back (when-not (false? back)
                                     [service-view-backlink workflow-map])
                             :right-item (when-not (false? right)
                                           [workflow-menu-dropdown])}
                      (:ui/subtitle service)
                      (assoc :subtitle (tr (:ui/subtitle service))))])
   [mobile/content
    (when-let [error @(deframe/!route-get-in [:fetch :error])]
      [expected-error-view error])

    (when (contains? workflow-map :vehicle/vin)
      [fahrzeug/vehicle-details-header workflow-map])

    ;;better predicate
    (when (contains? workflow-map :storage/location)
      [storage-location-header workflow-map])

    (when (contains? workflow-map :workflow/service-to-undo)
      [undo-service-view workflow-map (:workflow/service-to-undo workflow-map)])

    ;; TODO: show expected errors & exceptions
    [render-fields
     (merge workflow-map
            @(!recorded-attrs)
            {::service service})
     (if @(deframe/!route-get ::cancel-fields?)
       (:ui/cancel-fields service)
       (:ui/fields service))]
    (when (and (not @(deframe/!route-get ::cancel-fields?))
               (:ui/cancel-fields service))
      [:div.text-center.text-indigo-600.mt-3.cursor-pointer
       {:on-click #(do (.preventDefault %)
                       (.stopPropagation %)
                       (deframe/route-assoc! ::cancel-fields? true))
        :data-testid "cancel-service"}
       "Durchführung nicht möglich?"])]

   (when-not (:ui/hide-perform-button? service)
     (if (auxiliary-fields)
       [:div.sticky.bottom-0.z-10
        [footer {:type :submit
                 :disabled @!confirm-disabled
                 :hide-camera? true
                 :label (tr "Save")}]]
       (let [cancel-fields? @(deframe/!route-get ::cancel-fields?)]
         [footer {:type :submit
                  :disabled @!confirm-disabled
                  :hide-camera? (not (:service/scannable? service))
                  :variant (when cancel-fields? :destructive)
                  :label (if cancel-fields?
                           (tr "Cancel service")
                           (or (some-> (:service/confirm-label service) tr) (tr "Confirm")))}])))])

(defn on-scan-coupling-license-plate [scan]
  (when scan
    (when-let [vin (get-in scan [:scan/classified :vehicle/vin])]
      (deframe/route-assoc-in! [::recorded-attrs :vehicle/vin] (get-in scan [:scan/classified :vehicle/vin])))
    (when-let [ars-id (get-in scan [:scan/classified :ars/id])]
      (deframe/route-assoc-in! [::recorded-attrs :ars/id] (get-in scan [:scan/classified :ars/id])))
    (deframe/reset-current-scan!)))



(def dc-fzg
  {:compound/name "Eitting"
   :customer/name "Schwarz Beschaffungs AG"
   :vehicle/vin "WBA31FZ080FP38946"
   :vehicle/model "320d xDrive Touring"
   :make/name "BMW"
   :vehicle/make "BMW"})

(defscene record-coupling
  :params {:service {:service/code (tr "Entry")
                     :service/scannable? true
                     :ui/fields [{:ui/prompt (tr "Please scan the ARS-ID with the Hardware Scanner")
                                  :ui/icon :scan}
                                 {:ui/attr :vehicle/vin
                                  :ui/field-view :ui.view/scan
                                  :ui.scan/prompt (tr "Please scan the VIN")
                                  :ui.scan/success (tr "VIN detected")
                                  :ui.scan/allow-manual-entry? true
                                  :ui/placeholder (tr "Enter VIN (minimum 7 characters)")}
                                 {:ui/attr :ars/id
                                  :ui/field-view :ui.view/scan
                                  :ui.scan/prompt (tr "Please scan the ARS-ID")
                                  :ui.scan/success (tr "ARS-ID detected")}]}}
  [params]
  [record-attrs-view {:compound/name "Eitting"} params])

(defscene record-parking-spot
  :params {:fahrzeug (assoc dc-fzg :compound/name "Eitting")
           :service {:service/code "Parken"
                     :service/preds [(constantly true)]
                     :service/optionality :optionality/can
                     :service/default-attrs {:parking-spot/zone "Quarantäne SP3"}
                     :ui/fields [{:ui/prompt (tr "Bring the car to the parking spot")
                                  :ui/icon :car}
                                 {:ui/attr :vehicle/parking-spot
                                  :ui/field-view :ui.view/parking-spot}]}}
  [params]
  [record-attrs-view (:fahrzeug params) params])

(defscene record-canceled-entry
  :params {:fahrzeug dc-fzg
           :service {:service/code "Abbruch Eingangsprozess"
                     :ui/fields [{:ui/prompt "Eine nicht entstromte Hochvoltbatterie ist ein Sicherheitsrisiko. Bitte leiten sie unverzüglich die Abholung nach Garching ein."}]}}
  [params]
  [record-attrs-view (:fahrzeug params) params])

(defscene cancel-fields
  :params {:fahrzeug dc-fzg
           :service {:service/code "Batterie 12V messen"
                     :ui/fields [{:ui/attr :vehicle.status/battery-voltage
                                  :ui/schema-type :double
                                  :ui/field-view :ui.view/number
                                  :ui/mask :ui.mask/voltage-12v
                                  :ui/unit :unit/volt}]
                     :ui/cancel-fields [{:ui/prompt (tr "Cancel the service only if it cannot be performed.")}]}}
  [params]
  [record-attrs-view (:fahrzeug params) params])

(defscene record-battery-charge
  :params {:fahrzeug dc-fzg
           :service {:service/code "Batterie 12V messen"
                     :ui/fields [{:ui/attr :vehicle.status/battery-charge
                                  :ui/schema-type :double
                                  :ui/mask :ui.mask/voltage-12v
                                  :ui/field-view :ui.view/number
                                  :ui/unit :unit/volt}]}}
  [params]
  [record-attrs-view (:fahrzeug params) params])

(defscene record-vin
  :params {:fahrzeug dc-fzg
           :service {:service/code (tr "VIN")
                     :ui/fields [{:ui/attr :vehicle/vin
                                  :ui/schema-type :string
                                  :ui/mask :ui.mask/vin
                                  :ui/field-view :ui.view/text}]}}
  [params]
  [record-attrs-view (:fahrzeug params) params])

(defscene record-hv-battery-charge
  :params {:fahrzeug dc-fzg
           :service {:service/code "Hochvoltbatterie messen"
                     :ui/fields [{:ui/attr :vehicle.status/high-voltage-battery-charge
                                  :ui/schema-type :double
                                  :ui/mask :ui.mask/hv-charge
                                  :ui/field-view :ui.view/number
                                  :ui/unit :unit/kwh}]}}
  [params]
  [record-attrs-view (:fahrzeug params) params])

(defscene record-mileage
  :params {:fahrzeug dc-fzg
           :service {:service/code "Kilometerstand"
                     :ui/fields [{:ui/attr :vehicle/mileage
                                  :ui/schema-type :int
                                  :ui/mask :ui.mask/mileage
                                  :ui/field-view :ui.view/number
                                  :ui/unit :unit/km}]}}
  [params]
  [record-attrs-view (:fahrzeug params) params])

(defn truck-entry [_result {:as match :keys [path-params]}]
  [arrival-vin-scan (assoc path-params :entry/mode :mode/truck)])

(defn own-wheels-entry [_result {:as match :keys [path-params]}]
  [arrival-vin-scan (assoc path-params :entry/mode :mode/own-wheels)])

(defn train-entry [_result {:as match :keys [path-params]}]
  [arrival-vin-scan (assoc path-params :entry/mode :mode/train)])

(defn sequential-services-view [fahrzeug {:keys [service]}]
  (let [active-service (first (:service/services service))]
    [(:ui/view active-service) fahrzeug {:service active-service}]))

(defn entry-services-view
  [fahrzeug {:keys [service] :as opts}]
  (if (:service/take-first-child-service-as-active? service)
    [sequential-services-view fahrzeug opts]
    [compound-view fahrzeug opts]))

(defn store-wizard-state-in-url-maybe! [m]
  (when-let [new-ductile-base64-edn (and (contains? m :wizard/shared-state)
                                         (base64/encode (pr-str (:wizard/shared-state m))))]
    (when (not= new-ductile-base64-edn
                (:ductile/base64-edn (deframe/current-route-params)))
      (let [new-path (router/path (let [template (router/path->template (:template @deframe/!current-route))]
                                    (cond-> template
                                      (not (some #{:ductile/base64-edn} template))
                                      (conj :ductile/base64-edn)))
                                  (assoc (deframe/current-route-params) :ductile/base64-edn new-ductile-base64-edn))]
        (.pushState js/window.history nil "" new-path)))))

(defn step-preview [wizard {:as step :step/keys [idx code completed? active? summary]}]
  [:div {:class "relative md:flex md:flex-1 group"}
   [:div {:class "flex items-center px-3 py-3 text-sm font-medium", :aria-current "step"}
    (if completed?
      [:span {:class "flex size-8 shrink-0 items-center justify-center rounded-full bg-green-600"}
       [:svg {:class "size-5 text-white", :viewBox "0 0 24 24", :fill "currentColor", :aria-hidden "true", :data-slot "icon"}
        [:path {:fill-rule "evenodd", :d "M19.916 4.626a.75.75 0 0 1 .208 1.04l-9 13.5a.75.75 0 0 1-1.154.114l-6-6a.75.75 0 0 1 1.06-1.06l5.353 5.353 8.493-12.74a.75.75 0 0 1 1.04-.207Z", :clip-rule "evenodd"}]]]
      [:span {:class (concat ["flex" "size-8" "shrink-0" "items-center" "justify-center" "rounded-full" "border-2"]
                             (if active?
                               ["border-indigo-600" "bg-indigo-600"]
                               ["border-gray-500"]))}
       [:span {:class (if active? ["text-white"] ["text-gray-500"])} (inc idx)]])
    [:div {:class ["ml-4"]}
     [:span {:class (concat ["text-sm"]
                            (if active?
                              ["text-indigo-600" "font-bold"]
                              ["text-gray-500" "font-medium"]))} (tr code)]
     (when (and completed? summary)
       (let [summary-data (get-in (:wizard/shared-state wizard) (:summary/path summary))]
         [:div.text-gray-500.leading-tight.mt-1
          {:class ["text-[11px]"]}
          (case (:summary/format summary)
            :format/count (trn (:summary/label summary) (count summary-data))
            (str summary-data))]))]]])

(defn step-form [wizard {:as step :step/keys [idx code header description]}]
  [:div.bg-gray-100.-mt-5.relative.rounded-b-lg
   [:div.pt-5.bg-gray-200.sticky.top-0.z-20
    [:div.text-lg.font-bold.flex.gap-3.p-3.bg-white.rounded-t-lg.border.border-gray-300
     [:span {:class (concat ["flex" "size-8" "shrink-0" "items-center" "justify-center" "rounded-full" "border-2" "group-hover:border-indigo-600" "text-sm" "font-normal"]
                            ["border-indigo-600" "bg-indigo-600"])}
      [:span {:class ["text-white"]} (inc idx)]]
     [:div
      [:div (tr (or header code))]
      (when description
        [:div.text-xs.mt-1.font-normal description])]]]
   (when-let [error (:wizard/error wizard)]
     [expected-error-view error])
   [:div.rounded-b-lg.border.border-t-0.border-gray-300 {:style {:contain "paint"}}
    [render-fields (merge (:wizard/shared-state wizard)
                          wizard
                          @(!recorded-attrs))
     (:ui/fields step)]
    (if (auxiliary-fields)
      [:div.sticky.bottom-0.z-10
       [footer {:disabled @!confirm-disabled
                :on-click (fn [event]
                            (.preventDefault event)
                            (.stopPropagation event)
                            (when-let [on-auxiliary-save! @(deframe/!route-get ::on-auxiliary-save)]
                              (on-auxiliary-save!)
                              (deframe/route-dissoc! ::on-auxiliary-save))
                            (exit-auxiliary!))
                :hide-camera? true
                :label (tr "Save")}]]
      (when-not (:step/final? step)
        [:div.p-3.sticky.bottom-0.z-10.bg-gray-100.border-t.rounded-b-lg
         [mobile/button-full {:loading @deframe/!loading?
                              #_#_:loading @(deframe/!get ::deframe/current-workflow-fahrzeug)
                              :on-click (fn [_] (deframe/call-lambda
                                                 {:on-result (fn [x]
                                                               (when (:wizard/error x)
                                                                 (ui/scroll-into-view-with-margin! ".expected-error" {:block "center"}))
                                                              ; Return focus to "just before" first form element (but don't focus it directly, we don't want the possibly confusing visible-focus hightlight)
                                                               (some-> (js/document.querySelector ".form-fields") .focus))}
                                                 'ductile.lambda/perform-wizard-step
                                                 (merge (:wizard/shared-state wizard)
                                                        (select-keys step [:step/code :step/idx])
                                                        {:wizard/name (:wizard/name wizard)
                                                         :step/attrs (-> (submittable-params (merge (:wizard/shared-state wizard) step))
                                                                         (dissoc :ductile/id))})))}
          (tr "Perform")]]))]])

(comment
  (ductile.ui.routes/path->template (str (:template @deframe/!current-route)))
  (router/path->template (str (:template @deframe/!current-route)))

  (router/path '[foo bar :ductile/base64-edn]
               (assoc (deframe/current-route-params) :ductile/base64-edn "foo")))

(defn store-workflow-state-in-url-maybe! [m]
  (let [url-keys (filterv keyword? (:workflow/route-path m))
        new-path (router/path (:workflow/route-path m) (select-keys m url-keys))
        old-path (str (.-pathname (.-location js/window)))]
    (when (and (string? new-path) (not= new-path old-path))
      (.pushState js/window.history nil "" new-path))))

(defc wizard [wizard _match]
  (store-wizard-state-in-url-maybe! wizard)
  (let [active-step (first (filter :step/active? (:wizard/steps wizard)))]
    [:div
     (when (ui/debug-mode?)
       [:pre.w-full.p-4.center (with-out-str (pprint/pprint (:wizard/shared-state wizard)))])
     [:div.max-w-4xl.mx-auto.flex.gap-3
      [:div.relative {:class ["w-[250px] shrink-0"]}
       [:div.sticky.top-5
        (into [:div.flex.flex-col.bg-white.rounded-lg.border.border-gray-300.divide-y.divide-y-300]
              (map (fn [step]
                     [step-preview wizard step]))
              (:wizard/steps wizard))]]
      [:div {:style {:width "calc(100% - 250px)"}}
       [step-form wizard active-step]]]]))

(defn entity-table [entity-ductile-id attrs+vals]
  [:table.text-xs.w-full
   [:thead.text-left.bg-white
    [:tr.border-b.border-gray-300
     [:th.pl-2.pr-1.border-r.py-1.border-gray-300 (tr "Tx")]
     [:th.pl-2.pr-1.border-r.py-1.border-gray-300 (tr "Date")]
     [:th.pl-2.pr-1.border-r.py-1.border-gray-300 (tr "Attribute")]
     [:th.px-1.py-1 {:class "border-gray-300"} (tr "Value")]]]
   (into [:tbody]
         (map (fn [{:keys [datetime tx a v]}]
                [:tr.border-b.border-gray-300.bg-white
                 [:td.pl-2.pr-1.border-r.border-gray-300.font-mono
                  [:a {:href (str "/entity-history/" entity-ductile-id "/" tx)} (str tx)]]
                 [:td.pl-2.pr-1.border-r.border-gray-300.font-mono [render/inspect datetime]]
                 [:td.pl-2.pr-1.border-r.border-gray-300.font-mono (str a)]
                 [:td.px-1 {:class "border-gray-300"} [render/inspect v]]]))
         attrs+vals)])

(defc entity-history [data match]
  (let [entity-id (-> match :parameters :path :entity/id)
        tx (-> match :parameters :path :tx)]
    [:div {:style {:width "calc(100% - 250px)"}}
     [:div.rounded-b-lg.border.border-t-0.border-gray-300 {:style {:contain "paint"}}
      (when tx
        [:<>
         [:h3 (str "At tx " tx)]
         [:div [:a {:href (str "/entity-history/" entity-id)} "↩ back to latest"]]])
      [:h3 "Last tx that changed the entity's attributes"]
      [entity-table entity-id (:attrs+vals data)]
      (when tx
        [:<>
         [:h3 "Diff for tx"]
         [entity-diff-view (assoc data :ui/attr :tx-diff)]
         [:h3 "Datoms for tx"]
         [datoms-view (assoc data
                             :ui.view/history-link? true
                             :ui/attr :explained-tx)]])]]))

(defn workflow'
  [m]
  (let [m+ui-views (resolve-ui-views m)]
    (if-let [active-service (first (:workflow/services m+ui-views))]
      [:div.h-full.md:max-w-4xl.md:mx-auto.md:overflow-hidden.md:rounded-lg.md:border.md:border-slate-300
       [(:ui/view active-service) m+ui-views {:service active-service}]]
      [ui/error-view (merge {:error :ductile.workflow/missing-active-service} m)])))

(defn workflow [workflow-map _match]
  (store-workflow-state-in-url-maybe! workflow-map)

  ;; when previous page sets a previous-url, use that for backlink route
  (when-let [previous-url @(deframe/!get :previous-url)]
    ;; move previous-url data from global state to route-local state, to avoid
    ;; clobbering other page navigation
    (deframe/route-assoc! :previous-url previous-url)
    (deframe/dissoc! :previous-url))

  (if (:exit/datetime workflow-map)
    (if (wheel-set? workflow-map)
      [exited-wheel-set workflow-map]
      [exited-fahrzeug workflow-map])
    [workflow' workflow-map]))

(defn compound-ops-layout []
  [mobile/scroll-content
   [mobile/navbar {:title (tr "Start")
                   :back [mobile/back-link {:route (router/path '[])}]
                   :right-item [workflow-menu-dropdown]}]
   [:div.flex.items-center.justify-center.mt-8.-mb-4
    [:img {:src "/images/logo-green.png" :style {:height 50}}]]
   [floating-scan-button]])

(defn render-fahrzeug-series [query-params query-results]
  [:div
   [:> ui/history-context.Provider {:value true}
    (into [:div.flex.flex-wrap.gap-6.px-6.pb-6.mx-auto
           {:class "max-w-[1278px]"}]
          (map (fn [x]
                 [:div.rounded-md.border.border-slate-300.overflow-hidden {:class "w-[390px]"}
                  [workflow' x]]))
          query-results)]])

(defn render-history [_result {:keys [path-params] :as _match}]
  (let [{:keys [compound-name fahrzeug-id]} path-params
        fahrzeug-query-params (assoc (fahrzeug/fahrzeug-id->query fahrzeug-id)
                                     :compound/name
                                     compound-name)]
    [ui/page [ui/nav-bar]
     [:div.p-5.pb-10 {:style {:min-height "calc(100vh - 3rem - 28px)"}}
      [ui/loading-promise-view
       (partial render-fahrzeug-series fahrzeug-query-params)
       (http/$lambda 'ductile.workflow.engine/fahrzeug-workflow-history fahrzeug-query-params)]]]))

(defn table [{:keys [head rows]}]
  [:div.overflow-x-auto.bg-white.rounded-md.mx-3
   [:table.w-full
    [:thead {:class "text-[11px]"}
     (into [:tr.border-b]
           (map (fn [col]
                  [:th.text-left.px-2.py-1.whitespace-nowrap.border-r col]))
           head)]
    (into [:tbody {:class "text-[12px]"}]
          (map (fn [row]
                 (into [:tr.border-b.odd:bg-mauve2]
                       (map (fn [col]
                              [:td.px-2.py-1.whitespace-nowrap.border-r {:class (when (number? col) "text-right")} col]))
                       row)))
          rows)]])


(defn ship-exit [{:as result :keys [vins transport-name]} _match]
  (with-hardware-scanner {:view `ship-exit
                          :on-scan (fn [{:as scan :scan/keys [result classified]}]
                                     (let [vin (if-let [vin-regex (get-in classified [:vehicle/vin :ductile.vehicle-search/regex])]
                                                 (when-not (str/blank? vin-regex)
                                                   (first (filter
                                                           (fn [vin]
                                                             (re-find (re-pattern vin-regex) vin))
                                                           vins)))
                                                 (:vehicle/vin classified))]
                                       (deframe/reset-current-scan!)
                                       (cond
                                         (not vin)
                                         (deframe/route-assoc! ::notice {:type :error :message (str "Invalid VIN: " result)
                                                                         :on-close #(deframe/route-dissoc! ::notice)})


                                         (not (contains? (set vins) vin))
                                         (deframe/route-assoc! ::notice {:type :error :message (str "VIN " (:vehicle/vin classified) " not found on ship")
                                                                         :on-close #(deframe/route-dissoc! ::notice)})

                                         :else
                                         (router/navigate-to (router/path '[compound-ops :compound-name workflow exit ship :transport-id exit-service :fahrzeug-id]
                                                                          (assoc (:path-params @deframe/!current-route) :fahrzeug-id (->fahrzeug-id "vin" vin)))))))}

    [mobile/scroll-content
     [mobile/navbar {:title (tr "Ship exit")
                     :subtitle transport-name
                     :back [mobile/back-link {:route (router/path '[compound-ops :compound-name] (:path-params @deframe/!current-route))}]
                     :right-item [workflow-menu-dropdown]}]
     (when-let [notice @(deframe/!route-get ::notice)]
       [:div.overflow-hidden.rounded-lg.shadow-lg.mb-5 {:data-testid "ship-exit-notice"}
        [ui/flash-notice notice]])
     (if (:error result)
       [ui/expected-error-view result]
       [:div.m-4
        [transport/transport-progress result]])]))

(defn ship-entry-details-view [{:as opts :keys [total entered per-model per-model-entered]} _match]
  (let [progress-percent (if (nil? total)
                           "0%"
                           (gstring/format "%.2f%"
                                           (* 100 (/ entered
                                                     total))))]
    [mobile/scroll-content
     [mobile/navbar {:title (tr "Ship entry details")
                     :subtitle (:transport/name opts)
                     :back [mobile/back-link {:route (entry-route {:compound/name (get-in @deframe/!current-route [:path-params :compound-name])
                                                                   :transport/name (:transport/name opts)
                                                                   :mode :ship})}]}]
     (if (nil? total)
       [:div.my-3.text-center.font-bold.text-lg
        (tr "Transport not found")]
       [:div
        [:div.my-3
         [:div.text-center.font-bold.text-lg (gstring/format (tr "Unloading is %1 complete") progress-percent)]
         [:div.text-center (tr "%1 remaining, %2 unloaded, %3 total"
                               (- total entered)
                               entered
                               total)]
         [:div.w-full.bg-slate-300.relative.mt-3.shadow-inner
          [:div.h-8.bg-green-500 {:style {:width progress-percent}}]]]
        [table {:head [(tr "Model") (tr "Unloaded") (tr "Remaining") "Total"]
                :rows (mapv (fn [[model-name c]]
                              (let [entered (get per-model-entered model-name 0)]
                                [model-name entered (- c entered) c]))
                            (sort-by first per-model))}]])]))

(defn render-exit-verification [_result {:as match :keys [path-params]}]
  (let [{transport :result :keys [error]} @(deframe/!route-get :fetch)]
    (with-hardware-scanner {:view [render-exit-verification match]
                            :on-scan (fn [{:keys [scan/result]}]
                                       (let [scans @(deframe/!route-get :exit-verification/scans)
                                             transport (:result @(deframe/!route-get :fetch))]
                                         (when (and result (not (contains? (set scans) result)))
                                           (let [scans' (if (= :mode/own-wheels (:transport/mode transport))
                                                          [result]
                                                          (conj scans result))]
                                             (deframe/route-assoc! :exit-verification/scans scans')
                                             (deframe/call-lambda
                                              'ductile.workflow/exit-verification
                                              (merge path-params {:scans scans'})))))
                                       (deframe/reset-current-scan!))}
      [:div
       [mobile/scroll-content
        [mobile/navbar {:title (tr "Exit verification")
                        :right-item [workflow-menu-dropdown]
                        :back [mobile/back-link {:route (router/path '[compound-ops :compound-name] path-params)}]}]
        (when error
          [ui/expected-error-view error])
        (when (:error-message transport)
          [ui/expected-error-view transport])
        (when (:confirmed-exit transport)
          [ui/flash-notice {:type :success :message (tr "Vehicles were marked as exited.")}])
        (if-let [{:transport/keys [vehicles]} (and (:ductile/id transport) (:transport/vehicles transport) transport)]
          [:div
           [mobile/table {:inset? true
                          :title (tr "Transport")
                          :items [(when (:transport/name transport)
                                    [mobile/list-item {:title (str (tr "Transport name") ": " (:transport/name transport))}])
                                  [mobile/list-item {:title (str (tr "Load ID") ": " (:load/id transport))}]
                                  (when (:transport/load-number transport)
                                    [mobile/list-item {:title (str (tr "Load #") ": " (:transport/load-number transport))}])]}]
           [:div.mx-6.text-center.font-medium.text-slate-500.mb-3
            (tr "Please scan all VINs to verify their exit.")]
           [table {:head [[ui/icon {:size 14} :scan] (tr "VIN") (tr "Model") (tr "Open Services")]
                   :rows (mapv
                          (fn [{:as vehicle :vehicle/keys [model vin]}]
                            (let [class (cond
                                          (seq (:workflow/open-service-codes vehicle))
                                          "text-red9"
                                          (and (empty? (:workflow/open-service-codes vehicle))
                                               (:verified? vehicle))
                                          "text-green9")]
                              [(when (:verified? vehicle) [:span {:data-testid (str "scanned-" vin)} "✅"])
                               [:span {:class class} [ui/vin vin]]
                               [:span {:class class} model]
                               [:span {:class class} (str/join ", " (:workflow/open-service-codes vehicle))]]))
                          vehicles)}]]
          [mobile/inline-prompt :car (tr "Please scan a load to confirm the exit.")])
        (when-not (:confirmed-exit transport)
          [toolbar {:disabled (not (:verified? transport))
                    :on-click (fn [event]
                                (.preventDefault event)
                                (.stopPropagation event)
                                (deframe/call-lambda
                                 'ductile.workflow/handle-exit-verification
                                 (merge path-params {:scans @(deframe/!route-get :exit-verification/scans)})))
                    :label (tr "Confirm Exit")}])]])))

(defn edit-form? [{:as form-map :ui/keys [fields]}]
  (->> fields
       (filter #(and (contains? % :ui/attr)
                     (not (:ui/readonly? %))
                     (not (:ui/hidden? %))
                     (not (:ui/transient? %))))
       (map :ui/attr)
       (some #(contains? form-map %))))

(defn interpolate-fields
  ([form-map] (interpolate-fields form-map form-map))
  ([target interpolation-data]
   (let [disallow-interpolation-keys #{:form/transform-fn
                                       :form/bulk-apply-fn
                                       :form/on-submit
                                       :modal/on-close
                                       :ui/on-success
                                       :ui/update-attrs-fn
                                       :ui/pred}]
     (merge target
            (cond-> (apply dissoc target disallow-interpolation-keys)
              (contains? target :ui/fields)
              (update :ui/fields (partial map (fn [field] (interpolate-fields field interpolation-data))))

              true
              (update-vals (fn [v] (if (or (v/render-fn? v) (fn? v))
                                     (v interpolation-data)
                                     v))))))))

(defn modal-form-container
  [{:ui/keys [breadcrumb-name form-title parent-route label-key css-class]
    :modal/keys [on-close]
    :mobile/keys [in-view-stack?]
    :as form-map}
   inner]
  (if in-view-stack?
    ;; When rendered by view-stack-controller on mobile, skip the desktop chrome.
    ;; The controller provides the mobile navbar separately.
    inner
    (let [auxiliary-fields (auxiliary-fields)
          lambda-params (deframe/current-route-params)]
      [:div.mx-auto.flex.gap-3
       {:class (or css-class (when (:form/bulk-rows form-map) "max-w-full") "max-w-2xl")}
       [:div.bg-gray-100.relative.rounded-b-lg.w-full
        [:div.bg-gray-200.sticky.top-0 {:style {:z-index 25}}
         [:div.text-lg.font-bold.flex.items-center.p-3.bg-white.rounded-t-lg.border.border-gray-300.justify-between
          [:div.flex.gap-1.items-center
           (into [:<>]
                 (comp
                  (remove str/blank?)
                  (interpose [ui/icon {:size 18} :chevron-right]))
                 [[(if parent-route :a.text-indigo-600.hover:underline :span)
                   {:href (router/path parent-route lambda-params)}
                   (tr (or form-title (:form/name form-map)))]
                  (or breadcrumb-name (if (edit-form? form-map) (tr "Edit") (tr "New")))
                  (when (and (edit-form? form-map) label-key) [:span " " (get form-map label-key)])])]
          (when (or auxiliary-fields on-close)
            [:button.text-gray-500.hover:text-indigo-700.cursor-pointer
             {:on-click (if auxiliary-fields
                          exit-auxiliary!
                          on-close)}
             [ui/icon {:size 24} :x]])]]
        [:> ui/history-context.Provider {:value true}
         inner]]])))

(def default-bulk-col-th-class
  {:ductile/id "text-left min-w-72"
   :transport/forwarder "min-w-64"
   :exit/transport-order-destination-location "min-w-64"
   :fahrzeug/destination "min-w-64"
   :fahrzeug/expected-customer-delivery-datetime "max-w-24"})


(defn default-bulk-col-class [attr]
  (get
   {:ductile/id "justify-start"
    :transport/forwarder "text-left items-center"
    :fahrzeug/priorities "text-xl justify-center items-center"}
   attr "justify-center items-center text-center"))

(defc form [initial-form-map]
  (hooks/use-effect (fn [] (deframe/route-assoc-in! (form-path initial-form-map) initial-form-map)) [initial-form-map])
  (hooks/use-effect (fn [] (when (:form/focus-attr initial-form-map) (ui/scroll-into-view! (str "[data-testid='attr-" (:form/focus-attr initial-form-map) "']") {:block "center"}))) [(:form/focus-attr initial-form-map)])
  (let [path (form-path initial-form-map)
        form-data (without-retractions @(deframe/!route-get-in path initial-form-map))
        interpolation-context (merge (form-context initial-form-map) form-data)
        {:as form-map
         :ui/keys [fields info parent-route on-success-route submit-button-label submit-button-loading-label success-message on-edit-success on-create-success on-success]
         :form/keys [bulk-rows bulk-cols bulk-key bulk-label-key handler download-handler refetch-on-submit on-submit transform-fn submit-result-path set-query-params?]
         :modal/keys [on-close]} (interpolate-fields initial-form-map interpolation-context)
        submit-result-path (or submit-result-path [:current-route/transient])
        loading? (or @deframe/!loading? @(deframe/!get-in (conj submit-result-path :loading)))
        submit-button-label (if loading?
                              (str (or submit-button-loading-label (tr "Loading")) "…")
                              (or submit-button-label (when download-handler (tr "Download")) (tr "Save")))
        lambda-params (deframe/current-route-params)
        auxiliary-fields (auxiliary-fields)]
    [modal-form-container
     form-map
     [:div.rounded-b-lg.border.border-t-0.border-gray-300
      (when info
        [info-view info])
      (when-let [error @(deframe/!route-get-in (conj path :error))]
        [:div {:style {:margin-top -5}} ;; make this proper
         [expected-error-view error]])
      #_[:div (str form-map)]
      (if (seq bulk-rows)
        (let [bulk-fields (or bulk-cols fields)
              bulk-key (or (:form/bulk-key form-map)
                           (:ui/attr (first bulk-fields)))
              attr->field (into {} (map (juxt :ui/attr identity)) fields)
              attr->bulk-fields (into {}
                                      (map (fn [{:as field :ui/keys [attr]}]
                                             [attr (dissoc (merge (get attr->field attr) field) :ui/hidden?)]))
                                      bulk-fields)
              bulk-rows (map (fn [row] (merge row
                                              (without-retractions (get @(deframe/!route-get-in path) (bulk-key row)))))
                             bulk-rows)
              bulk-fields (remove (fn [bulk-field]
                                    (let [{:ui/keys [pred hide-empty-col?]} (get attr->bulk-fields (:ui/attr bulk-field))]
                                      (when (and pred hide-empty-col?)
                                        (every? (some-fn false? nil?)
                                                (map pred bulk-rows)))))
                                  bulk-fields)
              bulk-attrs (map :ui/attr bulk-fields)]
          [:div.overflow-auto.mb-3
           [:table.w-full
            (into [:thead]
                  (map (fn [ths] (into [:tr] ths)))
                  (apply map
                         list
                         (map (fn [col]
                                (let [{:as col :ui/keys [title attr readonly? optional?] :form/keys [bulk-apply-fn]} (get attr->bulk-fields (:ui/attr col))
                                      col-title (tr (or title attr))
                                      editable? (and attr (not readonly?))
                                      th-props (merge {:class (str (default-bulk-col-th-class attr) (when editable? " cursor-pointer"))}
                                                      (when editable?
                                                        {:data-attr (str attr)
                                                         :on-pointer-enter (fn []
                                                                             (.forEach (js/document.querySelectorAll (str "[data-attr=\"" attr "\"]"))
                                                                                       (fn [^js el]
                                                                                         (set! (.. el -style -backgroundColor) "#E1E6FD77")
                                                                                         (when-not (= "TH" (.-tagName el)) (set! (.. el -style -boxShadow) "-3px 1px 0px 0px #6366E9, 3px 0px 0px 0px #6366E9")))))
                                                         :on-pointer-leave (fn []
                                                                             (.forEach (js/document.querySelectorAll (str "[data-attr=\"" attr "\"]"))
                                                                                       (fn [^js el]
                                                                                         (set! (.. el -style -backgroundColor) nil)
                                                                                         (set! (.. el -style -boxShadow) nil))))

                                                         :on-click (fn [^js e]
                                                                     (.stopPropagation e)
                                                                     (view-stack/push-view!
                                                                      {:view/type :form-direct
                                                                       :view/id (str (random-uuid))
                                                                       :view/result (merge ; forward uninterpolated form-map to modal
                                                                                     (dissoc initial-form-map :ui/success-message :ui/parent-route :form/bulk-key :form/bulk-rows :form/handler)
                                                                                     {:ui/submit-button-label (tr "Apply to all %1 rows" (count bulk-rows))
                                                                                      :form/focus-attr attr
                                                                                      :ui/fields (map #(dissoc % :ui/pred :ui/hidden?)
                                                                                                      (filter (comp #{attr} :ui/attr) (:ui/fields initial-form-map)))
                                                                                      :form/on-submit (fn [submittable-attrs]
                                                                                                        (deframe/tx!
                                                                                                         (into []
                                                                                                               (map (fn [row]
                                                                                                                      (let [v (get submittable-attrs attr)]
                                                                                                                        [:op/merge-at (into [:current-route/transient] (conj path (bulk-key row)))
                                                                                                                         {attr (if bulk-apply-fn
                                                                                                                                 (get (bulk-apply-fn form-map v) (bulk-key row))
                                                                                                                                 v)}])))
                                                                                                               bulk-rows))
                                                                                                        (view-stack/pop-view!))
                                                                                      :form/path (conj path ::header)
                                                                                      :ui/breadcrumb-name [:span col-title " (" (tr "%1 rows" (count bulk-rows)) ")"]})}))}))]
                                  [[:th.text-slate-600.text-xs.px-2.pt-2.pb-1.bg-slate-100.first:rounded-md-tl.last:rounded-md-r.border-slate-200.border-r.last:border-r-0.text-center.whitespace-normal.select-none.group
                                    th-props
                                    [:div.flex.flex-col.justify-between.items-center.h-full
                                     (or (some-> title tr)
                                         (tr attr))

                                     [:div {:class "text-[11px]"}
                                      (when (and attr (not readonly?))
                                        (if optional?
                                          [:span.text-slate-400.ml-1 "(" (tr "optional") ")"]
                                          [:span.text-orange-400.ml-1 "(" (tr "required") ")"]))]]]

                                   [:th.text-xs.px-2.pb-2.border-slate-200.border-r.last:border-r-0.select-none
                                    th-props
                                    (when editable?
                                      [:div {:class "text-[11px] group-hover:underline"}
                                       [ui/link {:on-click (:on-click th-props) :data-testid (str "edit-" (namespace attr) "-" (name attr))} (tr "Edit")]])]]))
                              bulk-fields)))

            (into [:tbody]
                  (map (fn [row]
                         (let [editing-row? (= (:view/bulk-key-val (peek @(deframe/!route-get ::view-stack/view-stack)))
                                               (bulk-key row))
                               row-path (conj path (bulk-key row))
                               on-click (fn [attr]
                                          #(view-stack/push-view!
                                            {:view/type :form-direct
                                             :view/id (str (random-uuid))
                                             :view/bulk-key-val (bulk-key row)
                                             :view/result (merge (dissoc form-map :ui/success-message :ui/parent-route :form/bulk-key :form/bulk-rows :form/handler)
                                                                 row
                                                                 {:ui/submit-button-label (tr "Save")
                                                                  :form/focus-attr attr
                                                                  :form/on-submit (fn [submittable-attrs]
                                                                                    (deframe/tx! [[:op/merge-at (into [:current-route/transient] row-path) submittable-attrs]])
                                                                                    (view-stack/pop-view!))
                                                                  :form/path row-path
                                                                  :ui/breadcrumb-name (cond
                                                                                        (= :vehicle/vin bulk-label-key) [:span.font-normal [ui/vin (:vehicle/vin row)]]
                                                                                        bulk-label-key (bulk-label-key row)
                                                                                        :else (tr "Edit"))
                                                                  bulk-key (bulk-key row)})}))]
                           (into [:tr.border-none.cursor-pointer.hover:bg-indigo-50.odd:bg-white.even:bg-slate-50.cursor-pointer.hover:outline.outline-indigo-500.rounded.-outline-offset-2.focus-visible:bg-indigo-50
                                  {:tab-index 0
                                   :on-key-down (fn [^js e]
                                                  (when (contains? #{"Enter" "Space"} (.-code e))
                                                    (.preventDefault e)
                                                    ((on-click nil) e)))
                                   :class (when editing-row? "outline")}]
                                 (map (fn [attr]
                                        (let [{:as field :ui/keys [overridden-pred]} (get attr->bulk-fields attr)]
                                          [:td.text-xs.px-2.border-l.border-slate-200.first:pl-0.first:border-l-0.py-2.focus-visible:bg-indigo-50
                                           {:data-attr (str attr)
                                            :tab-index -1
                                            :data-testid (str "edit-" (namespace attr) "-" (name attr) "-" (get row :vehicle/vin))
                                            :on-click (on-click attr)}
                                           [:div.flex {:class (default-bulk-col-class attr)}
                                            [render-fields
                                             (merge form-map row {:ui/style :readonly-values
                                                                  :ui/recorded-attrs-path row-path
                                                                  :ui/overridden? (when overridden-pred (overridden-pred row))})
                                             [field]]]])))
                                 bulk-attrs))))
                  bulk-rows)]

           (when (ui/debug-mode?)
             [:div.text-xs (pr-str (submittable-params form-map))])])

        [render-fields
         (dissoc-invalid-fields
          (merge (form-context form-map)
                 (assoc form-map
                        :ui/recorded-attrs-path path
                        :ui/style :style/mobile)
                 @(!recorded-attrs path)
                 (select-keys form-map [:ui/fields])))
         fields])

      (when (and (:malli/error form-map) (ui/debug-mode?))
        [:div.p-3.font-mono (render/inspect (:malli/error form-map))])

      (when (ui/debug-mode?)
        [:div.p-3.font-mono.text-xs (pr-str (cond-> (without-retractions (submittable-params form-map))
                                              transform-fn (transform-fn form-map)))])

      [:div.px-3.pb-3.sticky.-bottom-px.bg-gray-100.pt-3.-mt-3.z-10
       (if auxiliary-fields
         [mobile/button-full
          {:disabled loading?
           :on-click (fn [event]
                       (.preventDefault event)
                       (.stopPropagation event)
                       (when-let [on-auxiliary-save! @(deframe/!route-get ::on-auxiliary-save)]
                         (on-auxiliary-save!)
                         (deframe/route-dissoc! ::on-auxiliary-save))
                       (exit-auxiliary!))}
          submit-button-label]
         [mobile/button-full
          {:disabled loading?
           :data-testid "submit-form"
           :on-click (fn []
                       (js/requestAnimationFrame
                        (fn []
                          (let [params (cond-> (submittable-params form-map)
                                         transform-fn (transform-fn form-map))]
                            (when set-query-params?
                              (util/set-query-params! params))

                            (cond
                              (fn? on-submit)
                              (on-submit (cond-> (without-retractions (submittable-params form-map))
                                           transform-fn (transform-fn form-map)))
                              download-handler
                              (.then (http/download-lambda (cond-> (assoc (without-retractions (submittable-params form-map))
                                                                          :lambda/handler download-handler)
                                                             transform-fn (transform-fn form-map)))
                                     #(when on-close (on-close)))

                              handler
                              (deframe/call-lambda
                               {:result-path submit-result-path
                                :on-result (fn [{:as result :keys [error]}]
                                             (cond
                                               error
                                               (do (deframe/route-assoc-in! (conj path :error) result)
                                                   (ui/scroll-into-view-with-margin! ".expected-error" {:block "center"}))

                                                   ;; Multi-step form: result is another form spec, push it as a new view
                                               (:form/name result)
                                               (do (deframe/route-dissoc-in! path)
                                                   (view-stack/push-view! {:view/type :form-direct
                                                                           :view/id (str (random-uuid))
                                                                           :view/result result}))

                                               :else
                                               (let [{:message/keys [kind title details]
                                                      :message.action/keys [route label data-testid]
                                                      :deframe/keys [tx dispatch]} (cond
                                                                                     (edit-form? form-map) on-edit-success
                                                                                     on-create-success on-create-success
                                                                                     :else success-message)
                                                     interpolation-params (merge (deframe/current-route-params)
                                                                                 params
                                                                                 result)]
                                                 #_(prn ::result :interpolation-params interpolation-params)
                                                 (deframe/route-dissoc-in! (conj path :error))
                                                 (case kind
                                                   :message.kind/toast (ui/toast! {:title (tr title interpolation-params)
                                                                                   :data-testid (or data-testid "toast-action")
                                                                                   :description (tr details interpolation-params)
                                                                                   :action (when label {:as :a
                                                                                                        :href (router/path route interpolation-params)
                                                                                                        :variant :success
                                                                                                        :data-testid "toast-action"
                                                                                                        :label (tr label interpolation-params)})})
                                                   nil)

                                                 (when on-success (on-success))
                                                 (when on-close (on-close {:result result :params params}))
                                                 (deframe/tx! (conj (or tx [])
                                                                    [:op/dissoc-in [:current-route/transient :fetch :error]]))
                                                 (when dispatch
                                                   (deframe/dispatch! dispatch))

                                                 (when (and parent-route (not on-close))
                                                   (router/navigate-to (router/path parent-route lambda-params)))

                                                 (when on-success-route
                                                   (router/navigate-to (router/path on-success-route interpolation-params)))

                                                     ;; NOTE: `submit-result-path` is always non-nil, maybe this should
                                                     ;; be `(when (= [:current-route/transient] submit-result-path) ...)`
                                                 (when (or (not submit-result-path)
                                                           refetch-on-submit)
                                                   (deframe/route-refetch!)))))}
                               handler
                               params))))))}
          submit-button-label])]]]))

(defn- render-form-spec
  "Renders loading/error/result from a form spec (used by view-stack-controller)."
  [{:keys [loading error result]} on-close form-path]
  (cond
    loading [:div.p-20 icons/spinner]
    error [:div.mx-auto.flex.max-w-2xl
           [:div.bg-gray-100.relative.rounded-b-lg.w-full
            [:div.rounded-b-lg.border.border-t-0.border-gray-300.overflow-hidden
             [expected-error-view error]
             [:div.px-3.pb-3.flex.flex-col
              [mobile/button-full {:on-click on-close} (tr "Cancel")]]]]]
    result (if (:ui/custom-view result)
             [modal-form-container
              {:modal/on-close on-close
               :ui/breadcrumb-name ""
               :ui/form-title (tr (-> result :service :service/code))
               :ui/css-class ["w-[700px]"]}
              [(get name->view-fn (:ui/custom-view result))
               (:workflow-map result)
               {:service (:service result)
                :in-modal? true
                :on-result (fn [_] (on-close))}]]
             [form (merge result
                          {::service (:service result)}
                          {:modal/on-close on-close
                           :ui/css-class ["w-[700px]"]
                           :form/path form-path})])))

(defn- render-form-spec-mobile
  "Renders a form spec as a full-screen mobile overlay with mobile chrome."
  [{:keys [loading error result]} on-close form-path]
  (let [form-title (tr (or (:ui/form-title result) (:form/name result) ""))]
    [:div.fixed.inset-0 {:class "bg-page" :style {:z-index 50}}
     [mobile/scroll-content
      [mobile/navbar {:title form-title
                      :back [mobile/back-link {:on-click on-close}]}]
      [mobile/content
       (cond
         loading [:div.p-20 icons/spinner]
         error [:<>
                [expected-error-view error]
                [:div.px-3.pb-3
                 [mobile/button-full {:on-click on-close} (tr "Cancel")]]]
         result [form (merge result
                             {::service (:service result)
                              :modal/on-close on-close
                              :mobile/in-view-stack? true
                              :form/path form-path})])]]]))

(defn view-stack-controller
  "Top-level view stack controller. Renders the topmost form view-stack entry on top of the UI.
   Skips :fields entries (rendered by render-fields inside the form) so that auxiliary
   views pushed from within a modal form don't cause the modal to disappear.
   Entry types:
     :form       — lambda-loaded async; result stored at :view/result-path
     :form-direct — result already in hand; stored at :view/result
   Mobile: full-screen overlay with mobile chrome (pass {:mobile? true} from mobile-layout).
   Desktop: stacked modal (pass {:mobile? false} from desktop-layout)."
  [{:keys [mobile?]}]
  (let [stack @(deframe/!route-get ::view-stack/view-stack)]
    (when-let [top (some->> stack rseq (some #(when (#{:form :form-direct} (:view/type %)) %)))]
      (let [on-close view-stack/pop-view!]
        (case (:view/type top)
          :form
          (let [{:view/keys [id result-path]} top
                spec @(deframe/!get-in result-path)
                path [::view-stack/view-stack-data id]]
            (if mobile?
              ^{:key id} [render-form-spec-mobile spec on-close path]
              [ui/alert {:open? true :on-close on-close}
               [ui/alert-content {:full? true :class "max-w-[700px]"}
                ^{:key id} [:div [render-form-spec spec on-close path]]]]))

          :form-direct
          (let [{:view/keys [id result]} top
                path [::view-stack/view-stack-data id]]
            (if mobile?
              ^{:key id} [render-form-spec-mobile {:result result} on-close path]
              [ui/alert {:open? true :on-close on-close}
               [ui/alert-content {:full? true :class "max-w-[700px]"}
                ^{:key id} [:div [render-form-spec {:result result} on-close path]]]])))))))


(defscene view-stack-demo
  "Demonstrates the view stack system: pushing form modals (desktop dialog and
   mobile full-screen overlay), stacking multiple views, and direct form views."
  :params (r/atom {:mobile? false})
  [!state]
  (let [{:keys [mobile?]} @!state
        sample-fields [{:ui/attr :ductile/note
                        :ui/schema-type :string
                        :ui/field-view :ui.view/text
                        :ui/placeholder ""}]
        push-form! (fn [title]
                     (view-stack/push-view! {:view/type :form-direct
                                             :view/id (str (random-uuid))
                                             :view/result {:form/name title
                                                           :ui/form-title title
                                                           :ui/fields sample-fields
                                                           :form/path [::view-stack/view-stack-data (str (random-uuid))]
                                                           :form/on-submit #(view-stack/pop-view!)}}))
        push-nested! (fn []
                       (push-form! "Second (nested) form"))]
    [:div.p-6.flex.flex-col.gap-4 {:class "max-w-2xl"}
     [:div.flex.gap-2.items-center
      [:span.text-sm.font-medium "Mode:"]
      [ui/button {:on-click #(swap! !state update :mobile? not)
                  :variant :secondary
                  :size :sm}
       (if mobile? "Mobile" "Desktop")]]

     [:div.text-sm.text-gray-500
      "Stack depth: " (count (or @(deframe/!route-get ::view-stack/view-stack) []))]

     [:div.flex.flex-col.gap-2
      [:h3.font-semibold.text-sm "Single form"]
      [ui/button {:on-click #(push-form! "Single form demo")} "Open form"]]

     [:div.flex.flex-col.gap-2
      [:h3.font-semibold.text-sm "Stacked forms (push two)"]
      [ui/button {:on-click (fn []
                              (push-form! "First form")
                              (push-nested!))}
       "Open two stacked forms"]]

     [view-stack-controller {:mobile? mobile?}]]))

(defn render-storage-rows-with-free-spots [{:keys [zones->rows zones->total-free]} {:keys [path-params]}]
  (let [{:keys [compound-name]} path-params]
    [mobile/scroll-content
     [mobile/navbar {:back [mobile/back-link {:route (router/path '[compound-ops :compound-name]
                                                                  {:compound-name compound-name})}]
                     :title (tr "Free storage spots")}]
     (into [:div.p-2.sm:p-6.flex.gap-3.sm:gap-6.flex-wrap.justify-center]
           (map (fn [[zone rows]]
                  [:div.w-full.sm:w-auto
                   [:div.mb-3.sm:mb-6.border.border-slate-300.rounded-lg.shadow.bg-white
                    [:div.text-base.sm:text-xl.font-bold.px-3.pt-2.pb-1.text-center zone]
                    [:table.w-full.text-sm.sm:text-lg
                     [:thead.border-b
                      [:tr
                       [:th.text-xs.pb-2.font-normal {:col-span 3} (tr "%1 free" (get zones->total-free zone))]]]
                     (into [:tbody] (map
                                     (fn [storage-row]
                                       [:tr.border-b.last:border-b-0
                                        [:td.py-1.px-2.sm:px-3.border-r.whitespace-nowrap (tr "Row %1" (:row storage-row))]
                                        [:td.py-1.px-2.sm:px-3.text-right.tabular-nums.whitespace-nowrap (tr "%1 free" (count (:free-places storage-row)))]
                                        [:td.py-1.px-2.flex.sm:gap-1.flex-wrap {:class "gap-0.5"}
                                         (map (fn [place]
                                                [:div.justify-center.rounded.flex.items-center.text-xs.sm:text-base.size-8.sm:size-12
                                                 {:class (if place
                                                           ["bg-green-100" "text-green-600"]
                                                           ["bg-slate-100"])}
                                                 place])
                                              (take 5 (concat (:free-places storage-row) (repeat nil))))]]))
                           rows)]]]))
           zones->rows)]))

(defn render-interventions [interventions _match]
  [:<>
   [:div.flex.justify-between.mb-3
    [:h3 (tr "Interventions")]
    [ui/button
     {:variant :primary
      :href "/wizard/intervention"}
     (tr "Run New Intervention")]]
   [:div.rounded-lg.border.border-gray-300.shadow.overflow-auto
    [table/table
     {:hide-columns [:git/ref]
      :column-order [:db/txInstant :db/id :reuse :undo :intervention/tx-var :ductile/dev-notes :ductile/user :intervention/inputs]
      :transform-columns {[:db/txInstant]
                          (fn [{created-at :db/txInstant}]
                            (time-format/->str created-at))
                          [:intervention/tx-var]
                          (fn [{tx-var :intervention/tx-var}]
                            (str/replace (str tx-var) #"ductile\.interventions/" ""))
                          [:ductile/user]
                          (fn [{user :ductile/user}]
                            (:user/email user))
                          [:ductile/dev-notes]
                          (fn [{notes :ductile/dev-notes}]
                            (str/join " " notes))
                          [:intervention/inputs]
                          (fn [{inputs :intervention/inputs}]
                            [render/inspect (v/code inputs)])}
      :computed-columns {:undo (fn [intervention]
                                 [ui/button {:href (str "/wizard/intervention/"
                                                        (base64/encode (pr-str {:wizard/active-idx 1
                                                                                :ductile.interventions-wizard/tx-var 'ductile.interventions/undo-intervention
                                                                                'intervention-db-id (:db/id intervention)})))
                                             :variant :destructive}
                                  [ui/icon :reset]
                                  "undo"])
                         :reuse (fn [{:intervention/keys [inputs tx-var]}]
                                  [ui/button {:href (str "/wizard/intervention/"
                                                         (base64/encode (pr-str (merge {:wizard/active-idx 1
                                                                                        :ductile.interventions-wizard/tx-var tx-var}
                                                                                       (try (cljs.reader/read-string inputs)
                                                                                            (catch :default e {}))))))}
                                   [ui/icon :reload]
                                   "reuse"])}}
     interventions]]])

(def entry-services-data
  [{:service/code "Spezielle Ausstattung erfassen"
    :ui/view (quote ductile.ui.workflow/record-special-equipment-view)
    :event/kind :service.event/completed
    :ductile/id "1232"}

   {:service/code "Startdatum Lagerpflege ermitteln"
    :ui/view (quote ductile.ui.workflow/record-storage-care-start-date-view)
    :ductile/id "12311"}

   {:service/code "Batterieladezustand erfassen"
    :ui/view (quote ductile.ui.workflow/record-battery-charge-status-view)
    :ductile/id "1233"}

   {:service/code "Tanken"
    :requested/fuel-liters 25
    :ui/view (quote ductile.ui.workflow/fueling-view)
    :ductile/id "1243"}])

(def dc-state {:compound/name "Eitting"
               :customer/name "Sixt Car Sales GmbH, Gutenbergstraße 4, 85748 Garching bei München, Germany"
               :vehicle/vin "WVWKP7AU2GW900436"
               :vehicle/model "2016 Volkswagen e-Golf"
               :make/name "Volkswagen"
               :vehicle/engine-type "Elektrisch"
               :vehicle/dimensions "4270 mm / 1799 mm (2027 mm inkl. Außenspiegel) / 1473 mm"
               :vehicle/wheel-base "2629 mm"
               :vehicle/weight "1585 kg"})

(defscene coupling-ars-id
  [coupling-ars-id-view {:entry/mode :mode/truck
                         :compound/name "Krefeld"}])

(defscene entry-truck-additional-info
  [entry-truck-additional-info-view {:compound/name "Eitting"}])

(defscene coupling-ars-id-tasks-completed
  [coupling-ars-id-view {:compound/name "Krefeld"
                         :entry/mode :mode/truck}])

(defscene entry-inspection
  :params dc-state
  [params]
  [entry-inspection-view params])

(defscene fueling
  :params (assoc dc-state :service {:service/code "Tanken"
                                    :requested/fuel-liters 25
                                    :ui/view (quote ductile.ui.workflow/fueling-view)
                                    :ductile/id "1243"})
  [params]
  [fueling-view params])

(defscene storage-care-start-date
  :params dc-state
  [params]
  [record-storage-care-start-date-view params])

(defscene storage-care-start-date-prefilled
  :params (assoc-in dc-state [:fahrzeug :vehicle/production-date] #inst "2021-10-08T00:00:00.000000Z")
  [params]
  [record-storage-care-start-date-view params])

(defscene fahrzeug-not-found
  :params "OAKBZDF2000120001200012000382020"
  [params]
  [fahrzeug-not-found-view params])

(defscene battery-charge-status
  :params dc-state
  [params]
  [record-battery-charge-status-view params])


(defscene high-voltage-battery-charge-status
  :params dc-state
  [params]
  [record-high-voltage-battery-charge-status-view params])

(defscene entry-parking
  :params dc-state
  [params]
  [parking-view params])

(defscene show-default-view
  :params (assoc dc-state :service {:service/code "Fahrzeugwäsche bei Ausgang"})
  [params]
  [default-view params])

(defscene show-manual-announcement
  [manual-announcement-view {:compound/name "Eitting"}])

(defscene select-damages
  [mobile/scroll-content
   [mobile/navbar {:title (tr "Record damages")}]
   [damages/selector {:path [::recorded-attrs :vehicle/outside-damages]
                      :diagram :explosion
                      :regions damages/default-outside-regions
                      :kinds damages/default-outside-kinds}]])
;; Scan an ARS ID
;; ductile.ui.scanner.register_scans([{"de.ductile.app.scanner.data_string": "ARSIDTEST123DUCTILE", "de.ductile.app.scanner.source": "scanner"}])

;; Scan a VIN
;; ductile.ui.scanner.register_scans([{"de.ductile.app.scanner.data_string": "WAUZZZGA0NA019713", "de.ductile.app.scanner.source": "scanner"}])

(def name->view-fn
  "Used for resolving `:ui-view` symbols to var values."
  ;; NOTE: defined at bottom of file because only vars defined above it will be accessible.
  (into {}
        (comp (filter (fn [var] (fn? @var)))
              (map (juxt symbol deref)))
        (vals (ns-publics 'ductile.ui.workflow))))
